import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  Building2,
  Camera,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CheckCheck,
  CircleDot,
  Clock3,
  Copy,
  Download,
  Inbox,
  LayoutGrid,
  List,
  LoaderCircle,
  MessageSquare,
  Paperclip,
  Pencil,
  Plus,
  Search,
  Send,
  Share2,
  Ticket,
  User,
  UserPlus,
  Users,
  X,
  XCircle,
} from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import NavbarUtility from "./NavbarUtility";

// Colore principale usato per accenti, pulsanti e stati evidenziati del ticketing.
const GOLD = "#ebb51b";
// Endpoint PHP principale che gestisce lettura, creazione, assegnazione e chiusura ticket.
const API_URL = "https://dashboard.technoparts.it/ws/ticket.php";
// Endpoint dedicato ai messaggi e agli stati della chat collegata a ogni ticket.
const CHAT_API_URL = "https://dashboard.technoparts.it/ws/ticket_chat.php";

// Endpoint che registra nel backend la sottoscrizione alle notifiche push del browser.
const PUSH_SUBSCRIBE_URL =
  "https://dashboard.technoparts.it/ws/push/subscribe.php";

// Chiave pubblica VAPID usata dal browser per creare la sottoscrizione Web Push.
const VAPID_PUBLIC_KEY =
  "BFtH8HNil9kgjFUoZCfSCrdvaNxwGWccxLtCkzK7TMHJGpt4u_v6Fn6pk_k-_XYbHKQ462S6cbtdQJrSeF2ygr0";

// Converte la chiave VAPID Base64 in Uint8Array, formato richiesto dalla Push API.
const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);

  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);

  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
};

// Raccoglie in un solo punto i valori di stato usati dal backend e dal frontend.
const STATUS = {
  OPEN: "aperto",
  ASSIGNED: "assegnato",
  WORKING: "in_lavorazione",
  CLOSED: "chiuso",
};

// Associa a ogni stato tecnico una descrizione leggibile nell’interfaccia.
const STATUS_LABELS = {
  [STATUS.OPEN]: "Aperto",
  [STATUS.ASSIGNED]: "Assegnato",
  [STATUS.WORKING]: "In lavorazione",
  [STATUS.CLOSED]: "Chiuso",
};

// Riconosce le chiusure automatiche effettuate per mancanza di informazioni.
const isMissingInfoClosure = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .includes("mancanza di informazioni");

// Reparti e utenti come da configurazione richiesta.
// Gli username vengono sempre normalizzati in lowercase per combaciare con AuthContext.
const FALLBACK_DEPARTMENTS = {
  IT: ["simone", "federico", "sabri", "dario"],
  MARKETING: ["leandro"],
  VENDITE: ["francesca", "roberta", "emiliano", "bruno"],
  ACQUISTI: ["denise", "rosy", "ilenia", "noemi"],
  CONTABILITÀ: ["viviana", "federica"],
  AMOTIVE: ["emanuele", "daniela", "irene"],
  DIREZIONE: ["alessandro", "rita"],
  "PROCUREMENT SPECIALIST": ["bruna"],
};

// Direzione vede tutti i ticket di tutti i reparti.
const GLOBAL_VIEWERS = new Set(["alessandro", "rita"]);

// Normalizza uno username in lowercase per confronti coerenti tra AuthContext, frontend e database.
const normalizeUser = (value) => {
  if (!value) return "guest";

  if (typeof value === "string") {
    return value.trim().toLowerCase();
  }

  if (typeof value === "object") {
    const candidate =
      value.username ||
      value.user ||
      value.name ||
      value.email ||
      value.id ||
      "guest";

    return String(candidate).trim().toLowerCase();
  }

  return String(value).trim().toLowerCase();
};

// Trasforma uno username tecnico in un nome più leggibile da mostrare nella UI.
const displayUser = (username) => {
  if (!username) return "—";

  return String(username)
    .trim()
    .split(/[.\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

// Trova il reparto associato a uno username.
const findDepartment = (username, departments = FALLBACK_DEPARTMENTS) => {
  const normalized = normalizeUser(username);

  for (const [department, members] of Object.entries(departments)) {
    if (members.includes(normalized)) {
      return department;
    }
  }

  return "NON ASSEGNATO";
};

// Mostra reparto e utente in un’unica stringa leggibile.
const displayDepartmentUser = (
  username,
  departments = FALLBACK_DEPARTMENTS,
  fallbackDepartment = "",
) => {
  const normalized = normalizeUser(username);
  if (!username || normalized === "guest") return "—";

  const department = findDepartment(normalized, departments);
  const effectiveDepartment =
    department && department !== "NON ASSEGNATO"
      ? department
      : fallbackDepartment || "NON ASSEGNATO";

  return `${effectiveDepartment} - ${displayUser(normalized)}`;
};

// Calcola la destinazione/responsabile corrente di un ticket.
const getCurrentDestination = (ticket, departments = FALLBACK_DEPARTMENTS) => {
  if (!ticket) return "—";

  if (ticket.assignedTo) {
    return displayDepartmentUser(
      ticket.assignedTo,
      departments,
      ticket.targetDepartment,
    );
  }

  if (ticket.targetUser) {
    return displayDepartmentUser(
      ticket.targetUser,
      departments,
      ticket.targetDepartment,
    );
  }

  return ticket.targetDepartment
    ? `${ticket.targetDepartment} - Tutto il reparto`
    : "—";
};

// Converte le date restituite dal database in oggetti Date JavaScript validi.
const parseDbDate = (value) => {
  if (!value) return null;

  const normalized =
    typeof value === "string" && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(value)
      ? value.replace(" ", "T")
      : value;

  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Formatta data e ora nel formato italiano usato nelle tabelle e nelle card.
const formatDateTime = (value) => {
  const date = parseDbDate(value);

  if (!date) return "—";

  return date.toLocaleString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// MIME type consentiti per gli allegati.
const ACCEPTED_FILE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/svg+xml",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

// Estensioni file consentite per gli allegati.
const ACCEPTED_FILE_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ".svg",
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
];

// Stringa passata all’attributo accept degli input file.
const ACCEPT_ATTRIBUTE = ACCEPTED_FILE_EXTENSIONS.join(",");
// Limite massimo complessivo degli allegati gestiti dal frontend: 8 MB.
const MAX_ATTACHMENT_SIZE = 8 * 1024 * 1024;

// Estrae in modo sicuro l’estensione da un nome file o URL.
const getFileExtension = (value = "") => {
  const cleanValue = String(value).split("?")[0].split("#")[0];
  const match = cleanValue.match(/\.([a-z0-9]+)$/i);
  return match ? `.${match[1].toLowerCase()}` : "";
};

// Converte una dimensione in byte in B, KB o MB leggibili.
const formatFileSize = (bytes = 0) => {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(2)} MB`;
};

// Ricava il nome visualizzabile di un allegato a partire dall’URL.
const getAttachmentName = (url, fallback = "Allegato") => {
  if (!url) return fallback;

  try {
    const pathname = new URL(url, window.location.href).pathname;
    const name = pathname.split("/").filter(Boolean).pop();
    return name ? decodeURIComponent(name) : fallback;
  } catch {
    const clean = String(url).split("?")[0].split("#")[0];
    const name = clean.split("/").filter(Boolean).pop();
    return name ? decodeURIComponent(name) : fallback;
  }
};

// Controlla se un allegato è un’immagine visualizzabile direttamente nel modale.
const isPreviewableImageAttachment = (attachment) => {
  if (!attachment) return false;

  const type = String(attachment.type || "").toLowerCase();
  const source = String(
    attachment.dataUrl || attachment.url || attachment.name || "",
  ).toLowerCase();

  return (
    type === "image/png" ||
    type === "image/jpeg" ||
    type === "image/webp" ||
    source.startsWith("data:image/png") ||
    source.startsWith("data:image/jpeg") ||
    source.startsWith("data:image/webp") ||
    /\.(png|jpe?g|webp)(?:$|[?#])/i.test(source)
  );
};

// Uniforma un allegato salvato nel DB nella struttura usata dal frontend.
const normalizeStoredAttachment = (item, index, fallbackName) => {
  if (!item) return null;

  if (typeof item === "string") {
    return {
      dataUrl: item,
      url: item,
      name: getAttachmentName(item, `${fallbackName} ${index + 1}`),
      type: "",
      size: 0,
    };
  }

  if (typeof item !== "object") return null;

  const url = String(item.url || item.dataUrl || "").trim();
  if (!url) return null;

  return {
    dataUrl: url,
    url,
    name: item.name || getAttachmentName(url, `${fallbackName} ${index + 1}`),
    type: item.type || "",
    size: Number(item.size) || 0,
  };
};

// Legge allegati JSON o legacy e li converte in un array uniforme.
const parseStoredAttachments = (raw, fallbackName = "Allegato") => {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw
      .map((item, index) =>
        normalizeStoredAttachment(item, index, fallbackName),
      )
      .filter(Boolean);
  }

  if (typeof raw === "object") {
    const normalized = normalizeStoredAttachment(raw, 0, fallbackName);
    return normalized ? [normalized] : [];
  }

  const value = String(raw).trim();
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item, index) =>
          normalizeStoredAttachment(item, index, fallbackName),
        )
        .filter(Boolean);
    }
  } catch {
    // Ticket legacy: la colonna conteneva direttamente un URL.
  }

  const legacy = normalizeStoredAttachment(value, 0, fallbackName);
  return legacy ? [legacy] : [];
};

// Verifica che tipo MIME o estensione del file siano supportati.
const isAllowedFile = (file) => {
  if (!file) return false;

  const type = String(file.type || "").toLowerCase();
  const extension = getFileExtension(file.name || "");

  return (
    ACCEPTED_FILE_TYPES.includes(type) ||
    ACCEPTED_FILE_EXTENSIONS.includes(extension)
  );
};

// Valida un file e prepara anche la preview Data URL quando è un’immagine.
const readAttachment = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      resolve(null);
      return;
    }

    if (!isAllowedFile(file)) {
      reject(
        new Error(
          "Formato non supportato. Usa immagini, SVG, PDF, Word o Excel.",
        ),
      );
      return;
    }

    if (file.size > MAX_ATTACHMENT_SIZE) {
      reject(new Error("Il singolo file supera 8 MB."));
      return;
    }

    const previewable =
      String(file.type || "").toLowerCase() === "image/png" ||
      String(file.type || "").toLowerCase() === "image/jpeg" ||
      String(file.type || "").toLowerCase() === "image/webp" ||
      /\.(png|jpe?g|webp)$/i.test(file.name || "");

    if (!previewable) {
      resolve({
        file,
        dataUrl: null,
        name: file.name || "allegato",
        type: file.type || "",
        size: file.size || 0,
      });
      return;
    }

    const reader = new FileReader();

    reader.onerror = () => reject(new Error("Impossibile leggere il file."));
    reader.onload = () =>
      resolve({
        file,
        dataUrl: reader.result,
        name: file.name || "immagine",
        type: file.type || "",
        size: file.size || 0,
      });

    reader.readAsDataURL(file);
  });

// Somma la dimensione complessiva di un gruppo di allegati.
const getAttachmentTotalSize = (attachments = []) =>
  attachments.reduce(
    (total, attachment) =>
      total + Number(attachment?.size || attachment?.file?.size || 0),
    0,
  );

// Conta gli allegati presenti sul ticket e sulla risposta/chiusura.
const getTicketAttachmentCount = (ticket) =>
  (ticket?.attachments?.length || 0) +
  (ticket?.replyAttachments?.length || 0);

// Prepara nuovi file e controlla che il totale non superi 8 MB.
const prepareAttachments = async (files, currentAttachments = []) => {
  const incomingFiles = Array.from(files || []).filter(Boolean);
  if (!incomingFiles.length) return currentAttachments;

  const prepared = [];

  for (const file of incomingFiles) {
    prepared.push(await readAttachment(file));
  }

  const next = [...currentAttachments, ...prepared];
  const totalSize = getAttachmentTotalSize(next);

  if (totalSize > MAX_ATTACHMENT_SIZE) {
    throw new Error("Gli allegati superano 8 MB complessivi.");
  }

  return next;
};

// Estrae eventuali immagini incollate dagli appunti del browser.
const getClipboardImageFiles = (clipboardData) => {
  if (!clipboardData) return [];

  const directFiles = Array.from(clipboardData.files || []).filter((file) =>
    String(file.type || "").startsWith("image/"),
  );

  if (directFiles.length) return directFiles;

  return Array.from(clipboardData.items || [])
    .filter(
      (item) =>
        item.kind === "file" && String(item.type || "").startsWith("image/"),
    )
    .map((item) => item.getAsFile?.())
    .filter(Boolean);
};

// Converte una riga restituita dal PHP nel modello ticket usato da React.
const normalizeTicket = (row) => {
  if (!row || typeof row !== "object") return null;

  const urgentRaw = row.urgente;
  const urgent =
    urgentRaw === true ||
    urgentRaw === 1 ||
    urgentRaw === "1" ||
    String(urgentRaw).toLowerCase() === "true";

  const id = Number(row.id_ticket);
  const attachments = parseStoredAttachments(row.allegato, "Allegato ticket");
  const replyAttachments = parseStoredAttachments(
    row.allegato_risposta,
    "Allegato risposta",
  );

  return {
    id: Number.isFinite(id) ? id : row.id_ticket,
    code: row.codice_ticket || `#${row.id_ticket ?? "—"}`,
    createdBy: normalizeUser(row.utente_creazione),
    creatorDepartment: row.reparto_creazione || "NON ASSEGNATO",
    targetDepartment: row.reparto_destinatario || "—",
    targetUser: row.utente_destinatario
      ? normalizeUser(row.utente_destinatario)
      : "",
    assignedBy: row.assegnato_da ? normalizeUser(row.assegnato_da) : "",
    assignedTo: row.assegnato_a ? normalizeUser(row.assegnato_a) : "",
    title: row.titolo || "Ticket senza titolo",
    description: row.descrizione || "",
    urgent,
    status: row.stato || STATUS.OPEN,
    createdAt: row.data_creazione || null,
    updatedAt: row.data_modifica || null,
    statusChangedAt: row.data_cambio_stato || null,
    resolvedAt: row.data_risoluzione || null,
    updatedBy: row.utente_ultima_modifica || row.utente_modifica || null,
    closedBy: row.utente_chiusura ? normalizeUser(row.utente_chiusura) : null,
    replyText: row.risposta || "",
    replyBy: row.utente_risposta ? normalizeUser(row.utente_risposta) : null,
    replyAt: row.data_risposta || null,
    attachments,
    replyAttachments,
    // Alias mantenuti per compatibilità con eventuale codice esterno.
    image: attachments[0] || null,
    replyImage: replyAttachments[0] || null,

    // Classificazione preparata dal PHP per la home Ticket ricevuti.
    hasViewFlags:
      row.vista_miei !== undefined ||
      row.vista_reparto !== undefined ||
      row.vista_tutto_reparto !== undefined,
    viewMine:
      row.vista_miei === true ||
      row.vista_miei === 1 ||
      String(row.vista_miei ?? "") === "1",
    viewDepartment:
      row.vista_reparto === true ||
      row.vista_reparto === 1 ||
      String(row.vista_reparto ?? "") === "1",
    viewAllDepartment:
      row.vista_tutto_reparto === true ||
      row.vista_tutto_reparto === 1 ||
      String(row.vista_tutto_reparto ?? "") === "1",

    raw: row,
  };
};

// Ordina i ticket dando priorità agli urgenti e poi ai più recenti.
const sortTickets = (items) =>
  [...items].sort((a, b) => {
    // Prima gli urgenti, poi i più recenti.
    if (a.urgent !== b.urgent) return a.urgent ? -1 : 1;

    const aDate = parseDbDate(a.createdAt)?.getTime() ?? 0;
    const bDate = parseDbDate(b.createdAt)?.getTime() ?? 0;

    if (aDate !== bDate) return bDate - aDate;

    const aId = Number(a.id) || 0;
    const bId = Number(b.id) || 0;
    return bId - aId;
  });

// Ordina un elenco ticket per data in senso crescente o decrescente.
const sortTicketsByDate = (items, direction = "desc") =>
  [...items].sort((a, b) => {
    const aDate = parseDbDate(a.createdAt)?.getTime() ?? 0;
    const bDate = parseDbDate(b.createdAt)?.getTime() ?? 0;

    if (aDate !== bDate) {
      return direction === "asc" ? aDate - bDate : bDate - aDate;
    }

    const aId = Number(a.id) || 0;
    const bId = Number(b.id) || 0;
    return direction === "asc" ? aId - bId : bId - aId;
  });

// Wrapper di fetch con log, parsing JSON robusto e gestione uniforme degli errori API.
const apiFetch = async (label, url, options = {}) => {
  const method = options.method || "GET";

  console.groupCollapsed(
    `%c[TICKET API] ${label}`,
    "color:#ebb51b;font-weight:bold",
  );
  console.log("Metodo:", method);
  console.log("URL:", url);

  if (options.body instanceof FormData) {
    const debugFormData = {};
    options.body.forEach((value, key) => {
      debugFormData[key] =
        value instanceof File
          ? `[FILE] ${value.name} · ${value.type} · ${value.size} bytes`
          : value;
    });
    console.log("Payload FormData:", debugFormData);
  } else if (options.body) {
    try {
      console.log("Payload:", JSON.parse(options.body));
    } catch {
      console.log("Payload raw:", options.body);
    }
  }

  const startedAt = performance.now();

  try {
    console.log("Fetch options:", options);

    const response = await fetch(url, options);
    const text = await response.text();

    console.log("HTTP status:", response.status, response.statusText);
    console.log("Response URL:", response.url);
    console.log("Content-Type:", response.headers.get("content-type"));

    // DEBUG VISIBILE: non è dentro un gruppo collassato.
    console.log("===============================================");
    console.log("[TICKET RAW RESPONSE]");
    console.log("HTTP:", response.status, response.statusText);
    console.log("Content-Type:", response.headers.get("content-type"));
    console.log("Lunghezza body:", text.length);
    console.log("BODY RAW:", JSON.stringify(text));
    console.log("BODY TESTO:", text || "[RISPOSTA VUOTA]");
    console.log("===============================================");

    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch (parseError) {
      console.error("[TICKET JSON PARSE ERROR]", parseError);
      console.error("[TICKET JSON NON VALIDO] BODY:", JSON.stringify(text));

      // Se PHP/wrapper stampa rumore prima/dopo il JSON, proviamo comunque
      // a individuare il blocco JSON per capire cosa sta succedendo.
      const firstBrace = text.indexOf("{");
      const lastBrace = text.lastIndexOf("}");

      if (firstBrace !== -1 && lastBrace > firstBrace) {
        const candidate = text.slice(firstBrace, lastBrace + 1);

        console.warn(
          "[TICKET] Trovato possibile JSON dentro risposta sporca:",
          candidate,
        );

        try {
          data = JSON.parse(candidate);
          console.warn(
            "[TICKET] JSON recuperato dalla risposta sporca. Il PHP sta stampando caratteri extra.",
          );
        } catch (fallbackError) {
          const preview = text ? text.slice(0, 1000) : "[RISPOSTA VUOTA]";

          throw new Error(
            `Risposta server non valida (HTTP ${response.status}). RAW: ${preview}`,
          );
        }
      } else {
        const preview = text ? text.slice(0, 1000) : "[RISPOSTA VUOTA]";

        throw new Error(
          `Risposta server non valida (HTTP ${response.status}). RAW: ${preview}`,
        );
      }
    }

    console.log("JSON parsed:", data);
    console.log("Durata:", `${Math.round(performance.now() - startedAt)} ms`);

    if (!response.ok || data?.success === false) {
      const message = data?.details
        ? `${data.error || "Errore API"}: ${data.details}`
        : data?.error || `Errore HTTP ${response.status}`;
      throw new Error(message);
    }

    return data;
  } catch (error) {
    console.error("ERRORE:", error);
    throw error;
  } finally {
    console.groupEnd();
  }
};

// Sceglie l’icona corretta in base allo stato del ticket.
const StatusIcon = ({ status, size = 15 }) => {
  if (status === STATUS.CLOSED) {
    return <CheckCircle2 size={size} />;
  }

  if (status === STATUS.WORKING) {
    return <LoaderCircle size={size} />;
  }

  if (status === STATUS.ASSIGNED) {
    return <Users size={size} />;
  }

  return <CircleDot size={size} />;
};

// Icona personalizzata usata quando una conversazione è in attesa di risposta.
const WaitingInputIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M5.25 4.75H18.75C20.5449 4.75 22 6.20507 22 8V14.25C22 16.0449 20.5449 17.5 18.75 17.5H16.25V14.75H18.75C19.0261 14.75 19.25 14.5261 19.25 14.25V8C19.25 7.72386 19.0261 7.5 18.75 7.5H5.25C4.97386 7.5 4.75 7.72386 4.75 8V14.25C4.75 14.5261 4.97386 14.75 5.25 14.75H7.75V17.5H5.25C3.45507 17.5 2 16.0449 2 14.25V8C2 6.20507 3.45507 4.75 5.25 4.75Z"
      fill="currentColor"
    />
    <path
      d="M12 9.25L16.25 13.75H13.5V20H10.5V13.75H7.75L12 9.25Z"
      fill="currentColor"
    />
  </svg>
);

// Componente riutilizzabile mostrato quando una sezione non contiene risultati.
const EmptyState = ({ title, text }) => (
  <div className="tk-empty">
    <Ticket size={29} />
    <strong>{title}</strong>
    <span>{text}</span>
  </div>
);


// Copia il codice ticket negli appunti con fallback per browser più vecchi.
const copyTicketCodeToClipboard = async (value) => {
  const text = String(value || "").trim();
  if (!text) return false;

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    console.warn("[TICKET] Clipboard API non disponibile:", error);
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    textarea.style.pointerEvents = "none";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    textarea.remove();
    return copied;
  } catch (error) {
    console.error("[TICKET] Copia codice fallita:", error);
    return false;
  }
};

// Mostra il codice ticket insieme al pulsante per copiarlo.
const TicketCodeWithCopy = ({ code, className = "" }) => {
  const [copied, setCopied] = useState(false);
  const value = String(code || "").trim();

  if (!value) return null;

  // Gestisce il click sul pulsante copia e mostra per un attimo lo stato “Copiato”.
  const handleCopy = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const ok = await copyTicketCodeToClipboard(value);

    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    }
  };

  return (
    <span className={`tk-code-copy-wrap ${className}`.trim()}>
      <span className="tk-code-copy-value">{value}</span>

      <button
        type="button"
        className={`tk-code-copy-button ${copied ? "is-copied" : ""}`}
        onClick={handleCopy}
        title={copied ? "Copiato" : `Copia ${value}`}
        aria-label={copied ? `${value} copiato` : `Copia codice ${value}`}
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </button>
    </span>
  );
};

// Card completa di un ticket ricevuto con stato, allegati e azioni disponibili.
const TicketCard = ({
  ticket,
  currentUser,
  currentDepartment,
  isDirector,
  showCreator = true,
  allowStatusChange = false,
  onStatusChange,
  onOpen,
  onEditAssignment,
  onCloseStatusRequest,
  onQuickCloseRequest,
  unreadCount = 0,
  actionRequired = false,
}) => {
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [savingAttachmentUrl, setSavingAttachmentUrl] = useState("");
  const [reopenConfirmOpen, setReopenConfirmOpen] = useState(false);

  useEffect(() => {
    if (!previewAttachment || typeof document === "undefined") return undefined;

    const body = document.body;
    const html = document.documentElement;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlOverflow = html.style.overflow;

    body.style.overflow = "hidden";
    html.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setPreviewAttachment(null);
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      body.style.overflow = previousBodyOverflow;
      html.style.overflow = previousHtmlOverflow;
    };
  }, [previewAttachment]);

  // Scarica l’allegato; se il fetch fallisce prova ad aprirlo direttamente in una nuova scheda.
  const downloadAttachment = async (attachment) => {
    const attachmentUrl = attachment?.dataUrl || attachment?.url;
    if (!attachmentUrl || savingAttachmentUrl === attachmentUrl) return;

    setSavingAttachmentUrl(attachmentUrl);

    try {
      const response = await fetch(attachmentUrl, { cache: "no-store" });
      if (!response.ok) {
        throw new Error(`Download allegato fallito: HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download =
        attachment.name || getAttachmentName(attachmentUrl, "allegato");
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    } catch (downloadError) {
      console.error("[TICKET] Download allegato fallito:", downloadError);
      const link = document.createElement("a");
      link.href = attachmentUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      setSavingAttachmentUrl("");
    }
  };

  // Apre la preview per le immagini oppure il file in una nuova scheda per gli altri formati.
  const openAttachment = (attachment) => {
    if (!attachment) return;

    if (isPreviewableImageAttachment(attachment)) {
      setPreviewAttachment(attachment);
      return;
    }

    const url = attachment.dataUrl || attachment.url;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const currentResponsibleUser = ticket.assignedTo || ticket.targetUser || "";
  const isDepartmentTicket = !ticket.assignedTo && !ticket.targetUser;
  const canManageDepartmentTicket =
    isDepartmentTicket && ticket.targetDepartment === currentDepartment;

  const canManageTicket =
    !isDirector &&
    (currentResponsibleUser === currentUser || canManageDepartmentTicket);

  const canChangeStatus = allowStatusChange && canManageTicket;
  const hasStoredAssignment = Boolean(ticket.assignedBy && ticket.assignedTo);
  const reopenTargetStatus = hasStoredAssignment ? STATUS.ASSIGNED : STATUS.OPEN;
  const reopenTargetLabel = STATUS_LABELS[reopenTargetStatus];
  const storedAttachments = ticket.attachments || [];

  return (
    <>
      <article className={`tk-ticket-card ${ticket.urgent ? "is-urgent" : ""}`}>
        <div className="tk-ticket-topline">
          <div className="tk-ticket-id">
            <Ticket size={14} />
            <TicketCodeWithCopy
              code={ticket.code || `TKT-${ticket.id}`}
              className={ticket.urgent ? "tk-code-urgent" : "tk-code-normal"}
            />
          </div>

          <div className="tk-badges">
            {[STATUS.OPEN, STATUS.ASSIGNED].includes(ticket.status) &&
              typeof onEditAssignment === "function" && (
                <button
                  type="button"
                  className="tk-edit-assignment-button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onEditAssignment(ticket);
                  }}
                  title="Modifica assegnazione"
                  aria-label={`Modifica assegnazione ${ticket.code || `TKT-${ticket.id}`}`}
                >
                  <Pencil size={14} />
                </button>
              )}

            {typeof onQuickCloseRequest === "function" && (
              <button
                type="button"
                className="tk-quick-close-button"
                disabled={ticket.status === STATUS.CLOSED}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (ticket.status !== STATUS.CLOSED) {
                    onQuickCloseRequest(ticket);
                  }
                }}
                title={
                  ticket.status === STATUS.CLOSED
                    ? "Ticket già chiuso"
                    : "Chiudi per mancanza di informazioni"
                }
                aria-label={`Chiudi per mancanza di informazioni ${ticket.code || `TKT-${ticket.id}`}`}
              >
                <XCircle size={14} />
              </button>
            )}

            {ticket.urgent && (
              <span className="tk-badge tk-badge-urgent">
                <AlertTriangle size={12} />
                Urgente
              </span>
            )}

            <span className={`tk-badge tk-status tk-status-${ticket.status}`}>
              <StatusIcon status={ticket.status} size={12} />
              {STATUS_LABELS[ticket.status] || ticket.status}
            </span>
          </div>
        </div>

        <h3>{ticket.title}</h3>
        <p className="tk-ticket-description">{ticket.description}</p>

        {storedAttachments.length > 0 && (
          <div className="tk-stored-files">
            <div className="tk-stored-files-title">
              <Paperclip size={14} />
              {storedAttachments.length}{" "}
              {storedAttachments.length === 1 ? "allegato" : "allegati"}
            </div>

            <div className="tk-stored-files-grid">
              {storedAttachments.map((attachment, index) => (
                <button
                  type="button"
                  className="tk-stored-file"
                  key={`${attachment.dataUrl || attachment.name}-${index}`}
                  onClick={() => openAttachment(attachment)}
                  title={attachment.name}
                >
                  {isPreviewableImageAttachment(attachment) ? (
                    <img src={attachment.dataUrl || attachment.url} alt="" />
                  ) : (
                    <span className="tk-stored-file-icon">
                      <Paperclip size={17} />
                    </span>
                  )}
                  <span>{attachment.name || `Allegato ${index + 1}`}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="tk-ticket-meta tk-ticket-meta-full">
          {showCreator && (
            <div>
              <User size={13} />
              <span>
                Creato da <b>{displayDepartmentUser(ticket.createdBy, FALLBACK_DEPARTMENTS, ticket.creatorDepartment)}</b>
              </span>
            </div>
          )}

          <div>
            <Users size={13} />
            <span>
              Utente destinatario{" "}
              <b>
                {ticket.targetUser
                  ? displayDepartmentUser(ticket.targetUser, FALLBACK_DEPARTMENTS, ticket.targetDepartment)
                  : `${ticket.targetDepartment} - Tutto il reparto`}
              </b>
            </span>
          </div>

          <div>
            <User size={13} />
            <span>
              Assegnato da <b>{ticket.assignedBy ? displayDepartmentUser(ticket.assignedBy, FALLBACK_DEPARTMENTS, ticket.targetDepartment) : "—"}</b>
            </span>
          </div>

          <div>
            <User size={13} />
            <span>
              Assegnato a <b>{ticket.assignedTo ? displayDepartmentUser(ticket.assignedTo, FALLBACK_DEPARTMENTS, ticket.targetDepartment) : "—"}</b>
            </span>
          </div>

          <div>
            <Clock3 size={13} />
            <span>Creato il {formatDateTime(ticket.createdAt)}</span>
          </div>
        </div>

        {typeof onOpen === "function" && (
          <button type="button" className="tk-open-chat" onClick={onOpen}>
            <MessageSquare size={15} />
            <span>Apri conversazione</span>
            {unreadCount > 0 && (
              <span className="tk-open-chat-badge">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}

            {unreadCount === 0 && actionRequired && (
              <span
                className="tk-open-chat-action-required"
                title="Hai letto l'ultimo messaggio: devi ancora rispondere"
                aria-label="Risposta richiesta"
              >
                <WaitingInputIcon size={17} />
              </span>
            )}
          </button>
        )}

        <div className="tk-status-editor">
          <span>Stato ticket</span>

          <div className="tk-status-buttons">
            <button
              type="button"
              className={`tk-status-choice ${ticket.status === STATUS.OPEN ? "is-active" : ""}`}
              disabled={
                ticket.status === STATUS.OPEN ||
                !canChangeStatus ||
                hasStoredAssignment ||
                ![STATUS.ASSIGNED, STATUS.WORKING].includes(ticket.status)
              }
              onClick={() => setReopenConfirmOpen(true)}
              title={
                hasStoredAssignment
                  ? "Il ticket ha già un assegnatario: deve tornare allo stato Assegnato"
                  : "Riporta il ticket ad Aperto"
              }
            >
              <StatusIcon status={STATUS.OPEN} size={13} />
              Aperto
            </button>

            <button
              type="button"
              className={`tk-status-choice ${ticket.status === STATUS.ASSIGNED ? "is-active" : ""}`}
              disabled={
                ticket.status === STATUS.ASSIGNED ||
                !canChangeStatus ||
                !hasStoredAssignment ||
                ![STATUS.OPEN, STATUS.WORKING].includes(ticket.status)
              }
              onClick={() => setReopenConfirmOpen(true)}
              title={
                hasStoredAssignment
                  ? "Riporta il ticket allo stato Assegnato"
                  : "Prima assegna il ticket a un utente"
              }
            >
              <StatusIcon status={STATUS.ASSIGNED} size={13} />
              Assegnato
            </button>

            <button
              type="button"
              className={`tk-status-choice ${ticket.status === STATUS.WORKING ? "is-active" : ""}`}
              disabled={
                ticket.status === STATUS.WORKING ||
                !canChangeStatus ||
                ![STATUS.OPEN, STATUS.ASSIGNED].includes(ticket.status)
              }
              onClick={() => onStatusChange(ticket.id, STATUS.WORKING)}
            >
              <StatusIcon status={STATUS.WORKING} size={13} />
              In lavorazione
            </button>

            <button
              type="button"
              className={`tk-status-choice ${ticket.status === STATUS.CLOSED ? "is-active" : ""}`}
              disabled={
                ticket.status === STATUS.CLOSED ||
                !canChangeStatus ||
                typeof onCloseStatusRequest !== "function"
              }
              onClick={() => onCloseStatusRequest?.(ticket)}
              title={
                ticket.status === STATUS.CLOSED
                  ? "Stato attuale"
                  : "Apri la conversazione e inserisci la risposta di chiusura"
              }
            >
              <StatusIcon status={STATUS.CLOSED} size={13} />
              Chiuso
            </button>
          </div>
        </div>

        {ticket.updatedAt && (
          <div className="tk-last-update">
            Ultimo aggiornamento: <b>{formatDateTime(ticket.updatedAt)}</b>
            {ticket.updatedBy ? <> · {displayUser(ticket.updatedBy)}</> : null}
          </div>
        )}
      </article>

      {reopenConfirmOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="tk-chat-join-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Conferma riapertura ticket"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setReopenConfirmOpen(false);
              }
            }}
          >
            <div className="tk-chat-join-card">
              <div className="tk-chat-join-icon">
                <AlertTriangle size={22} />
              </div>
              <h3>Riportare il ticket ad {reopenTargetLabel}?</h3>
              <p>
                {hasStoredAssignment
                  ? "Il ticket tornerà allo stato Assegnato. L’assegnazione esistente resta memorizzata e potrai cambiare assegnatario con la matita."
                  : "Il ticket tornerà Aperto e disponibile per una nuova assegnazione."}
              </p>
              <div className="tk-chat-join-actions">
                <button
                  type="button"
                  className="tk-chat-join-cancel"
                  onClick={() => setReopenConfirmOpen(false)}
                >
                  Annulla
                </button>
                <button
                  type="button"
                  className="tk-chat-join-confirm"
                  onClick={async () => {
                    setReopenConfirmOpen(false);
                    await onStatusChange(ticket.id, reopenTargetStatus);
                  }}
                >
                  <Check size={15} />
                  <span>Sì, torna a {reopenTargetLabel}</span>
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {previewAttachment &&
        isPreviewableImageAttachment(previewAttachment) &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="tk-image-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Anteprima allegato"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setPreviewAttachment(null);
              }
            }}
          >
            <div className="tk-image-modal-card">
              <button
                type="button"
                className="tk-image-close"
                onClick={() => setPreviewAttachment(null)}
                aria-label="Chiudi anteprima"
              >
                <X size={19} />
              </button>

              <img
                src={previewAttachment.dataUrl || previewAttachment.url}
                alt={previewAttachment.name || "Allegato"}
              />

              <div className="tk-image-modal-footer">
                <div className="tk-image-caption">
                  {previewAttachment.name || "Allegato"}
                </div>

                <button
                  type="button"
                  className="tk-image-download"
                  onClick={() => downloadAttachment(previewAttachment)}
                  disabled={
                    savingAttachmentUrl ===
                    (previewAttachment.dataUrl || previewAttachment.url)
                  }
                >
                  <Download size={15} />
                  {savingAttachmentUrl ===
                  (previewAttachment.dataUrl || previewAttachment.url)
                    ? "Salvataggio..."
                    : "Salva allegato"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

// Converte una riga chat del backend nel formato usato dalla UI.
const normalizeChatMessage = (row) => {
  if (!row || typeof row !== "object") return null;

  const id = Number(row.id_messaggio);

  return {
    id: Number.isFinite(id) ? id : row.id_messaggio,
    ticketId: Number(row.id_ticket),
    user: normalizeUser(row.utente),
    text: row.messaggio || "",
    attachments: parseStoredAttachments(row.allegato, "Allegato chat"),
    createdAt: row.data_creazione || null,
    modified:
      row.modificato === true ||
      row.modificato === 1 ||
      row.modificato === "1" ||
      String(row.modificato).toLowerCase() === "true",
    updatedAt: row.data_modifica || null,
  };
};

// Crea una firma degli allegati per confrontare messaggi ottimistici e persistiti.
const chatAttachmentFingerprint = (attachments = []) =>
  (Array.isArray(attachments) ? attachments : [])
    .map((attachment) => String(attachment?.name || "").trim().toLowerCase())
    .filter(Boolean)
    .sort()
    .join("|");

// Capisce se un messaggio temporaneo è lo stesso appena salvato dal server.
const isSameOptimisticChatMessage = (pending, persisted) => {
  if (!pending?.pending || !persisted || persisted?.pending) return false;
  if (normalizeUser(pending.user) !== normalizeUser(persisted.user)) return false;
  if (String(pending.text || "").trim() !== String(persisted.text || "").trim()) return false;

  const pendingAttachments = chatAttachmentFingerprint(pending.attachments);
  const persistedAttachments = chatAttachmentFingerprint(persisted.attachments);
  if (pendingAttachments !== persistedAttachments) return false;

  const pendingTime = new Date(pending.createdAt || 0).getTime();
  const persistedTime = new Date(persisted.createdAt || 0).getTime();

  // Se gli orari sono disponibili, consideriamo lo stesso messaggio soltanto
  // entro una finestra breve. Evita di confondere due messaggi uguali inviati
  // in momenti differenti.
  if (Number.isFinite(pendingTime) && Number.isFinite(persistedTime)) {
    return Math.abs(persistedTime - pendingTime) <= 45000;
  }

  return true;
};

// Unisce polling e messaggi ottimistici evitando duplicati nella conversazione.
const mergeChatMessages = (current = [], incoming = []) => {
  const pending = current.filter((message) => message?.pending);
  const persisted = new Map();

  current.forEach((message) => {
    if (message?.pending) return;
    const id = Number(message?.id);
    if (Number.isFinite(id) && id > 0) persisted.set(id, message);
  });

  incoming.forEach((message) => {
    if (!message || message.pending) return;
    const id = Number(message.id);
    if (Number.isFinite(id) && id > 0) persisted.set(id, message);
  });

  const persistedMessages = Array.from(persisted.values()).sort(
    (a, b) => Number(a.id) - Number(b.id),
  );

  // Il polling può ricevere dal DB il messaggio mentre il POST è ancora in
  // attesa. In quel caso rimuoviamo immediatamente la copia ottimistica
  // corrispondente invece di mostrare "messaggio vero + Invio...".
  const consumedPersistedIds = new Set();
  const unmatchedPending = pending.filter((pendingMessage) => {
    const match = [...persistedMessages]
      .reverse()
      .find((persistedMessage) => {
        const persistedId = Number(persistedMessage.id);
        return (
          !consumedPersistedIds.has(persistedId) &&
          isSameOptimisticChatMessage(pendingMessage, persistedMessage)
        );
      });

    if (!match) return true;
    consumedPersistedIds.add(Number(match.id));
    return false;
  });

  return [...persistedMessages, ...unmatchedPending];
};

// Normalizza lo stato di lettura/partecipazione degli utenti della chat.
const normalizeChatReadState = (row) => ({
  user: normalizeUser(row?.utente || ""),
  participant:
    row?.partecipa === true ||
    row?.partecipa === 1 ||
    row?.partecipa === "1" ||
    String(row?.partecipa || "").toLowerCase() === "true",
  lastSeenId: Number(row?.ultimo_id_visualizzato) || 0,
  seenAt: row?.data_visualizzato || null,
  joinedAt: row?.data_partecipazione || null,
});

// Gestisce conversazione, allegati, typing, visualizzazioni e chiusura del ticket.
const TicketChat = ({
  ticket,
  currentUser,
  currentDepartment,
  isDirector,
  onSeen,
  onTicketUpdated,
  openClosureOnMount = false,
}) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatError, setChatError] = useState("");
  const [messageText, setMessageText] = useState("");
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [serverCanWrite, setServerCanWrite] = useState(null);
  const [serverCanManage, setServerCanManage] = useState(null);
  const [serverCanJoin, setServerCanJoin] = useState(false);
  const [isParticipant, setIsParticipant] = useState(false);
  const [readStates, setReadStates] = useState([]);
  const [joinConfirmOpen, setJoinConfirmOpen] = useState(false);
  const [joinBusy, setJoinBusy] = useState(false);
  const [chatTicketMeta, setChatTicketMeta] = useState(null);
  const [requestedStatus, setRequestedStatus] = useState(
    openClosureOnMount ? STATUS.CLOSED : ticket.status || STATUS.OPEN,
  );
  const [statusBusy, setStatusBusy] = useState(false);
  const [closureText, setClosureText] = useState("");
  const [closureAttachment, setClosureAttachment] = useState(null);
  const [closureBusy, setClosureBusy] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [savingAttachmentUrl, setSavingAttachmentUrl] = useState("");

  const fileInputRef = useRef(null);
  const closureInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingStopTimerRef = useRef(null);
  const typingLastSentRef = useRef(0);
  const optimisticIdRef = useRef(0);
  const chatPollInFlightRef = useRef(false);
  const sendInFlightRef = useRef(false);
  const lastMessageIdRef = useRef(0);
  const seenSentRef = useRef(0);
  const shouldStickToBottomRef = useRef(true);

  const isCreator = ticket.createdBy === currentUser;
  const currentResponsibleUser = ticket.assignedTo || ticket.targetUser || "";
  const isAssigned = currentResponsibleUser === currentUser;
  const isDepartmentMember =
    !ticket.assignedTo &&
    !ticket.targetUser &&
    ticket.targetDepartment === currentDepartment;

  const localCanWrite =
    !isDirector && (isCreator || isAssigned || isDepartmentMember);

  const canWrite =
    serverCanWrite === null ? localCanWrite : Boolean(serverCanWrite);

  const localCanManage = !isDirector && (isAssigned || isDepartmentMember);
  const canManage =
    serverCanManage === null ? localCanManage : Boolean(serverCanManage);

  const chatStatus = chatTicketMeta?.stato || ticket.status || STATUS.OPEN;
  const chatIsClosed = chatStatus === STATUS.CLOSED;
  const closureAttachments = parseStoredAttachments(
    chatTicketMeta?.allegato_chiusura,
    "Allegato chiusura",
  );

  // Comunica al backend se l’utente sta scrivendo nella chat.
  const postTyping = async (typing) => {
    if (!ticket?.id || !currentUser || currentUser === "guest" || !canWrite) return;

    const payload = new FormData();
    payload.append("action", "typing");
    payload.append("id_ticket", String(ticket.id));
    payload.append("utente", currentUser);
    payload.append("typing", typing ? "1" : "0");

    try {
      await fetch(CHAT_API_URL, { method: "POST", body: payload, cache: "no-store" });
    } catch (typingError) {
      console.warn("[TICKET CHAT] Stato typing non inviato:", typingError);
    }
  };

  // Legge dal backend gli utenti che stanno scrivendo nella conversazione.
  const loadTyping = async () => {
    if (!ticket?.id || !currentUser || currentUser === "guest") return;

    try {
      const response = await fetch(
        `${CHAT_API_URL}?action=typing&id_ticket=${encodeURIComponent(ticket.id)}&utente=${encodeURIComponent(currentUser)}&_=${Date.now()}`,
        { cache: "no-store" },
      );
      if (!response.ok) return;
      const data = await response.json();
      if (data?.success !== true) return;
      setTypingUsers(
        Array.isArray(data.typing_users)
          ? data.typing_users.map(normalizeUser).filter(Boolean)
          : [],
      );
    } catch {
      // Best-effort.
    }
  };

  // Registra nel database l’ultimo messaggio visualizzato dall’utente corrente.
  const markSeenInDb = async (lastMessageId, { force = false } = {}) => {
    const numericLastId = Number(lastMessageId) || 0;
    if (
      numericLastId <= 0 ||
      !ticket?.id ||
      !currentUser ||
      currentUser === "guest" ||
      (typeof document !== "undefined" && document.visibilityState !== "visible")
    ) {
      return;
    }

    if (!force && numericLastId <= seenSentRef.current) return;
    const previousSent = seenSentRef.current;
    seenSentRef.current = numericLastId;

    const payload = new FormData();
    payload.append("action", "seen");
    payload.append("id_ticket", String(ticket.id));
    payload.append("utente", currentUser);
    payload.append("ultimo_id_messaggio", String(numericLastId));

    try {
      const data = await apiFetch("POST visualizzato chat", CHAT_API_URL, {
        method: "POST",
        body: payload,
      });
      if (Array.isArray(data.read_states)) {
        setReadStates(data.read_states.map(normalizeChatReadState));
      }
    } catch (seenError) {
      seenSentRef.current = previousSent;
      console.warn("[TICKET CHAT] Visualizzato non registrato:", seenError);
    }
  };

  // Carica i messaggi della chat e aggiorna permessi, partecipanti e stato di lettura.
  const loadChat = async ({ silent = false, reset = false } = {}) => {
    if (!ticket?.id || !currentUser || currentUser === "guest") return;
    if (chatPollInFlightRef.current) return;

    chatPollInFlightRef.current = true;
    if (!silent) setLoading(true);

    const afterId = reset ? 0 : Math.max(0, Number(lastMessageIdRef.current) || 0);

    try {
      const data = await apiFetch(
        "GET chat ticket",
        `${CHAT_API_URL}?action=chat&id_ticket=${encodeURIComponent(ticket.id)}&utente=${encodeURIComponent(currentUser)}&after_id=${encodeURIComponent(afterId)}&_=${Date.now()}`,
      );

      const incoming = Array.isArray(data.messages)
        ? data.messages.map(normalizeChatMessage).filter(Boolean)
        : [];

      const incomingMax = incoming.reduce((max, message) => {
        const id = Number(message.id) || 0;
        return id > max ? id : max;
      }, 0);

      if (reset) {
        setMessages((current) => {
          const pending = current.filter((message) => message.pending);
          return mergeChatMessages(pending, incoming);
        });
        lastMessageIdRef.current = incomingMax;
      } else if (incoming.length > 0) {
        setMessages((current) => mergeChatMessages(current, incoming));
        lastMessageIdRef.current = Math.max(lastMessageIdRef.current, incomingMax);
      }

      setServerCanWrite(Boolean(data.can_write));
      setServerCanManage(Boolean(data.can_manage));
      setServerCanJoin(Boolean(data.can_join));
      setIsParticipant(Boolean(data.is_participant));
      setChatTicketMeta(data.ticket || null);

      if (Array.isArray(data.read_states)) {
        setReadStates(data.read_states.map(normalizeChatReadState));
      }

      if (data.ticket?.stato) {
        setRequestedStatus((current) =>
          current === STATUS.CLOSED && data.ticket.stato !== STATUS.CLOSED
            ? current
            : data.ticket.stato,
        );
      }

      setTypingUsers(
        Array.isArray(data.typing_users)
          ? data.typing_users.map(normalizeUser).filter(Boolean)
          : [],
      );
      setChatError("");

      const lastId = Math.max(lastMessageIdRef.current, incomingMax);
      if (lastId > 0) {
        void markSeenInDb(lastId);
      }

      if (typeof onSeen === "function" && reset) {
        onSeen(ticket.id, incoming.length);
      }
    } catch (requestError) {
      console.error("[TICKET CHAT] Caricamento fallito:", requestError);
      if (!silent) {
        setChatError(requestError.message || "Impossibile caricare la conversazione.");
      }
    } finally {
      chatPollInFlightRef.current = false;
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    setMessages([]);
    setMessageText("");
    setFiles([]);
    setServerCanWrite(null);
    setServerCanManage(null);
    setServerCanJoin(false);
    setIsParticipant(false);
    setReadStates([]);
    setJoinConfirmOpen(false);
    setJoinBusy(false);
    setChatTicketMeta(null);
    setRequestedStatus(
      openClosureOnMount ? STATUS.CLOSED : ticket.status || STATUS.OPEN,
    );
    setClosureText("");
    setClosureAttachment(null);
    if (closureInputRef.current) closureInputRef.current.value = "";
    setTypingUsers([]);
    setChatError("");
    lastMessageIdRef.current = 0;
    seenSentRef.current = 0;
    shouldStickToBottomRef.current = true;
    chatPollInFlightRef.current = false;

    loadChat({ reset: true });

    // Polling incrementale: il server restituisce solo i messaggi successivi
    // all'ultimo id già presente. Evita reload completo, flicker e duplicati.
    const chatIntervalId = window.setInterval(
      () => loadChat({ silent: true }),
      1400,
    );

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        void loadChat({ silent: true });
        void markSeenInDb(lastMessageIdRef.current, { force: true });
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.clearInterval(chatIntervalId);
      document.removeEventListener("visibilitychange", handleVisibility);
      if (typingStopTimerRef.current) window.clearTimeout(typingStopTimerRef.current);
      postTyping(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.id, currentUser, openClosureOnMount]);

  useEffect(() => {
    if (!shouldStickToBottomRef.current) return;
    messagesEndRef.current?.scrollIntoView({
      behavior: messages.length > 1 ? "smooth" : "auto",
      block: "end",
    });
  }, [messages.length]);

  useEffect(() => {
    if (!previewAttachment || typeof document === "undefined") return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setPreviewAttachment(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [previewAttachment]);

  // Aggiorna il testo del messaggio e invia lo stato typing quando necessario.
  const handleMessageTextChange = (event) => {
    const value = event.target.value;
    setMessageText(value);

    if (typingStopTimerRef.current) window.clearTimeout(typingStopTimerRef.current);

    if (!value.trim()) {
      postTyping(false);
      return;
    }

    const now = Date.now();
    if (now - typingLastSentRef.current > 850) {
      typingLastSentRef.current = now;
      postTyping(true);
    }

    typingStopTimerRef.current = window.setTimeout(() => postTyping(false), 1800);
  };

  // Valida e aggiunge nuovi allegati alla bozza del messaggio chat.
  const addFiles = async (incomingFiles) => {
    const incoming = Array.from(incomingFiles || []).filter(Boolean);
    if (!incoming.length) return;
    setChatError("");
    try {
      const next = await prepareAttachments(incoming, files);
      setFiles(next);
    } catch (attachmentError) {
      setChatError(attachmentError.message || "Errore durante il caricamento degli allegati.");
    }
  };

  // Gestisce gli allegati scelti tramite input file della chat.
  const handleFileChange = async (event) => {
    await addFiles(event.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Gestisce il drag & drop di allegati dentro la chat.
  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragging(false);
    await addFiles(event.dataTransfer.files);
  };

  // Intercetta immagini incollate dagli appunti e le aggiunge alla chat.
  const handlePaste = async (event) => {
    const pastedFiles = getClipboardImageFiles(event.clipboardData);
    if (!pastedFiles.length) return;
    event.preventDefault();
    await addFiles(pastedFiles);
  };

  // Rimuove un allegato dalla bozza del messaggio.
  const removeFile = (index) => {
    setFiles((current) => current.filter((_, fileIndex) => fileIndex !== index));
  };

  // Invia il messaggio e gli allegati della chat usando un aggiornamento ottimistico.
  const sendMessage = async () => {
    const cleanText = messageText.trim();
    if ((!cleanText && files.length === 0) || !canWrite) return;

    const draftText = messageText;
    const draftFiles = files;
    const tempId = `temp-${Date.now()}-${++optimisticIdRef.current}`;
    const optimisticMessage = {
      id: tempId,
      ticketId: Number(ticket.id),
      user: currentUser,
      text: cleanText,
      attachments: draftFiles.map((attachment) => ({
        dataUrl: attachment.dataUrl || null,
        url: attachment.dataUrl || null,
        name: attachment.name || "Allegato",
        type: attachment.type || "",
        size: Number(attachment.size) || 0,
      })),
      createdAt: new Date().toISOString(),
      modified: false,
      updatedAt: null,
      pending: true,
    };

    sendInFlightRef.current = true;
    setMessages((current) => [...current, optimisticMessage]);
    setMessageText("");
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
    postTyping(false);

    const payload = new FormData();
    payload.append("action", "send");
    payload.append("id_ticket", String(ticket.id));
    payload.append("utente", currentUser);
    payload.append("messaggio", cleanText);
    draftFiles.forEach((attachment) => {
      if (attachment.file) payload.append("allegato[]", attachment.file);
    });

    // UX ottimistica: la bolla compare subito e il composer resta immediatamente utilizzabile.
    setChatError("");

    try {
      const data = await apiFetch("POST messaggio chat", CHAT_API_URL, {
        method: "POST",
        body: payload,
      });

      const created = normalizeChatMessage(data.chat_message);
      setMessages((current) => {
        const withoutTemp = current.filter((message) => message.id !== tempId);
        return created
          ? mergeChatMessages(withoutTemp, [created])
          : withoutTemp;
      });

      if (created) {
        lastMessageIdRef.current = Math.max(
          lastMessageIdRef.current,
          Number(created.id) || 0,
        );
      }
      if (Array.isArray(data.read_states)) {
        setReadStates(data.read_states.map(normalizeChatReadState));
      }
      shouldStickToBottomRef.current = true;
      void markSeenInDb(lastMessageIdRef.current, { force: true });
    } catch (requestError) {
      console.error("[TICKET CHAT] Invio fallito:", requestError);
      setMessages((current) => current.filter((message) => message.id !== tempId));
      setMessageText((current) => (current ? current : draftText));
      setFiles((current) => (current.length ? current : draftFiles));
      setChatError(requestError.message || "Impossibile inviare il messaggio.");
    } finally {
      sendInFlightRef.current = false;
    }
  };

  // Gestisce il cambio stato richiesto direttamente dalla conversazione.
  const handleChatStatusChange = async (event) => {
    const nextStatus = event.target.value;

    if (!canManage || chatIsClosed || statusBusy || closureBusy) return;

    if (nextStatus === STATUS.CLOSED) {
      setRequestedStatus(STATUS.CLOSED);
      setChatError("");
      return;
    }

    // Aperto e Assegnato sono stati gestiti dal flusso del ticket:
    // non vengono impostati manualmente dalla chat.
    if (nextStatus !== STATUS.WORKING) {
      setRequestedStatus(chatStatus);
      return;
    }

    if (chatStatus === STATUS.WORKING) {
      setRequestedStatus(STATUS.WORKING);
      return;
    }

    const payload = new FormData();
    payload.append("action", "status");
    payload.append("id_ticket", String(ticket.id));
    payload.append("utente", currentUser);
    payload.append("stato", STATUS.WORKING);

    try {
      setStatusBusy(true);
      setChatError("");
      await apiFetch("POST stato ticket da chat", CHAT_API_URL, {
        method: "POST",
        body: payload,
      });
      setRequestedStatus(STATUS.WORKING);
      await loadChat({ silent: true });
      if (typeof onTicketUpdated === "function") {
        await onTicketUpdated(ticket.id);
      }
    } catch (requestError) {
      console.error("[TICKET CHAT] Cambio stato fallito:", requestError);
      setRequestedStatus(chatStatus);
      setChatError(requestError.message || "Impossibile aggiornare lo stato.");
    } finally {
      setStatusBusy(false);
    }
  };

  // Prepara l’allegato scelto per la chiusura dalla chat.
  const handleClosureFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setChatError("");
      const attachment = await readAttachment(file);
      setClosureAttachment(attachment);
    } catch (attachmentError) {
      setChatError(
        attachmentError.message || "Errore caricamento allegato di chiusura.",
      );
      if (closureInputRef.current) closureInputRef.current.value = "";
    }
  };

  // Chiude il ticket direttamente dalla conversazione con risposta e allegato opzionale.
  const closeTicketFromChat = async () => {
    const response = closureText.trim();
    if (!canManage || chatIsClosed || closureBusy) return;

    if (!response) {
      setChatError("Inserisci la risposta di chiusura.");
      return;
    }

    const payload = new FormData();
    payload.append("action", "close");
    payload.append("id_ticket", String(ticket.id));
    payload.append("utente", currentUser);
    payload.append("risposta_chiusura", response);

    if (closureAttachment?.file) {
      payload.append(
        "allegato_chiusura",
        closureAttachment.file,
        closureAttachment.name || closureAttachment.file.name,
      );
    }

    try {
      setClosureBusy(true);
      setChatError("");
      await apiFetch("POST chiusura ticket da chat", CHAT_API_URL, {
        method: "POST",
        body: payload,
      });
      setClosureText("");
      setClosureAttachment(null);
      if (closureInputRef.current) closureInputRef.current.value = "";
      setRequestedStatus(STATUS.CLOSED);
      await loadChat({ silent: true });
      if (typeof onTicketUpdated === "function") {
        await onTicketUpdated(ticket.id);
      }
    } catch (requestError) {
      console.error("[TICKET CHAT] Chiusura fallita:", requestError);
      setChatError(requestError.message || "Impossibile chiudere il ticket.");
    } finally {
      setClosureBusy(false);
    }
  };

  // Aggiunge l’utente corrente tra i partecipanti autorizzati alla conversazione.
  const joinChat = async () => {
    if (joinBusy || !serverCanJoin || chatIsClosed) return;

    const payload = new FormData();
    payload.append("action", "join");
    payload.append("id_ticket", String(ticket.id));
    payload.append("utente", currentUser);

    try {
      setJoinBusy(true);
      setChatError("");
      const data = await apiFetch("POST partecipa chat", CHAT_API_URL, {
        method: "POST",
        body: payload,
      });
      setServerCanWrite(true);
      setServerCanJoin(false);
      setIsParticipant(true);
      if (Array.isArray(data.read_states)) {
        setReadStates(data.read_states.map(normalizeChatReadState));
      }
      setJoinConfirmOpen(false);
      shouldStickToBottomRef.current = true;
      await loadChat({ silent: true });
    } catch (requestError) {
      console.error("[TICKET CHAT] Partecipazione fallita:", requestError);
      setChatError(requestError.message || "Impossibile partecipare alla chat.");
    } finally {
      setJoinBusy(false);
    }
  };

  // Restituisce gli utenti che hanno già visualizzato uno specifico messaggio.
  const getSeenByForMessage = (message) => {
    const messageId = Number(message?.id) || 0;
    if (messageId <= 0) return [];

    return readStates
      .filter(
        (state) =>
          state.user &&
          state.user !== currentUser &&
          Number(state.lastSeenId) >= messageId,
      )
      .sort((a, b) => String(a.user).localeCompare(String(b.user)));
  };

  // Apre la preview per le immagini oppure il file in una nuova scheda per gli altri formati.
  const openAttachment = (attachment) => {
    if (!attachment) return;
    if (isPreviewableImageAttachment(attachment)) {
      setPreviewAttachment(attachment);
      return;
    }
    const url = attachment.dataUrl || attachment.url;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  // Scarica l’allegato; se il fetch fallisce prova ad aprirlo direttamente in una nuova scheda.
  const downloadAttachment = async (attachment) => {
    const attachmentUrl = attachment?.dataUrl || attachment?.url;
    if (!attachmentUrl || savingAttachmentUrl === attachmentUrl) return;
    setSavingAttachmentUrl(attachmentUrl);
    try {
      const response = await fetch(attachmentUrl, { cache: "no-store" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = attachment.name || getAttachmentName(attachmentUrl, "allegato");
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    } catch (downloadError) {
      console.error("[TICKET CHAT] Download allegato fallito:", downloadError);
      window.open(attachmentUrl, "_blank", "noopener,noreferrer");
    } finally {
      setSavingAttachmentUrl("");
    }
  };

  const typingLabel =
    typingUsers.length === 1
      ? `${displayUser(typingUsers[0])} sta scrivendo`
      : typingUsers.length > 1
        ? `${typingUsers.map(displayUser).join(", ")} stanno scrivendo`
        : "";

  const persistedMessageCount = messages.filter((message) => !message.pending).length;

  return (
    <>
      <section className="tk-chat-panel" onPaste={handlePaste}>
        <div className="tk-chat-heading">
          <div>
            <span className="tk-section-kicker">Conversazione</span>
            <h3>Chat del ticket</h3>
            <p>
              {persistedMessageCount}{" "}
              {persistedMessageCount === 1 ? "messaggio" : "messaggi"}
            </p>
          </div>
          <div className="tk-chat-heading-actions">
            {canManage && !chatIsClosed && (
              <button
                type="button"
                className="tk-chat-close-direct"
                onClick={() => setRequestedStatus(STATUS.CLOSED)}
                disabled={closureBusy}
                title="Chiudi ticket con risposta conclusiva"
              >
                <CheckCircle2 size={14} />
                <span>Chiudi ticket</span>
              </button>
            )}

            <div className={`tk-chat-live ${loading ? "is-loading" : ""}`}>
              <span />
              {loading ? "Caricamento" : "Live"}
            </div>
          </div>
        </div>

        <div
          ref={messagesContainerRef}
          className="tk-chat-messages"
          onScroll={(event) => {
            const element = event.currentTarget;
            const distanceFromBottom =
              element.scrollHeight - element.scrollTop - element.clientHeight;
            shouldStickToBottomRef.current = distanceFromBottom < 90;
          }}
        >
          {loading && messages.length === 0 ? (
            <div className="tk-chat-empty">
              <LoaderCircle size={18} />
              Carico la conversazione...
            </div>
          ) : messages.length === 0 ? (
            <div className="tk-chat-empty">
              <MessageSquare size={22} />
              <strong>Nessun messaggio</strong>
              <span>Scrivi il primo messaggio della conversazione.</span>
            </div>
          ) : (
            messages.map((message) => {
              const mine = message.user === currentUser;
              const seenBy = mine && !message.pending
                ? getSeenByForMessage(message)
                : [];
              const seenNames = seenBy.map((state) => displayUser(state.user));
              const seenText =
                seenNames.length === 0
                  ? "Inviato"
                  : seenNames.length === 1
                    ? `Visualizzato da ${seenNames[0]}`
                    : `Visualizzato da ${seenNames[0]} +${seenNames.length - 1}`;
              return (
                <div
                  className={`tk-chat-message-row ${mine ? "is-mine" : "is-other"} ${message.pending ? "is-pending" : ""}`}
                  key={message.id}
                >
                  <div className="tk-chat-message-meta">
                    {!mine && <strong>{displayUser(message.user)}</strong>}
                    <span>{formatDateTime(message.createdAt)}</span>
                  </div>
                  <div className="tk-chat-bubble">
                    {message.text && <p>{message.text}</p>}
                    {message.attachments.length > 0 && (
                      <div className="tk-chat-attachments">
                        {message.attachments.map((attachment, index) => (
                          <button
                            type="button"
                            className="tk-chat-attachment"
                            key={`${message.id}-${attachment.url || attachment.name}-${index}`}
                            onClick={() => openAttachment(attachment)}
                            title={attachment.name}
                          >
                            {isPreviewableImageAttachment(attachment) ? (
                              <img src={attachment.dataUrl || attachment.url} alt={attachment.name || "Allegato"} />
                            ) : (
                              <span className="tk-chat-file-icon"><Paperclip size={16} /></span>
                            )}
                            <span>{attachment.name || `Allegato ${index + 1}`}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {message.modified && <small className="tk-chat-edited">modificato</small>}
                    {mine && message.pending && (
                      <small
                        className="tk-chat-seen"
                        title="Messaggio in invio"
                      >
                        <CheckCheck size={10} />
                        <span>Inviato</span>
                      </small>
                    )}
                    {mine && !message.pending && (
                      <small
                        className={`tk-chat-seen ${seenBy.length > 0 ? "is-seen" : ""}`}
                        title={
                          seenNames.length > 0
                            ? `Visualizzato da ${seenNames.join(", ")}`
                            : "Messaggio inviato"
                        }
                        data-seen-users={
                          seenNames.length > 0
                            ? seenNames.join(", ")
                            : ""
                        }
                      >
                        <CheckCheck size={10} />
                        <span>{seenText}</span>
                      </small>
                    )}
                  </div>
                  {mine && <span className="tk-chat-me">Tu</span>}
                </div>
              );
            })
          )}

          {typingUsers.length > 0 && (
            <div className="tk-chat-typing-row">
              <div className="tk-chat-typing-bubble" aria-label={typingLabel}>
                <span /><span /><span />
              </div>
              <small>{typingLabel}</small>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {chatIsClosed && (
          <div
            className={`tk-chat-closure-summary ${
              isMissingInfoClosure(chatTicketMeta?.risposta_chiusura)
                ? "is-missing-info"
                : "is-normal-close"
            }`}
          >
            <div className="tk-chat-closure-title">
              <CheckCircle2 size={16} />
              <div>
                <strong>Ticket chiuso</strong>
                <span>
                  {chatTicketMeta?.utente_chiusura
                    ? `${displayUser(chatTicketMeta.utente_chiusura)} · `
                    : ""}
                  {formatDateTime(chatTicketMeta?.data_chiusura)}
                </span>
              </div>
            </div>
            <p>{chatTicketMeta?.risposta_chiusura || "—"}</p>
            {closureAttachments.length > 0 && (
              <div className="tk-chat-closure-files">
                {closureAttachments.map((attachment, index) => (
                  <button
                    type="button"
                    key={`${attachment.url || attachment.name}-${index}`}
                    onClick={() => openAttachment(attachment)}
                    title={attachment.name}
                  >
                    <Paperclip size={13} />
                    <span>{attachment.name || `Allegato ${index + 1}`}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {!chatIsClosed && canManage && requestedStatus === STATUS.CLOSED && (
          <div className="tk-chat-close-panel">
            <div className="tk-chat-close-heading">
              <div>
                <span className="tk-section-kicker">Chiusura ticket</span>
                <strong>Risposta di chiusura</strong>
              </div>
              <button
                type="button"
                className="tk-chat-close-cancel"
                onClick={() => {
                  setRequestedStatus(chatStatus);
                  setClosureText("");
                  setClosureAttachment(null);
                  if (closureInputRef.current) closureInputRef.current.value = "";
                }}
              >
                Annulla
              </button>
            </div>

            <textarea
              rows={4}
              value={closureText}
              onChange={(event) => setClosureText(event.target.value)}
              placeholder="Scrivi la risposta conclusiva del ticket..."
            />

            <div className="tk-chat-close-actions">
              <label className="tk-chat-close-file">
                <Paperclip size={14} />
                <span>
                  {closureAttachment?.name || "Allegato opzionale"}
                </span>
                <input
                  ref={closureInputRef}
                  type="file"
                  accept={ACCEPT_ATTRIBUTE}
                  onChange={handleClosureFileChange}
                />
              </label>

              {closureAttachment && (
                <button
                  type="button"
                  className="tk-chat-close-remove-file"
                  onClick={() => {
                    setClosureAttachment(null);
                    if (closureInputRef.current) closureInputRef.current.value = "";
                  }}
                  aria-label="Rimuovi allegato di chiusura"
                >
                  <X size={13} />
                </button>
              )}

              <button
                type="button"
                className="tk-chat-close-submit"
                onClick={closeTicketFromChat}
                disabled={closureBusy || !closureText.trim()}
              >
                {closureBusy ? <LoaderCircle size={15} /> : <CheckCircle2 size={15} />}
                <span>{closureBusy ? "Chiusura..." : "Conferma chiusura"}</span>
              </button>
            </div>
          </div>
        )}

        {chatError && (
          <div className="tk-chat-error"><AlertTriangle size={14} />{chatError}</div>
        )}

        {canWrite ? (
          <div className="tk-chat-composer">
            {files.length > 0 && (
              <div className="tk-chat-selected-files">
                {files.map((attachment, index) => (
                  <div className="tk-chat-selected-file" key={`${attachment.name}-${index}`}>
                    <Paperclip size={12} /><span>{attachment.name}</span>
                    <button type="button" onClick={() => removeFile(index)} aria-label={`Rimuovi ${attachment.name}`}>
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="tk-chat-compose-row">
              <button
                type="button"
                className={`tk-chat-attach-button ${dragging ? "is-dragging" : ""}`}
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
                onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = "copy"; setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                title="Allega file"
              >
                <Paperclip size={18} />
              </button>
              <input ref={fileInputRef} type="file" multiple accept={ACCEPT_ATTRIBUTE} onChange={handleFileChange} className="tk-chat-file-input" />
              <textarea
                rows={1}
                maxLength={4000}
                value={messageText}
                onChange={handleMessageTextChange}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Scrivi un messaggio..."
              />
              <button
                type="button"
                className="tk-chat-send"
                onClick={sendMessage}
                disabled={!messageText.trim() && files.length === 0}
              >
                <Send size={17} />
                <span>Invia</span>
              </button>
            </div>
            <div className="tk-chat-compose-hint">
              Invio con Enter · Shift+Enter va a capo · CTRL+V incolla screenshot · max 8 MB
            </div>
          </div>
        ) : serverCanJoin && !chatIsClosed ? (
          <div className="tk-chat-readonly tk-chat-join-strip">
            <div>
              <MessageSquare size={15} />
              <span>Stai visualizzando la conversazione del reparto.</span>
            </div>
            <button
              type="button"
              className="tk-chat-join-button"
              onClick={() => setJoinConfirmOpen(true)}
            >
              <UserPlus size={14} />
              <span>Partecipa alla chat</span>
            </button>
          </div>
        ) : (
          <div className="tk-chat-readonly">
            <MessageSquare size={15} />
            {chatIsClosed
              ? "Ticket chiuso: conversazione disponibile in sola lettura."
              : isParticipant
                ? "Partecipazione registrata. La conversazione è momentaneamente in sola lettura."
                : "Conversazione disponibile in sola lettura."}
          </div>
        )}
      </section>

      {joinConfirmOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="tk-chat-join-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Conferma partecipazione chat"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !joinBusy) {
                setJoinConfirmOpen(false);
              }
            }}
          >
            <div className="tk-chat-join-card">
              <div className="tk-chat-join-icon">
                <UserPlus size={22} />
              </div>
              <h3>Partecipare alla chat?</h3>
              <p>
                Da questo momento potrai scrivere nella conversazione insieme ai
                diretti interessati del ticket.
              </p>
              <div className="tk-chat-join-actions">
                <button
                  type="button"
                  className="tk-chat-join-cancel"
                  onClick={() => setJoinConfirmOpen(false)}
                  disabled={joinBusy}
                >
                  Annulla
                </button>
                <button
                  type="button"
                  className="tk-chat-join-confirm"
                  onClick={joinChat}
                  disabled={joinBusy}
                >
                  {joinBusy ? <LoaderCircle size={15} /> : <UserPlus size={15} />}
                  <span>{joinBusy ? "Accesso..." : "Sì, partecipa"}</span>
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {previewAttachment &&
        isPreviewableImageAttachment(previewAttachment) &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="tk-image-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Anteprima allegato chat"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setPreviewAttachment(null);
            }}
          >
            <div className="tk-image-modal-card">
              <button type="button" className="tk-image-close" onClick={() => setPreviewAttachment(null)} aria-label="Chiudi anteprima">
                <X size={19} />
              </button>
              <img src={previewAttachment.dataUrl || previewAttachment.url} alt={previewAttachment.name || "Allegato"} />
              <div className="tk-image-modal-footer">
                <div className="tk-image-caption">{previewAttachment.name || "Allegato"}</div>
                <button
                  type="button"
                  className="tk-image-download"
                  onClick={() => downloadAttachment(previewAttachment)}
                  disabled={savingAttachmentUrl === (previewAttachment.dataUrl || previewAttachment.url)}
                >
                  <Download size={15} />
                  {savingAttachmentUrl === (previewAttachment.dataUrl || previewAttachment.url) ? "Salvataggio..." : "Salva allegato"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

// Componente principale: orchestra caricamenti API, filtri, tabelle, modali e azioni del ticketing.
function TicketPage() {
  const { user } = useAuth() || {};

  const currentUser = useMemo(() => normalizeUser(user), [user]);

  useEffect(() => {
    document.title = "Utility Web - Ticket";
  }, []);

  // Attiva le notifiche push e registra la sottoscrizione sul backend.
  const enableNotifications = async () => {
    try {
      setError("");

      if (!currentUser || currentUser === "guest") {
        throw new Error("Devi essere autenticato per attivare le notifiche.");
      }

      if (!("Notification" in window)) {
        throw new Error("Questo browser non supporta le notifiche.");
      }

      if (!("serviceWorker" in navigator)) {
        throw new Error("Questo browser non supporta i Service Worker.");
      }

      if (!("PushManager" in window)) {
        throw new Error("Questo browser non supporta Web Push.");
      }

      console.log("[PUSH] Registro Service Worker...");

      const registration = await navigator.serviceWorker.register("/sw.js");

      await navigator.serviceWorker.ready;

      console.log("[PUSH] Service Worker pronto:", registration);

      let permission = Notification.permission;

      if (permission === "default") {
        permission = await Notification.requestPermission();
      }

      console.log("[PUSH] Permesso:", permission);

      if (permission !== "granted") {
        throw new Error("Permesso notifiche non concesso.");
      }

      /*
       * Controlliamo se Chrome ha già una subscription.
       */
      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        console.log("[PUSH] Creo nuova PushSubscription...");

        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,

          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
      }

      console.log("[PUSH] Subscription:", subscription);

      /*
       * toJSON() ci restituisce:
       *
       * endpoint
       * expirationTime
       * keys.p256dh
       * keys.auth
       */
      const subscriptionJSON = subscription.toJSON();

      console.log("[PUSH] Subscription JSON:", subscriptionJSON);

      console.log("[PUSH] Associo browser all'utente:", currentUser);

      const response = await fetch(PUSH_SUBSCRIBE_URL, {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          utente: currentUser,
          subscription: subscriptionJSON,
        }),
      });

      const text = await response.text();

      console.log("[PUSH] Risposta RAW server:", text);

      let data;

      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(
          "Il server Push ha restituito una risposta non valida: " + text,
        );
      }

      if (!response.ok || data.success === false) {
        throw new Error(data.error || `Errore HTTP ${response.status}`);
      }

      console.log("[PUSH] Browser registrato:", data);

      setNotificationsEnabled(true);
      setPushStatusChecked(true);
      setNotice(`Notifiche attivate per ${displayUser(currentUser)}.`);
    } catch (pushError) {
      console.error("[PUSH] ERRORE:", pushError);

      setError(
        pushError?.message || "Errore durante l'attivazione delle notifiche.",
      );
    }
  };
  const [departments, setDepartments] = useState(FALLBACK_DEPARTMENTS);
  const [serverDepartment, setServerDepartment] = useState(null);
  const [serverViewAll, setServerViewAll] = useState(null);

  const currentDepartment = useMemo(
    () => serverDepartment || findDepartment(currentUser, departments),
    [currentUser, departments, serverDepartment],
  );

  // Normalizza il nome di un reparto confrontandolo con quelli disponibili.
  const resolveDepartmentKey = (value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (!normalized || normalized === "—" || normalized === "non assegnato") {
      return "";
    }

    return (
      Object.keys(departments).find(
        (department) => department.toLowerCase() === normalized,
      ) || ""
    );
  };

  // Ricava il reparto operativo attuale di un ticket.
  const getTicketCurrentDepartment = (ticket) => {
    if (!ticket) return resolveDepartmentKey(currentDepartment);

    const responsibleUser = ticket.assignedTo || ticket.targetUser || "";
    const responsibleDepartment = responsibleUser
      ? findDepartment(responsibleUser, departments)
      : "";

    return (
      resolveDepartmentKey(responsibleDepartment) ||
      resolveDepartmentKey(ticket.targetDepartment) ||
      resolveDepartmentKey(currentDepartment)
    );
  };

  const isDirector =
    serverViewAll !== null ? serverViewAll : GLOBAL_VIEWERS.has(currentUser);

  const [activeTab, setActiveTab] = useState("inbox");
  const [tickets, setTickets] = useState([]);

  // "Stato dei miei ticket" viene caricato dal PHP a pagine da 50.
  const [myTicketsPage, setMyTicketsPage] = useState([]);
  const [minePage, setMinePage] = useState(1);
  const [mineLoading, setMineLoading] = useState(false);
  const [minePagination, setMinePagination] = useState({
    page: 1,
    perPage: 50,
    total: 0,
    totalPages: 1,
    hasPrevious: false,
    hasNext: false,
  });
  const [mineStats, setMineStats] = useState({
    total: 0,
    open: 0,
    assigned: 0,
    working: 0,
    closed: 0,
  });

  const [apiLoading, setApiLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [dangerNotice, setDangerNotice] = useState("");
  const [error, setError] = useState("");
  const [inboxSort, setInboxSort] = useState("desc");
  const [inboxView, setInboxView] = useState("grid");
  const [mineSort, setMineSort] = useState("desc");
  const [mineStatusFilter, setMineStatusFilter] = useState("all");
  const [mineUrgentFilter, setMineUrgentFilter] = useState("all");
  const [mineSearch, setMineSearch] = useState("");
  const [mineSearchDebounced, setMineSearchDebounced] = useState("");
  // Gruppo attualmente selezionato nella sezione Ticket chiusi.
  const [closedFilter, setClosedFilter] = useState("mine");

  // Testo scritto dall'utente nel campo di ricerca dei ticket chiusi.
  const [closedSearch, setClosedSearch] = useState("");

  // Versione ritardata della ricerca: evita di chiamare il PHP a ogni tasto.
  const [closedSearchDebounced, setClosedSearchDebounced] = useState("");

  // Intervallo opzionale applicato alla data di chiusura del ticket.
  const [closedDateFrom, setClosedDateFrom] = useState("");
  const [closedDateTo, setClosedDateTo] = useState("");

  // Ordinamento dell'archivio chiuso. Di default mostra i più recenti per primi.
  const [closedSort] = useState("desc");

  // Contiene ESCLUSIVAMENTE i massimo 50 ticket chiusi della pagina corrente.
  const [closedTicketsPage, setClosedTicketsPage] = useState([]);

  // Pagina corrente dell'archivio Ticket chiusi.
  const [closedPage, setClosedPage] = useState(1);

  // Indica quando il frontend sta aspettando la pagina dei ticket chiusi dal PHP.
  const [closedLoading, setClosedLoading] = useState(false);

  // Metadati restituiti dal PHP per costruire i pulsanti di paginazione.
  const [closedPagination, setClosedPagination] = useState({
    page: 1,
    perPage: 50,
    total: 0,
    totalPages: 1,
    hasPrevious: false,
    hasNext: false,
  });

  // Contatori complessivi dei quattro filtri della sezione Ticket chiusi.
  // Non rappresentano solo la pagina corrente: arrivano direttamente dal DB.
  const [closedStats, setClosedStats] = useState({
    mine: 0,
    closedByMe: 0,
    department: 0,
    all: 0,
  });

  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [pushStatusChecked, setPushStatusChecked] = useState(false);
  const [pushSupported, setPushSupported] = useState(true);
  const [selectedMyTicketId, setSelectedMyTicketId] = useState(null);
  const [selectedChatTicketId, setSelectedChatTicketId] = useState(null);
  const [selectedChatStartClosing, setSelectedChatStartClosing] = useState(false);
  const [screenCaptureBusy, setScreenCaptureBusy] = useState(false);
  const [screenshotDraft, setScreenshotDraft] = useState(null);
  const [screenshotCopied, setScreenshotCopied] = useState(false);
  const [screenshotShareBusy, setScreenshotShareBusy] = useState(false);
  const [departmentInboxTab, setDepartmentInboxTab] = useState("department");
  const [quickCloseTicketId, setQuickCloseTicketId] = useState(null);
  const [quickCloseBusy, setQuickCloseBusy] = useState(false);

  // Unisce tutti i ticket attualmente presenti nel browser:
  // home, pagina personale e pagina dell'archivio chiusi.
  // In questo modo dettaglio/chat funzionano anche sui ticket storici caricati
  // dal nuovo endpoint paginato closed_tickets.
  const loadedTickets = useMemo(() => {
    const byId = new Map();

    [...tickets, ...myTicketsPage, ...closedTicketsPage].forEach((ticket) => {
      if (!ticket?.id) return;
      byId.set(String(ticket.id), ticket);
    });

    return Array.from(byId.values());
  }, [tickets, myTicketsPage, closedTicketsPage]);

  const selectedMyTicket = useMemo(
    () =>
      selectedMyTicketId == null
        ? null
        : loadedTickets.find(
            (ticket) => String(ticket.id) === String(selectedMyTicketId),
          ) || null,
    [loadedTickets, selectedMyTicketId],
  );

  const selectedChatTicket = useMemo(
    () =>
      selectedChatTicketId == null
        ? null
        : loadedTickets.find(
            (ticket) => String(ticket.id) === String(selectedChatTicketId),
          ) || null,
    [loadedTickets, selectedChatTicketId],
  );

  const quickCloseTicket = useMemo(
    () =>
      quickCloseTicketId == null
        ? null
        : loadedTickets.find(
            (ticket) => String(ticket.id) === String(quickCloseTicketId),
          ) || null,
    [loadedTickets, quickCloseTicketId],
  );

  const openTicketDetail = (
    ticketId,
    { prefillAssignmentDepartment = "" } = {},
  ) => {
    const ticket =
      loadedTickets.find((item) => String(item.id) === String(ticketId)) || null;

    const department =
      resolveDepartmentKey(prefillAssignmentDepartment) ||
      getTicketCurrentDepartment(ticket);

    setSelectedChatTicketId(null);
    setSelectedChatStartClosing(false);
    setAssignmentForm({
      department,
      user: "",
    });
    setClosureForm({ response: "", attachment: null });
    if (closureFileInputRef.current) closureFileInputRef.current.value = "";
    setSelectedMyTicketId(ticketId);
  };

  // Apre il dettaglio del ticket predisponendo la modifica dell’assegnazione.
  const openTicketAssignment = (ticket) => {
    if (!ticket) return;
    setSelectedChatStartClosing(false);

    setSelectedChatTicketId(null);
    setClosureForm({ response: "", attachment: null });
    if (closureFileInputRef.current) closureFileInputRef.current.value = "";
    setAssignmentForm({
      department: getTicketCurrentDepartment(ticket),
      user: "",
    });
    setSelectedMyTicketId(ticket.id);
  };

  // Apre il modale della conversazione associata a un ticket.
  const openTicketChat = (ticketId, { startClosing = false } = {}) => {
    setSelectedMyTicketId(null);
    setSelectedChatStartClosing(Boolean(startClosing));
    setSelectedChatTicketId(ticketId);
  };

  // Acquisisce uno screenshot da aggiungere come allegato quando supportato.
  const captureOtherScreen = async () => {
    if (screenCaptureBusy) return;

    if (!navigator?.mediaDevices?.getDisplayMedia) {
      setError("Il browser non supporta la cattura dello schermo.");
      return;
    }

    let stream = null;

    try {
      setScreenCaptureBusy(true);
      setError("");
      setScreenshotCopied(false);

      stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      });

      const video = document.createElement("video");
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;

      await video.play();
      await new Promise((resolve) => {
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          resolve();
          return;
        }
        video.onloadedmetadata = () => resolve();
      });
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const context = canvas.getContext("2d");
      if (!context) throw new Error("Canvas non disponibile.");
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png", 1),
      );
      if (!blob) throw new Error("Impossibile creare lo screenshot.");

      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const file = new File([blob], `ticket-screen-${stamp}.png`, {
        type: "image/png",
        lastModified: Date.now(),
      });
      const attachment = await readAttachment(file);

      if (
        getAttachmentTotalSize([...form.attachments, attachment]) >
        MAX_ATTACHMENT_SIZE
      ) {
        throw new Error("Con questo screenshot gli allegati superano 8 MB.");
      }

      setForm((current) => ({
        ...current,
        attachments: [...current.attachments, attachment],
      }));

      setScreenshotDraft({
        blob,
        file,
        attachment,
      });

      setNotice("Screenshot aggiunto agli allegati.");
    } catch (captureError) {
      if (captureError?.name !== "NotAllowedError") {
        console.error("[SCREENSHOT] Cattura schermo fallita:", captureError);
        setError(captureError?.message || "Impossibile catturare lo schermo.");
      }
    } finally {
      stream?.getTracks?.().forEach((track) => track.stop());
      setScreenCaptureBusy(false);
    }
  };

  // Annulla e rimuove lo screenshot temporaneo.
  const cancelCapturedScreenshot = () => {
    const screenshotFile = screenshotDraft?.file;
    if (screenshotFile) {
      setForm((current) => ({
        ...current,
        attachments: current.attachments.filter(
          (attachment) => attachment?.file !== screenshotFile,
        ),
      }));
    }

    setScreenshotDraft(null);
    setScreenshotCopied(false);
  };

  // Copia lo screenshot acquisito negli appunti.
  const copyCapturedScreenshot = async () => {
    const blob = screenshotDraft?.blob;
    if (!blob) return;

    try {
      if (!navigator?.clipboard?.write || typeof ClipboardItem === "undefined") {
        throw new Error("Copia immagine non supportata da questo browser.");
      }

      await navigator.clipboard.write([
        new ClipboardItem({
          "image/png": blob,
        }),
      ]);

      setScreenshotCopied(true);
      window.setTimeout(() => setScreenshotCopied(false), 1800);
    } catch (copyError) {
      console.error("[SCREENSHOT] Copia fallita:", copyError);
      setError(copyError?.message || "Impossibile copiare lo screenshot.");
    }
  };

  // Condivide lo screenshot usando la Web Share API quando disponibile.
  const shareCapturedScreenshot = async () => {
    const file = screenshotDraft?.file;
    if (!file || screenshotShareBusy) return;

    try {
      setScreenshotShareBusy(true);

      if (
        !navigator?.share ||
        (navigator.canShare && !navigator.canShare({ files: [file] }))
      ) {
        throw new Error(
          "La condivisione diretta del file non è supportata da questo browser.",
        );
      }

      await navigator.share({
        title: "Screenshot ticket",
        files: [file],
      });
    } catch (shareError) {
      if (shareError?.name !== "AbortError") {
        console.error("[SCREENSHOT] Condivisione fallita:", shareError);
        setError(
          shareError?.message || "Impossibile condividere lo screenshot.",
        );
      }
    } finally {
      setScreenshotShareBusy(false);
    }
  };

  const [form, setForm] = useState({
    targetDepartment: "",
    targetUser: "",
    title: "",
    description: "",
    urgent: false,
    attachments: [],
  });

  const [assignmentForm, setAssignmentForm] = useState({
    department: "",
    user: "",
  });

  const [assignmentBusy, setAssignmentBusy] = useState(false);
  const [closureForm, setClosureForm] = useState({
    response: "",
    attachment: null,
  });
  const [closureBusy, setClosureBusy] = useState(false);
  const closureFileInputRef = useRef(null);

  const [imageBusy, setImageBusy] = useState(false);
  const [ticketDragging, setTicketDragging] = useState(false);
  const fileInputRef = useRef(null);

  const [chatSummaries, setChatSummaries] = useState({});
  const [chatSeenCounts, setChatSeenCounts] = useState({});
  const [chatSeenReady, setChatSeenReady] = useState(false);
  const chatSeenHasBaselineRef = useRef(false);
  const deepLinkOpenedRef = useRef(false);

  const chatSeenStorageKey = `tp-ticket-chat-seen-v1:${currentUser}`;

  // Salva localmente i contatori di messaggi già visualizzati.
  const persistChatSeenCounts = (counts) => {
    try {
      window.localStorage.setItem(chatSeenStorageKey, JSON.stringify(counts));
    } catch (storageError) {
      console.warn("[TICKET CHAT] Impossibile salvare i non letti:", storageError);
    }
  };

  // Segna localmente una chat come letta fino all’ultimo messaggio noto.
  const markChatSeen = (ticketId, count) => {
    const key = String(ticketId);
    const nextCount = Math.max(0, Number(count) || 0);
    setChatSeenCounts((current) => {
      const next = { ...current, [key]: Math.max(Number(current[key]) || 0, nextCount) };
      persistChatSeenCounts(next);
      return next;
    });
  };

  // Calcola quanti messaggi non letti risultano per un ticket.
  const getChatUnreadCount = (ticketId) => {
    const key = String(ticketId);
    const dbUnread = Number(chatSummaries[key]?.unreadCount);
    if (Number.isFinite(dbUnread)) return Math.max(0, dbUnread);

    const total = Number(chatSummaries[key]?.count) || 0;
    const seen = Number(chatSeenCounts[key]) || 0;
    return Math.max(0, total - seen);
  };

  // Restituisce il numero totale di messaggi noto per la chat del ticket.
  const getChatMessageCount = (ticketId) => {
    const key = String(ticketId);
    return Math.max(0, Number(chatSummaries[key]?.count) || 0);
  };

  // Verifica se la conversazione è in attesa di una risposta dell’utente corrente.
  const isWaitingForChatReply = (ticketId) => {
    const key = String(ticketId);
    const summary = chatSummaries[key];
    if (!summary) return false;

    const unread = getChatUnreadCount(ticketId);
    const total = Number(summary.count) || 0;
    const lastUser = normalizeUser(summary.lastUser || "");

    // L'icona "agisci" appartiene sempre a chi HA RICEVUTO
    // l'ultimo messaggio e lo ha già letto.
    // Chi ha scritto l'ultimo messaggio non deve vedere nulla.
    return (
      unread === 0 &&
      total > 0 &&
      lastUser !== "" &&
      lastUser !== "guest" &&
      lastUser !== currentUser
    );
  };

  // Determina se un ticket ricevuto richiede ancora un’azione dell’utente.
  const needsActionOnReceivedTicket = (ticketId) => {
    const key = String(ticketId);
    const summary = chatSummaries[key];
    if (!summary) return false;

    const unread = getChatUnreadCount(ticketId);
    const total = Number(summary.count) || 0;
    const lastUser = normalizeUser(summary.lastUser || "");

    return (
      unread === 0 &&
      total > 0 &&
      lastUser !== "" &&
      lastUser !== "guest" &&
      lastUser !== currentUser
    );
  };

  useEffect(() => {
    if (!currentUser || currentUser === "guest") {
      setChatSeenCounts({});
      setChatSeenReady(false);
      chatSeenHasBaselineRef.current = false;
      return;
    }

    try {
      const raw = window.localStorage.getItem(chatSeenStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        setChatSeenCounts(parsed && typeof parsed === "object" ? parsed : {});
        chatSeenHasBaselineRef.current = true;
      } else {
        setChatSeenCounts({});
        chatSeenHasBaselineRef.current = false;
      }
    } catch {
      setChatSeenCounts({});
      chatSeenHasBaselineRef.current = false;
    }
    setChatSeenReady(true);
  }, [currentUser, chatSeenStorageKey]);

  useEffect(() => {
    if (!chatSeenReady || !currentUser || currentUser === "guest" || loadedTickets.length === 0) {
      if (loadedTickets.length === 0) setChatSummaries({});
      return undefined;
    }

    let cancelled = false;
    const loadChatSummaries = async () => {
      const ids = loadedTickets.map((ticket) => Number(ticket.id)).filter((id) => Number.isFinite(id) && id > 0);
      if (!ids.length) return;

      try {
        const response = await fetch(
          `${CHAT_API_URL}?action=summary&utente=${encodeURIComponent(currentUser)}&ticket_ids=${encodeURIComponent(ids.join(","))}&_=${Date.now()}`,
          { cache: "no-store" },
        );
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled || data?.success !== true) return;

        const next = {};
        (Array.isArray(data.summaries) ? data.summaries : []).forEach((summary) => {
          const key = String(summary.id_ticket);
          next[key] = {
            count: Number(summary.count) || 0,
            unreadCount: Number(summary.unread_count) || 0,
            lastMessageId: Number(summary.last_message_id) || 0,
            lastSeenId: Number(summary.last_seen_id) || 0,
            lastUser: normalizeUser(summary.last_user || ""),
            lastMessageAt: summary.last_message_at || null,
          };
        });
        setChatSummaries(next);

        if (!chatSeenHasBaselineRef.current) {
          const baseline = {};
          Object.entries(next).forEach(([key, summary]) => {
            baseline[key] = Number(summary.count) || 0;
          });
          setChatSeenCounts(baseline);
          persistChatSeenCounts(baseline);
          chatSeenHasBaselineRef.current = true;
        }
      } catch {
        // Il badge non deve mai bloccare il ticketing.
      }
    };

    loadChatSummaries();
    const intervalId = window.setInterval(loadChatSummaries, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [loadedTickets, currentUser, chatSeenReady, chatSeenStorageKey]);

  useEffect(() => {
    if (deepLinkOpenedRef.current || loadedTickets.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const ticketId = params.get("id");
    if (!ticketId) return;
    const exists = loadedTickets.some((ticket) => String(ticket.id) === String(ticketId));
    if (exists) {
      deepLinkOpenedRef.current = true;
      openTicketChat(ticketId);
    }
  }, [loadedTickets]);

  useEffect(() => {
    let cancelled = false;

    const checkPushStatus = async () => {
      const supported =
        typeof window !== "undefined" &&
        "Notification" in window &&
        "serviceWorker" in navigator &&
        "PushManager" in window;

      if (!supported) {
        if (!cancelled) {
          setPushSupported(false);
          setNotificationsEnabled(false);
          setPushStatusChecked(true);
        }
        return;
      }

      try {
        setPushSupported(true);

        if (Notification.permission !== "granted") {
          if (!cancelled) {
            setNotificationsEnabled(false);
            setPushStatusChecked(true);
          }
          return;
        }

        const registration = await navigator.serviceWorker.getRegistration();
        const subscription = registration
          ? await registration.pushManager.getSubscription()
          : null;

        if (!cancelled) {
          setNotificationsEnabled(Boolean(subscription));
          setPushStatusChecked(true);
        }
      } catch (pushStatusError) {
        console.warn("[PUSH] Impossibile verificare lo stato:", pushStatusError);

        if (!cancelled) {
          setNotificationsEnabled(false);
          setPushStatusChecked(true);
        }
      }
    };

    checkPushStatus();

    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  useEffect(() => {
    if (
      (!selectedMyTicket && !selectedChatTicket) ||
      typeof document === "undefined"
    ) {
      return undefined;
    }

    const body = document.body;
    const html = document.documentElement;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlOverflow = html.style.overflow;

    body.style.overflow = "hidden";
    html.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedMyTicketId(null);
        setSelectedChatTicketId(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      body.style.overflow = previousBodyOverflow;
      html.style.overflow = previousHtmlOverflow;
    };
  }, [selectedMyTicket, selectedChatTicket]);

  // Carica dal PHP reparti, utenti e permessi globali.
  const loadUsersConfig = async () => {
    if (!currentUser || currentUser === "guest") return;

    console.log("[TICKET] Carico configurazione reparti per:", currentUser);

    try {
      const data = await apiFetch(
        "GET configurazione utenti/reparti",
        `${API_URL}?action=users&utente=${encodeURIComponent(currentUser)}`,
      );

      if (data.reparti && typeof data.reparti === "object") {
        const normalizedDepartments = Object.fromEntries(
          Object.entries(data.reparti).map(([department, members]) => [
            department,
            Array.isArray(members)
              ? members.map((member) => normalizeUser(member))
              : [],
          ]),
        );

        setDepartments(normalizedDepartments);
      }

      setServerDepartment(data.reparto || null);
      setServerViewAll(Boolean(data.vede_tutti));

      console.log("[TICKET] Reparto corrente:", data.reparto);
      console.log("[TICKET] Visione completa:", Boolean(data.vede_tutti));
    } catch (requestError) {
      console.error(
        "[TICKET] Errore config utenti, uso fallback locale:",
        requestError,
      );
      setDepartments(FALLBACK_DEPARTMENTS);
      setServerDepartment(null);
      setServerViewAll(null);
    }
  };

  // Carica la lista operativa dei ticket ricevuti/visibili dalla GET generale.
  const loadTickets = async ({ silent = false } = {}) => {
    if (!currentUser || currentUser === "guest") return;

    if (!silent) setApiLoading(true);
    setError("");

    console.log("[TICKET] Carico ticket per:", currentUser);

    try {
      const data = await apiFetch(
        "GET lista ticket",
        `${API_URL}?utente=${encodeURIComponent(currentUser)}`,
      );

      const normalized = Array.isArray(data.tickets)
        ? data.tickets.map(normalizeTicket).filter(Boolean)
        : [];

      console.log("[TICKET] Ticket raw ricevuti:", data.tickets || []);
      console.log("[TICKET] Ticket normalizzati:", normalized);

      setTickets(normalized);

      if (data.reparto !== undefined) {
        setServerDepartment(data.reparto || null);
      }

      if (data.vede_tutti !== undefined) {
        setServerViewAll(Boolean(data.vede_tutti));
      }
    } catch (requestError) {
      console.error("[TICKET] GET ticket fallita:", requestError);
      setError(requestError.message || "Errore caricamento ticket.");
    } finally {
      if (!silent) setApiLoading(false);
    }
  };

  // Carica dal server una pagina da massimo 50 ticket creati dall’utente.
  const loadMyTickets = async ({
    page = minePage,
    sort = mineSort,
    status = mineStatusFilter,
    urgent = mineUrgentFilter,
    search = mineSearchDebounced,
    silent = false,
  } = {}) => {
    if (!currentUser || currentUser === "guest") return;

    const requestedPage = Math.max(1, Number(page) || 1);

    if (!silent) setMineLoading(true);

    try {
      const params = new URLSearchParams({
        action: "my_tickets",
        utente: currentUser,
        page: String(requestedPage),
        per_page: "50",
        sort,
        status,
        urgent,
      });

      if (search.trim()) {
        params.set("q", search.trim());
      }

      const data = await apiFetch(
        "GET miei ticket paginati",
        `${API_URL}?${params.toString()}`,
      );

      const normalized = Array.isArray(data.tickets)
        ? data.tickets.map(normalizeTicket).filter(Boolean)
        : [];

      const pagination = data.pagination || {};
      const stats = data.stats || {};

      setMyTicketsPage(normalized);

      const serverPage = Math.max(1, Number(pagination.page) || requestedPage);
      const totalPages = Math.max(1, Number(pagination.total_pages) || 1);

      setMinePagination({
        page: serverPage,
        perPage: Math.max(1, Number(pagination.per_page) || 50),
        total: Math.max(0, Number(pagination.total) || 0),
        totalPages,
        hasPrevious: Boolean(pagination.has_previous),
        hasNext: Boolean(pagination.has_next),
      });

      setMineStats({
        total: Math.max(0, Number(stats.totale) || 0),
        open: Math.max(0, Number(stats.aperti) || 0),
        assigned: Math.max(0, Number(stats.assegnati) || 0),
        working: Math.max(0, Number(stats.in_lavorazione) || 0),
        closed: Math.max(0, Number(stats.chiusi) || 0),
      });

      // Il server può correggere una pagina fuori range.
      if (serverPage !== minePage) {
        setMinePage(serverPage);
      }
    } catch (requestError) {
      console.error("[TICKET] GET miei ticket paginati fallita:", requestError);
      setError(
        requestError.message || "Errore caricamento dei miei ticket.",
      );
    } finally {
      if (!silent) setMineLoading(false);
    }
  };



  // Carica dal PHP una singola pagina dell'archivio Ticket chiusi.
  // La paginazione, la ricerca e i filtri data vengono eseguiti dal database:
  // il browser riceve al massimo 50 righe per richiesta.
  const loadClosedTickets = async ({
    page = closedPage,
    scope = closedFilter,
    search = closedSearchDebounced,
    dateFrom = closedDateFrom,
    dateTo = closedDateTo,
    sort = closedSort,
    silent = false,
  } = {}) => {
    if (!currentUser || currentUser === "guest") return;

    const requestedPage = Math.max(1, Number(page) || 1);

    if (!silent) setClosedLoading(true);

    try {
      // Costruisce i parametri che il nuovo blocco GET closed_tickets del PHP legge.
      const params = new URLSearchParams({
        action: "closed_tickets",
        utente: currentUser,
        page: String(requestedPage),
        per_page: "50",
        scope,
        sort,
      });

      // Invia q soltanto quando esiste una ricerca reale.
      if (search.trim()) {
        params.set("q", search.trim());
      }

      // Invia le date soltanto se selezionate dall'utente.
      if (dateFrom) {
        params.set("date_from", dateFrom);
      }

      if (dateTo) {
        params.set("date_to", dateTo);
      }

      const data = await apiFetch(
        "GET ticket chiusi paginati",
        `${API_URL}?${params.toString()}`,
      );

      // Converte le righe SQL nella stessa struttura ticket usata dal resto del JSX.
      const normalized = Array.isArray(data.tickets)
        ? data.tickets.map(normalizeTicket).filter(Boolean)
        : [];

      const pagination = data.pagination || {};
      const stats = data.stats || {};

      // Salva soltanto la pagina ricevuta, non l'intero archivio.
      setClosedTicketsPage(normalized);

      const serverPage = Math.max(1, Number(pagination.page) || requestedPage);
      const totalPages = Math.max(1, Number(pagination.total_pages) || 1);

      // Memorizza i dati necessari a Precedente / pagine / Successiva.
      setClosedPagination({
        page: serverPage,
        perPage: Math.max(1, Number(pagination.per_page) || 50),
        total: Math.max(0, Number(pagination.total) || 0),
        totalPages,
        hasPrevious: Boolean(pagination.has_previous),
        hasNext: Boolean(pagination.has_next),
      });

      // Aggiorna i conteggi dei quattro tab con i valori calcolati dal database.
      setClosedStats({
        mine: Math.max(0, Number(stats.mine) || 0),
        closedByMe: Math.max(0, Number(stats.closed_by_me) || 0),
        department: Math.max(0, Number(stats.department) || 0),
        all: Math.max(0, Number(stats.all) || 0),
      });

      // Come my_tickets, il PHP può correggere una pagina oltre il limite.
      if (serverPage !== closedPage) {
        setClosedPage(serverPage);
      }
    } catch (requestError) {
      console.error("[TICKET] GET ticket chiusi paginati fallita:", requestError);
      setError(
        requestError.message || "Errore caricamento dei ticket chiusi.",
      );
    } finally {
      if (!silent) setClosedLoading(false);
    }
  };

  useEffect(() => {
    if (!user || currentUser === "guest") return;

    console.log("===============================================");
    console.log("[TICKET] Avvio pagina Ticket");
    console.log("[TICKET] Auth user:", user);
    console.log("[TICKET] Username normalizzato:", currentUser);
    console.log("[TICKET] API_URL:", API_URL);
    console.log("===============================================");

    loadUsersConfig();
    loadTickets();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, user]);

  // Debounce ricerca: evita una GET a ogni singolo tasto premuto.
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setMineSearchDebounced(mineSearch.trim());
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [mineSearch]);


  // Debounce della ricerca nell'archivio chiusi.
  // Quando cambia il testo ricominciamo sempre dalla prima pagina.
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setClosedSearchDebounced(closedSearch.trim());
      setClosedPage(1);
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [closedSearch]);

  // Richiede al server la pagina corrente dei ticket chiusi.
  // activeTab è volutamente una dipendenza: entrando nella scheda "Ticket chiusi"
  // viene eseguito immediatamente un refresh dell'archivio.
  useEffect(() => {
    if (!user || currentUser === "guest") return;

    loadClosedTickets({
      page: closedPage,
      scope: closedFilter,
      search: closedSearchDebounced,
      dateFrom: closedDateFrom,
      dateTo: closedDateTo,
      sort: closedSort,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentUser,
    user,
    activeTab,
    closedPage,
    closedFilter,
    closedSearchDebounced,
    closedDateFrom,
    closedDateTo,
    closedSort,
  ]);

  // Quando la scheda dei chiusi è aperta aggiorna silenziosamente la pagina
  // corrente ogni 10 secondi, senza scaricare tutto lo storico.
  useEffect(() => {
    if (!user || currentUser === "guest" || activeTab !== "closed") {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      loadClosedTickets({
        page: closedPage,
        scope: closedFilter,
        search: closedSearchDebounced,
        dateFrom: closedDateFrom,
        dateTo: closedDateTo,
        sort: closedSort,
        silent: true,
      });
    }, 10000);

    return () => window.clearInterval(intervalId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentUser,
    user,
    activeTab,
    closedPage,
    closedFilter,
    closedSearchDebounced,
    closedDateFrom,
    closedDateTo,
    closedSort,
  ]);

  // La cronologia personale viene richiesta al server SOLO 50 righe alla volta.
  useEffect(() => {
    if (!user || currentUser === "guest") return;

    loadMyTickets({
      page: minePage,
      sort: mineSort,
      status: mineStatusFilter,
      urgent: mineUrgentFilter,
      search: mineSearchDebounced,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentUser,
    user,
    minePage,
    mineSort,
    mineStatusFilter,
    mineUrgentFilter,
    mineSearchDebounced,
  ]);

  // Se siamo nella scheda personale aggiorniamo soltanto la pagina corrente,
  // senza scaricare tutta la cronologia.
  useEffect(() => {
    if (
      !user ||
      currentUser === "guest" ||
      activeTab !== "mine"
    ) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      loadMyTickets({
        page: minePage,
        sort: mineSort,
        status: mineStatusFilter,
        urgent: mineUrgentFilter,
        search: mineSearchDebounced,
        silent: true,
      });
    }, 10000);

    return () => window.clearInterval(intervalId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentUser,
    user,
    activeTab,
    minePage,
    mineSort,
    mineStatusFilter,
    mineUrgentFilter,
    mineSearchDebounced,
  ]);

  // Aggiornamento automatico: riceve i nuovi ticket senza premere F5.
  useEffect(() => {
    if (!user || currentUser === "guest") return undefined;

    const intervalId = window.setInterval(() => {
      loadTickets({ silent: true });
    }, 10000);

    return () => window.clearInterval(intervalId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, user]);

  useEffect(() => {
    if (!notice) return;

    const timeout = window.setTimeout(() => setNotice(""), 3200);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  useEffect(() => {
    if (!dangerNotice) return;

    const timeout = window.setTimeout(() => setDangerNotice(""), 4200);
    return () => window.clearTimeout(timeout);
  }, [dangerNotice]);

  const activeTickets = useMemo(
    () => tickets.filter((ticket) => ticket.status !== STATUS.CLOSED),
    [tickets],
  );


  // =========================================================
  // TICKET RICEVUTI - 3 GRUPPI
  //
  // Il PHP aggiunge vista_miei / vista_reparto / vista_tutto_reparto.
  // Manteniamo comunque un fallback locale per compatibilità nel caso
  // il frontend venga aperto per qualche secondo contro una vecchia API.
  // =========================================================

const assignedToMe = useMemo(
  () =>
    sortTicketsByDate(
      activeTickets.filter((ticket) => {

        /*
         * Se il PHP ci ha già mandato la classificazione,
         * usiamo direttamente quella.
         */
        if (ticket.hasViewFlags) {
          return ticket.viewMine;
        }

        /*
         * FALLBACK:
         *
         * SOPRA se:
         *
         * - assegnato a me
         *
         * oppure
         *
         * - destinato a me e ancora non assegnato
         */
        return (
          ticket.assignedTo === currentUser ||
          (
            ticket.targetUser === currentUser &&
            !ticket.assignedTo
          )
        );
      }),
      inboxSort,
    ),
  [
    activeTickets,
    currentUser,
    inboxSort,
  ],
);


const departmentQueueTickets = useMemo(
  () =>
    sortTicketsByDate(
      activeTickets.filter((ticket) => {

        /*
         * Normalmente arriva già deciso dal PHP.
         */
        if (ticket.hasViewFlags) {
          return ticket.viewDepartment;
        }


        /*
         * Controlliamo prima che appartenga
         * al reparto corrente.
         */
        const belongsToDepartment =
          isDirector ||
          ticket.targetDepartment === currentDepartment;


        /*
         * Verifichiamo se sarebbe un ticket personale.
         */
        const mine =
          ticket.assignedTo === currentUser ||
          (
            ticket.targetUser === currentUser &&
            !ticket.assignedTo
          );


        /*
         * REPARTO:
         *
         * 1.
         * nessun destinatario
         * nessun assegnatario
         *
         * oppure
         *
         * 2.
         * destinatario = currentUser
         * ma assegnato ad un altro utente.
         */
        return (
          belongsToDepartment &&
          !mine &&
          (
            (
              !ticket.targetUser &&
              !ticket.assignedTo
            )
            ||
            (
              ticket.targetUser === currentUser &&
              Boolean(ticket.assignedTo) &&
              ticket.assignedTo !== currentUser
            )
          )
        );
      }),
      inboxSort,
    ),
  [
    activeTickets,
    currentUser,
    currentDepartment,
    isDirector,
    inboxSort,
  ],
);


const allDepartmentTickets = useMemo(
  () =>
    sortTicketsByDate(
      activeTickets.filter((ticket) => {

        /*
         * Se abbiamo il flag PHP utilizziamo quello.
         */
        if (ticket.hasViewFlags) {
          return ticket.viewAllDepartment;
        }


        const belongsToDepartment =
          isDirector ||
          ticket.targetDepartment === currentDepartment;


        /*
         * Stessa regola della sezione SOPRA.
         */
        const mine =
          ticket.assignedTo === currentUser ||
          (
            ticket.targetUser === currentUser &&
            !ticket.assignedTo
          );


        /*
         * Stessa regola della sezione REPARTO.
         */
        const departmentQueue =
          belongsToDepartment &&
          !mine &&
          (
            (
              !ticket.targetUser &&
              !ticket.assignedTo
            )
            ||
            (
              ticket.targetUser === currentUser &&
              Boolean(ticket.assignedTo) &&
              ticket.assignedTo !== currentUser
            )
          );


        /*
         * Tutto ciò che appartiene al reparto ma
         * non è personale e non è nella coda reparto
         * va in "Tutto il reparto".
         */
        return (
          belongsToDepartment &&
          !mine &&
          !departmentQueue
        );
      }),
      inboxSort,
    ),
  [
    activeTickets,
    currentUser,
    currentDepartment,
    isDirector,
    inboxSort,
  ],
);


  const visibleDepartmentTickets =
    departmentInboxTab === "department"
      ? departmentQueueTickets
      : allDepartmentTickets;

  // "Stato dei miei ticket" usa direttamente la pagina ricevuta dal PHP.
  // Contiene insieme aperti, assegnati, in lavorazione e chiusi.
  const myCreatedTickets = myTicketsPage;

  const hasMineFilters =
    mineStatusFilter !== "all" ||
    mineUrgentFilter !== "all" ||
    mineSearch.trim() !== "";

  const minePageItems = useMemo(() => {
    const totalPages = Math.max(1, minePagination.totalPages);
    const currentPage = Math.min(
      Math.max(1, minePagination.page || minePage),
      totalPages,
    );

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => ({
        type: "page",
        value: index + 1,
      }));
    }

    const wanted = new Set([
      1,
      totalPages,
      currentPage - 1,
      currentPage,
      currentPage + 1,
    ]);

    const pages = Array.from(wanted)
      .filter((page) => page >= 1 && page <= totalPages)
      .sort((a, b) => a - b);

    const items = [];
    pages.forEach((page, index) => {
      if (index > 0 && page - pages[index - 1] > 1) {
        items.push({
          type: "ellipsis",
          value: `ellipsis-${pages[index - 1]}-${page}`,
        });
      }
      items.push({ type: "page", value: page });
    });

    return items;
  }, [minePagination.page, minePagination.totalPages, minePage]);

  // La tabella visualizza direttamente i massimo 50 ticket ricevuti dal PHP.
  // Non applichiamo slice/filter client-side all'archivio completo.
  const visibleClosedTickets = closedTicketsPage;

  // Indica se ricerca o date stanno restringendo i risultati mostrati.
  const hasClosedFilters =
    closedSearch.trim() !== "" || closedDateFrom !== "" || closedDateTo !== "";

  // Costruisce i numeri di pagina dell'archivio chiusi.
  // Con molte pagine mostra prima/ultima e le pagine vicine a quella corrente.
  const closedPageItems = useMemo(() => {
    const totalPages = Math.max(1, closedPagination.totalPages);
    const currentPage = Math.min(
      Math.max(1, closedPagination.page || closedPage),
      totalPages,
    );

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => ({
        type: "page",
        value: index + 1,
      }));
    }

    const wanted = new Set([
      1,
      totalPages,
      currentPage - 1,
      currentPage,
      currentPage + 1,
    ]);

    const pages = Array.from(wanted)
      .filter((page) => page >= 1 && page <= totalPages)
      .sort((a, b) => a - b);

    const items = [];

    pages.forEach((page, index) => {
      if (index > 0 && page - pages[index - 1] > 1) {
        items.push({
          type: "ellipsis",
          value: `closed-ellipsis-${pages[index - 1]}-${page}`,
        });
      }

      items.push({ type: "page", value: page });
    });

    return items;
  }, [closedPagination.page, closedPagination.totalPages, closedPage]);

  const myOpenCount =
    mineStats.open + mineStats.assigned + mineStats.working;
  const receivedOpenCount = assignedToMe.length;

  const departmentMembers = form.targetDepartment
    ? departments[form.targetDepartment] || []
    : [];

  const assignmentMembers = assignmentForm.department
    ? departments[assignmentForm.department] || []
    : [];

  // Azzera tutti i campi del form di creazione ticket.
  const resetForm = () => {
    setForm({
      targetDepartment: "",
      targetUser: "",
      title: "",
      description: "",
      urgent: false,
      attachments: [],
    });

    setTicketDragging(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Aggiorna il reparto destinatario e resetta l’utente selezionato.
  const handleDepartmentChange = (event) => {
    const targetDepartment = event.target.value;

    console.log("[TICKET] Reparto selezionato:", targetDepartment);

    setForm((prev) => ({
      ...prev,
      targetDepartment,
      targetUser: "",
    }));
  };

  // Aggiunge e valida nuovi allegati al ticket in creazione.
  const addTicketFiles = async (files) => {
    const incoming = Array.from(files || []).filter(Boolean);
    if (!incoming.length) return;

    setError("");

    try {
      setImageBusy(true);
      const next = await prepareAttachments(incoming, form.attachments);
      setForm((prev) => ({ ...prev, attachments: next }));
    } catch (attachmentError) {
      console.error("[TICKET] Errore allegati:", attachmentError);
      setError(attachmentError.message || "Errore caricamento allegati.");
    } finally {
      setImageBusy(false);
    }
  };

  // Gestisce i file scelti tramite input allegati.
  const handleImageChange = async (event) => {
    await addTicketFiles(event.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Gestisce il drag & drop di allegati nel form ticket.
  const handleTicketDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setTicketDragging(false);
    await addTicketFiles(event.dataTransfer.files);
  };

  // Gestisce immagini incollate dagli appunti nel form ticket.
  const handleTicketPaste = async (event) => {
    const files = getClipboardImageFiles(event.clipboardData);
    if (!files.length) return;

    event.preventDefault();
    await addTicketFiles(files);
  };

  // Rimuove un allegato dalla bozza del nuovo ticket.
  const removeTicketAttachment = (index) => {
    setForm((prev) => ({
      ...prev,
      attachments: prev.attachments.filter(
        (_, attachmentIndex) => attachmentIndex !== index,
      ),
    }));
    setError("");
  };

  // Valida il form e invia al PHP la creazione del nuovo ticket.
  const submitTicket = async (event) => {
    event.preventDefault();
    setError("");

    if (!form.targetDepartment) {
      setError("Seleziona il reparto destinatario.");
      return;
    }

    const title = form.title.trim();
    const description = form.description.trim();

    if (!title) {
      setError("Inserisci un titolo per il ticket.");
      return;
    }

    if (!description) {
      setError("Inserisci il testo del ticket.");
      return;
    }

    if (getAttachmentTotalSize(form.attachments) > MAX_ATTACHMENT_SIZE) {
      setError("Gli allegati superano 8 MB complessivi.");
      return;
    }

    const payload = new FormData();
    payload.append("utente_creazione", currentUser);
    payload.append("reparto_destinatario", form.targetDepartment);
    payload.append(
      "utente_destinatario",
      form.targetUser ? normalizeUser(form.targetUser) : "",
    );
    payload.append("titolo", title);
    payload.append("descrizione", description);
    payload.append("urgente", form.urgent ? "1" : "0");

    form.attachments.forEach((attachment) => {
      if (attachment.file) {
        payload.append("allegato[]", attachment.file);
      }
    });

    console.log("[TICKET] Invio nuovo ticket:", {
      utente_creazione: currentUser,
      reparto_creazione: currentDepartment,
      reparto_destinatario: form.targetDepartment,
      utente_destinatario: form.targetUser
        ? normalizeUser(form.targetUser)
        : "",
      titolo: title,
      descrizione: description,
      urgente: form.urgent,
      allegati: form.attachments.map((attachment) => ({
        name: attachment.name,
        size: attachment.size,
        type: attachment.type,
      })),
    });

    try {
      setApiLoading(true);

      const data = await apiFetch("POST crea ticket", API_URL, {
        method: "POST",
        body: payload,
      });

      const createdTicket = normalizeTicket(data.ticket);
      console.log("[TICKET] POST SUCCESS");
      console.log("[TICKET] Ticket creato:", createdTicket || data.ticket);

      resetForm();
      setNotice(
        `Ticket ${createdTicket?.code || data.ticket?.codice_ticket || ""} creato correttamente.`,
      );
      setMinePage(1);
      setActiveTab("mine");

      await Promise.all([
        loadTickets({ silent: true }),
        loadMyTickets({ page: 1, sort: mineSort, silent: true }),
        loadClosedTickets({
          page: closedPage,
          scope: closedFilter,
          search: closedSearchDebounced,
          dateFrom: closedDateFrom,
          dateTo: closedDateTo,
          sort: closedSort,
          silent: true,
        }),
      ]);
    } catch (requestError) {
      console.error("[TICKET] Creazione fallita:", requestError);
      setError(
        requestError.message || "Errore durante la creazione del ticket.",
      );
    } finally {
      setApiLoading(false);
    }
  };

  // Invia al PHP un cambio di stato per un ticket esistente.
  const updateTicketStatus = async (ticketId, nextStatus) => {
    if (![STATUS.OPEN, STATUS.ASSIGNED, STATUS.WORKING].includes(nextStatus)) return;

    const currentTicket = loadedTickets.find(
      (ticket) => String(ticket.id) === String(ticketId),
    );

    console.log("[TICKET] Cambio stato richiesto:", {
      id_ticket: ticketId,
      codice_ticket: currentTicket?.code,
      da: currentTicket?.status,
      a: nextStatus,
      utente: currentUser,
    });

    try {
      setApiLoading(true);
      setError("");

      const data = await apiFetch("PUT cambia stato", API_URL, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_ticket: ticketId,
          utente: currentUser,
          stato: nextStatus,
        }),
      });

      console.log("[TICKET] PUT completata:", data);
      setNotice(`Stato aggiornato: ${STATUS_LABELS[nextStatus]}.`);

      await Promise.all([
        loadTickets({ silent: true }),
        loadMyTickets({ page: minePage, sort: mineSort, silent: true }),
        loadClosedTickets({
          page: closedPage,
          scope: closedFilter,
          search: closedSearchDebounced,
          dateFrom: closedDateFrom,
          dateTo: closedDateTo,
          sort: closedSort,
          silent: true,
        }),
      ]);
    } catch (requestError) {
      console.error("[TICKET] Cambio stato fallito:", requestError);
      setError(requestError.message || "Errore aggiornamento stato.");
    } finally {
      setApiLoading(false);
    }
  };

  // Prepara l’eventuale allegato da associare alla chiusura.
  const handleClosureAttachmentChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setError("");
      const attachment = await readAttachment(file);
      setClosureForm((current) => ({ ...current, attachment }));
    } catch (attachmentError) {
      setError(attachmentError.message || "Errore caricamento allegato di chiusura.");
      if (closureFileInputRef.current) closureFileInputRef.current.value = "";
    }
  };

  // Chiude un ticket con risposta finale ed eventuale allegato.
  const closeTicket = async () => {
    if (!selectedMyTicket) return;

    const response = closureForm.response.trim();
    if (!response) {
      setError("Inserisci la risposta di chiusura.");
      return;
    }

    try {
      setClosureBusy(true);
      setError("");

      const payload = new FormData();
      payload.append("action", "close");
      payload.append("id_ticket", String(selectedMyTicket.id));
      payload.append("utente", currentUser);
      payload.append("risposta_chiusura", response);

      if (closureForm.attachment?.file) {
        payload.append(
          "allegato_chiusura",
          closureForm.attachment.file,
          closureForm.attachment.name || closureForm.attachment.file.name,
        );
      }

      const data = await apiFetch("POST chiudi ticket", API_URL, {
        method: "POST",
        body: payload,
      });

      const updated = normalizeTicket(data.ticket);
      setDangerNotice("");
      setNotice(`Ticket ${updated?.code || selectedMyTicket.code} chiuso correttamente.`);
      setClosureForm({ response: "", attachment: null });
      if (closureFileInputRef.current) closureFileInputRef.current.value = "";
      await Promise.all([
        loadTickets({ silent: true }),
        loadMyTickets({ page: minePage, sort: mineSort, silent: true }),
        loadClosedTickets({
          page: closedPage,
          scope: closedFilter,
          search: closedSearchDebounced,
          dateFrom: closedDateFrom,
          dateTo: closedDateTo,
          sort: closedSort,
          silent: true,
        }),
      ]);
    } catch (requestError) {
      console.error("[TICKET] Chiusura fallita:", requestError);
      setError(requestError.message || "Errore durante la chiusura del ticket.");
    } finally {
      setClosureBusy(false);
    }
  };

  // Esegue la chiusura rapida per mancanza di informazioni.
  const quickCloseMissingInfo = async () => {
    if (!quickCloseTicket) return;

    try {
      setQuickCloseBusy(true);
      setError("");

      const payload = new FormData();
      payload.append("action", "close_missing_info");
      payload.append("id_ticket", String(quickCloseTicket.id));
      payload.append("utente", currentUser);

      const data = await apiFetch("POST chiusura rapida ticket", API_URL, {
        method: "POST",
        body: payload,
      });

      const updated = normalizeTicket(data.ticket);
      setNotice("");
      setDangerNotice(
        `Ticket ${updated?.code || quickCloseTicket.code} chiuso per mancanza di informazioni.`,
      );
      setQuickCloseTicketId(null);
      await Promise.all([
        loadTickets({ silent: true }),
        loadMyTickets({ page: minePage, sort: mineSort, silent: true }),
        loadClosedTickets({
          page: closedPage,
          scope: closedFilter,
          search: closedSearchDebounced,
          dateFrom: closedDateFrom,
          dateTo: closedDateTo,
          sort: closedSort,
          silent: true,
        }),
      ]);
    } catch (requestError) {
      console.error("[TICKET] Chiusura rapida fallita:", requestError);
      setError(
        requestError.message ||
          "Errore durante la chiusura per mancanza di informazioni.",
      );
    } finally {
      setQuickCloseBusy(false);
    }
  };

  // Assegna o riassegna il ticket a un utente tramite endpoint PUT.
  const assignTicket = async () => {
    if (!selectedMyTicket) return;

    if (!assignmentForm.department) {
      setError("Seleziona il reparto a cui assegnare il ticket.");
      return;
    }

    if (!assignmentForm.user) {
      setError("Seleziona l'utente a cui assegnare il ticket.");
      return;
    }

    try {
      setAssignmentBusy(true);
      setError("");

      const data = await apiFetch("PUT assegna ticket", API_URL, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "assign",
          id_ticket: selectedMyTicket.id,
          utente: currentUser,
          reparto_assegnazione: assignmentForm.department,
          assegnato_a: assignmentForm.user,
        }),
      });

      const updated = normalizeTicket(data.ticket);
      setNotice(
        `Ticket ${updated?.code || selectedMyTicket.code} assegnato a ${displayUser(assignmentForm.user)}.`,
      );
      setAssignmentForm({
        department:
          resolveDepartmentKey(assignmentForm.department) ||
          getTicketCurrentDepartment(updated),
        user: "",
      });
      await Promise.all([
        loadTickets({ silent: true }),
        loadMyTickets({ page: minePage, sort: mineSort, silent: true }),
        loadClosedTickets({
          page: closedPage,
          scope: closedFilter,
          search: closedSearchDebounced,
          dateFrom: closedDateFrom,
          dateTo: closedDateTo,
          sort: closedSort,
          silent: true,
        }),
      ]);
    } catch (requestError) {
      console.error("[TICKET] Assegnazione fallita:", requestError);
      setError(requestError.message || "Errore durante l'assegnazione del ticket.");
    } finally {
      setAssignmentBusy(false);
    }
  };

  const selectedResponsibleUser =
    selectedMyTicket?.assignedTo || selectedMyTicket?.targetUser || "";

  const canManageSelectedTicket =
    Boolean(selectedMyTicket) &&
    !isDirector &&
    (
      selectedResponsibleUser === currentUser ||
      (
        !selectedMyTicket?.assignedTo &&
        !selectedMyTicket?.targetUser &&
        selectedMyTicket?.targetDepartment === currentDepartment
      )
    );

  const canAssignSelectedTicket =
    Boolean(selectedMyTicket) &&
    !isDirector &&
    [STATUS.OPEN, STATUS.ASSIGNED].includes(selectedMyTicket?.status) &&
    (
      canManageSelectedTicket ||
      selectedMyTicket?.targetDepartment === currentDepartment
    );

  const canCloseSelectedTicket =
    Boolean(selectedMyTicket) &&
    !isDirector &&
    selectedMyTicket?.status !== STATUS.CLOSED &&
    canManageSelectedTicket;

  const tabs = [
    {
      id: "inbox",
      label: "Ticket ricevuti",
      icon: Inbox,
      count: receivedOpenCount,
    },
    {
      id: "new",
      label: "Nuovo ticket",
      icon: Plus,
    },
    {
      id: "mine",
      label: "Stato dei miei ticket",
      icon: Ticket,
      count: myOpenCount,
    },
    {
      id: "closed",
      label: "Ticket chiusi",
      icon: CheckCircle2,
      // Mostra il totale storico dei ticket che riguardano l'utente, non soltanto
      // il numero di righe presenti nella pagina corrente.
      count: closedStats.mine,
    },
  ];

  return (
    <div
      className="tk-shell w-100"
      style={{
        overflowY: "scroll",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      <NavbarUtility />
      <div className="tk-ambient tk-ambient-one" />
      <div className="tk-ambient tk-ambient-two" />

      <div className="tk-page">
        <header className="tk-hero">
          <div>
            <div className="tk-eyebrow">
              <span />
              Assistenza aziendale
            </div>

            <h1>
              Centro <strong>Ticket</strong>
            </h1>

            <p>
              Invia richieste a un intero reparto oppure assegnale a una persona
              e segui lo stato della lavorazione.
            </p>
          </div>

          <div className="tk-hero-actions">
            <div className="tk-user-glass">
            {pushStatusChecked && pushSupported && !notificationsEnabled && (
              <button
                type="button"
                onClick={enableNotifications}
                style={{
                  border: "1px solid rgba(235,181,27,.35)",
                  background: "rgba(235,181,27,.12)",
                  color: "#ebb51b",
                  borderRadius: "10px",
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                🔔 Attiva notifiche push
              </button>
            )}
            <div className="tk-user-avatar">
              {displayUser(currentUser).charAt(0) || "U"}
            </div>

            <div>
              <span>Accesso come</span>
              <strong>{displayUser(currentUser)}</strong>
              <small>
                {isDirector
                  ? "Direzione · visione completa"
                  : currentDepartment}
              </small>
            </div>
          </div>
          </div>
        </header>

        {currentDepartment === "NON ASSEGNATO" && !isDirector && (
          <div className="tk-warning-banner">
            <AlertTriangle size={17} />
            <span>
              L'utente <b>{displayUser(currentUser)}</b> non è presente nella
              mappa reparti di Ticket.jsx. Può creare ticket, ma la vista
              reparto resterà vuota finché non gli assegni un reparto.
            </span>
          </div>
        )}

        <nav className="tk-tabs" aria-label="Sezioni ticket">
          {tabs.map((tab) => {
            const Icon = tab.icon;

            return (
              <button
                type="button"
                key={tab.id}
                className={activeTab === tab.id ? "tk-tab is-active" : "tk-tab"}
                onClick={() => {
                  setActiveTab(tab.id);
                  setError("");
                }}
              >
                <Icon size={17} />
                <span>{tab.label}</span>

                {typeof tab.count === "number" && tab.count > 0 && (
                  <b>{tab.count}</b>
                )}
              </button>
            );
          })}
        </nav>

        {notice && (
          <div className="tk-notice" role="status">
            <CheckCircle2 size={17} />
            {notice}
          </div>
        )}

        {dangerNotice && (
          <div className="tk-danger-notice" role="status">
            <XCircle size={17} />
            {dangerNotice}
          </div>
        )}

        {error && (
          <div className="tk-error" role="alert">
            <AlertTriangle size={17} />
            {error}
          </div>
        )}

        {apiLoading && (
          <div className="tk-notice" role="status">
            <LoaderCircle size={17} />
            Sto inviando il ticket, attendere prego...
          </div>
        )}

        {activeTab === "inbox" && (
          <div className="tk-tab-panel">
            <div className="tk-list-toolbar">
              <div>
                <span className="tk-section-kicker">Ordine visualizzazione</span>
                <strong>Ordina i ticket per data</strong>
              </div>

              <label className="tk-sort-control">
                <span>Data</span>
                <select
                  value={inboxSort}
                  onChange={(event) => setInboxSort(event.target.value)}
                >
                  <option value="desc">Dal più recente al più vecchio</option>
                  <option value="asc">Dal più vecchio al più recente</option>
                </select>
              </label>
            </div>

            <section className="tk-section">
              <div className="tk-section-heading">
                <div>
                  <span className="tk-section-kicker">Personali</span>
                  <h2>I miei ticket ricevuti</h2>
                  <p>Ticket con assegnato a te oppure, se non ancora assegnati, destinati direttamente a te.</p>
                </div>

                <div className="tk-received-heading-actions">
                  <div className="tk-view-switch" aria-label="Vista ticket ricevuti">
                    <button
                      type="button"
                      className={inboxView === "grid" ? "is-active" : ""}
                      onClick={() => setInboxView("grid")}
                      title="Vista a griglia"
                      aria-label="Vista a griglia"
                    >
                      <LayoutGrid size={15} />
                    </button>
                    <button
                      type="button"
                      className={inboxView === "list" ? "is-active" : ""}
                      onClick={() => setInboxView("list")}
                      title="Vista a lista"
                      aria-label="Vista a lista"
                    >
                      <List size={16} />
                    </button>
                  </div>

                  <div className="tk-counter">{assignedToMe.length}</div>
                </div>
              </div>

              {assignedToMe.length > 0 ? (
                inboxView === "grid" ? (
                  <div className="tk-received-scroll-area tk-received-scroll-area-grid">
                    <div className="tk-ticket-grid">
                      {assignedToMe.map((ticket) => (
                        <TicketCard
                          key={ticket.id}
                          ticket={ticket}
                          currentUser={currentUser}
                          currentDepartment={currentDepartment}
                          isDirector={isDirector}
                          allowStatusChange
                          onStatusChange={updateTicketStatus}
                          onEditAssignment={() => openTicketAssignment(ticket)}
                          onCloseStatusRequest={() =>
                            openTicketChat(ticket.id, { startClosing: true })
                          }
                          onQuickCloseRequest={() => setQuickCloseTicketId(ticket.id)}
                          onOpen={() => openTicketChat(ticket.id)}
                          unreadCount={getChatUnreadCount(ticket.id)}
                          actionRequired={needsActionOnReceivedTicket(ticket.id)}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="tk-received-scroll-area tk-received-scroll-area-list">
                    <div className="tk-mine-table-wrap tk-received-list-wrap">
                      <table className="tk-mine-table tk-received-list-table">
                      <thead>
                        <tr>
                          <th>Ticket</th>
                          <th>Data creazione</th>
                          <th>Titolo</th>
                          <th>Creato da</th>
                          <th>Stato</th>
                          <th>Chat</th>
                          <th>Allegati</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignedToMe.map((ticket) => (
                          <tr
                            key={ticket.id}
                            className={`tk-mine-row ${ticket.urgent ? "is-urgent" : ""}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => openTicketDetail(ticket.id)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                openTicketDetail(ticket.id);
                              }
                            }}
                          >
                            <td data-label="Ticket">
                              <TicketCodeWithCopy
                                code={ticket.code || `TKT-${ticket.id}`}
                                className={`tk-table-code ${
                                  ticket.urgent
                                    ? "tk-code-urgent"
                                    : "tk-code-normal"
                                }`}
                              />
                            </td>
                            <td data-label="Data creazione" className="tk-date-cell">
                              {formatDateTime(ticket.createdAt)}
                            </td>
                            <td data-label="Titolo">
                              <strong className="tk-mine-title" title={ticket.title}>
                                {ticket.title}
                              </strong>
                            </td>
                            <td data-label="Creato da">
                              {displayDepartmentUser(
                                ticket.createdBy,
                                departments,
                                ticket.creatorDepartment,
                              )}
                            </td>
                            <td data-label="Stato">
                              <span
                                className={`tk-badge tk-status tk-status-${ticket.status}`}
                              >
                                <StatusIcon status={ticket.status} size={12} />
                                {STATUS_LABELS[ticket.status] || ticket.status}
                              </span>
                            </td>
                            <td data-label="Chat">
                              <div className="tk-chat-cell-state">
                                <button
                                  type="button"
                                  className={`tk-table-chat-button ${
                                    getChatUnreadCount(ticket.id) > 0
                                      ? "has-unread"
                                      : ""
                                  }`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openTicketChat(ticket.id);
                                  }}
                                  title={
                                    getChatUnreadCount(ticket.id) > 0
                                      ? `${getChatUnreadCount(ticket.id)} nuovi messaggi`
                                      : "Apri conversazione"
                                  }
                                >
                                  <MessageSquare size={13} />
                                  <span>{getChatMessageCount(ticket.id)}</span>

                                  {getChatUnreadCount(ticket.id) === 0 &&
                                    needsActionOnReceivedTicket(ticket.id) && (
                                      <span
                                        className="tk-chat-waiting-reply"
                                        title="Hai letto l'ultimo messaggio: devi ancora rispondere"
                                        aria-label="Risposta richiesta"
                                      >
                                        <WaitingInputIcon size={16} />
                                      </span>
                                    )}
                                </button>
                              </div>
                            </td>
                            <td data-label="Allegati">
                              <span className="tk-attachment-count">
                                <Paperclip size={13} />
                                {getTicketAttachmentCount(ticket)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      </table>
                    </div>
                  </div>
                )
              ) : (
                <EmptyState
                  title="Nessun ticket personale"
                  text="Non risultano ticket aperti assegnati direttamente a te."
                />
              )}
            </section>

            <section className="tk-section tk-department-section">
              <div className="tk-section-heading tk-department-section-heading">
                <div>
                  <span className="tk-section-kicker">
                    {isDirector ? "Azienda" : currentDepartment || "Reparto"}
                  </span>

                  <h2>Ticket reparto</h2>

                  <p>
                    {departmentInboxTab === "department"
                      ? "Ticket ancora senza assegnatario: destinati al reparto oppure direttamente a te."
                      : isDirector
                        ? "Tutti gli altri ticket aziendali già destinati o assegnati."
                        : `Tutti gli altri ticket del reparto ${currentDepartment} già destinati o assegnati.`}
                  </p>
                </div>

                <div className="tk-department-heading-actions">
                  <div
                    className="tk-department-view-tabs"
                    role="tablist"
                    aria-label="Vista ticket reparto"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={departmentInboxTab === "department"}
                      className={`tk-department-view-tab ${
                        departmentInboxTab === "department" ? "is-active" : ""
                      }`}
                      onClick={() => setDepartmentInboxTab("department")}
                    >
                      <Users size={14} />
                      <span>Reparto</span>
                      <b>{departmentQueueTickets.length}</b>
                    </button>

                    <button
                      type="button"
                      role="tab"
                      aria-selected={departmentInboxTab === "all_department"}
                      className={`tk-department-view-tab ${
                        departmentInboxTab === "all_department" ? "is-active" : ""
                      }`}
                      onClick={() => setDepartmentInboxTab("all_department")}
                    >
                      <LayoutGrid size={14} />
                      <span>Tutto il reparto</span>
                      <b>{allDepartmentTickets.length}</b>
                    </button>
                  </div>
                </div>
              </div>

              {visibleDepartmentTickets.length > 0 ? (
<div className="tk-mine-table-wrap tk-department-table-wrap">
                      <table className="tk-mine-table tk-department-table">
                        <thead>
                          <tr>
                            <th>Ticket</th>
                            <th>Data creazione</th>
                            <th>Titolo</th>
                            <th>Creato da</th>
                            <th>Dest. utente</th>
                            <th>Assegnato da</th>
                            <th>Assegnato a</th>
                            <th>Stato</th>
                            <th className="tk-edit-column" aria-label="Azioni ticket"></th>
                            <th>Chat</th>
                            <th>Allegati</th>
                          </tr>
                        </thead>

                        <tbody>
                          {visibleDepartmentTickets.map((ticket) => (
                            <tr
                              key={ticket.id}
                              className={`tk-mine-row tk-department-row ${
                                ticket.urgent ? "is-urgent" : ""
                              }`}
                              role="button"
                              tabIndex={0}
                              onClick={() =>
                                openTicketDetail(ticket.id, {
                                  prefillAssignmentDepartment: currentDepartment,
                                })
                              }
                              onKeyDown={(event) => {
                                if (
                                  event.key === "Enter" ||
                                  event.key === " "
                                ) {
                                  event.preventDefault();
                                  openTicketDetail(ticket.id, {
                                    prefillAssignmentDepartment: currentDepartment,
                                  });
                                }
                              }}
                            >
                              <td data-label="Ticket">
                                <TicketCodeWithCopy
                                  code={ticket.code || `TKT-${ticket.id}`}
                                  className={`tk-table-code ${
                                    ticket.urgent
                                      ? "tk-code-urgent"
                                      : "tk-code-normal"
                                  }`}
                                />

                                {ticket.urgent && (
                                  <span className="tk-table-urgent">
                                    <AlertTriangle size={11} />
                                    Urgente
                                  </span>
                                )}
                              </td>

                              <td data-label="Data creazione" className="tk-date-cell">
                                {formatDateTime(ticket.createdAt)}
                              </td>

                              <td data-label="Titolo">
                                <strong>{ticket.title}</strong>
                              </td>

                              <td data-label="Creato da">
                                {displayDepartmentUser(
                                  ticket.createdBy,
                                  departments,
                                  ticket.creatorDepartment,
                                )}
                              </td>


                              <td data-label="Dest. utente">
                                {ticket.targetUser
                                  ? displayDepartmentUser(
                                      ticket.targetUser,
                                      departments,
                                      ticket.targetDepartment,
                                    )
                                  : `${ticket.targetDepartment} - Tutto il reparto`}
                              </td>

                              <td data-label="Assegnato da">
                                {ticket.assignedBy
                                  ? displayDepartmentUser(
                                      ticket.assignedBy,
                                      departments,
                                      ticket.targetDepartment,
                                    )
                                  : "—"}
                              </td>

                              <td data-label="Assegnato a">
                                {ticket.assignedTo
                                  ? displayDepartmentUser(
                                      ticket.assignedTo,
                                      departments,
                                      ticket.targetDepartment,
                                    )
                                  : "—"}
                              </td>

                              <td data-label="Stato">
                                <span
                                  className={`tk-badge tk-status tk-status-${ticket.status}`}
                                >
                                  <StatusIcon
                                    status={ticket.status}
                                    size={12}
                                  />
                                  {STATUS_LABELS[ticket.status] ||
                                    ticket.status}
                                </span>
                              </td>

                              <td data-label="Azioni" className="tk-edit-column">
                                <div className="tk-ticket-row-actions">
                                  {[STATUS.OPEN, STATUS.ASSIGNED].includes(ticket.status) && (
                                    <button
                                      type="button"
                                      className="tk-edit-assignment-button tk-edit-assignment-table"
                                      onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        openTicketAssignment(ticket);
                                      }}
                                      title="Assegna ticket"
                                      aria-label={`Assegna ${ticket.code || `TKT-${ticket.id}`}`}
                                    >
                                      <Pencil size={14} />
                                    </button>
                                  )}

                                  <button
                                    type="button"
                                    className="tk-quick-close-button tk-edit-assignment-table"
                                    disabled={ticket.status === STATUS.CLOSED}
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      if (ticket.status !== STATUS.CLOSED) {
                                        setQuickCloseTicketId(ticket.id);
                                      }
                                    }}
                                    title={
                                      ticket.status === STATUS.CLOSED
                                        ? "Ticket già chiuso"
                                        : "Chiudi per mancanza di informazioni"
                                    }
                                    aria-label={`Chiudi per mancanza di informazioni ${ticket.code || `TKT-${ticket.id}`}`}
                                  >
                                    <XCircle size={14} />
                                  </button>
                                </div>
                              </td>

                              <td data-label="Chat">
                                <button
                                  type="button"
                                  className={`tk-table-chat-button ${
                                    getChatUnreadCount(ticket.id) > 0 ? "has-unread" : ""
                                  }`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openTicketChat(ticket.id);
                                  }}
                                  title="Apri conversazione"
                                >
                                  <MessageSquare size={13} />
                                  <span>{getChatMessageCount(ticket.id)}</span>
                                </button>
                              </td>

                              <td data-label="Allegati">
                                <span className="tk-attachment-count">
                                  <Paperclip size={13} />
                                  {getTicketAttachmentCount(ticket)}
                                </span>
                              </td>

                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
              ) : (
                <EmptyState
                  title={
                    departmentInboxTab === "department"
                      ? "Nessun ticket da prendere in carico"
                      : "Nessun altro ticket del reparto"
                  }
                  text={
                    departmentInboxTab === "department"
                      ? "Non risultano ticket del reparto ancora senza assegnatario."
                      : isDirector
                        ? "Non risultano altri ticket aziendali aperti."
                        : `Non risultano altri ticket aperti per il reparto ${currentDepartment}.`
                  }
                />
              )}
            </section>
          </div>
        )}

        {activeTab === "new" && (
          <div className="tk-tab-panel">
            <section className="tk-compose-layout">
              <form
                className="tk-compose-card"
                onSubmit={submitTicket}
                onPaste={handleTicketPaste}
              >
                <div className="tk-compose-header">
                  <div className="tk-compose-icon">
                    <Plus size={24} />
                  </div>

                  <div>
                    <span>Nuova richiesta</span>
                    <h2>Scrivi un ticket</h2>
                    <p>
                      Scegli il reparto. L'assegnazione a una persona è
                      facoltativa.
                    </p>
                  </div>
                </div>

                <div className="tk-form-grid">
                  <label className="tk-field">
                    <span>
                      <Building2 size={14} />
                      Reparto destinatario
                    </span>

                    <select
                      value={form.targetDepartment}
                      onChange={handleDepartmentChange}
                      required
                    >
                      <option value="">Seleziona reparto</option>

                      {Object.keys(departments).map((department) => (
                        <option key={department} value={department}>
                          {department}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="tk-field">
                    <span>
                      <User size={14} />
                      Destinatario
                      <span className="tk-optional">opzionale</span>
                    </span>

                    <select
                      value={form.targetUser}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          targetUser: event.target.value,
                        }))
                      }
                      disabled={!form.targetDepartment}
                    >
                      <option value="">
                        {form.targetDepartment
                          ? `Tutto il reparto ${form.targetDepartment}`
                          : "Prima scegli il reparto"}
                      </option>

                      {departmentMembers.map((member) => (
                        <option key={member} value={member}>
                          {displayUser(member)}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <label className="tk-field">
                  <span>
                    <Ticket size={14} />
                    Titolo
                  </span>

                  <input
                    type="text"
                    maxLength={120}
                    placeholder="Es. Problema accesso CRM"
                    value={form.title}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        title: event.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <div className="tk-compose-message-row">
                  <label className="tk-field tk-message-field">
                    <span>
                      <Send size={14} />
                      Testo
                    </span>

                    <textarea
                      rows={7}
                      maxLength={4000}
                      placeholder="Descrivi il problema o la richiesta nel modo più chiaro possibile..."
                      value={form.description}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          description: event.target.value,
                        }))
                      }
                      required
                    />
                    <small>{form.description.length}/4000</small>
                  </label>
                  <div className="tk-attachment-wrapper">
                    <div className="tk-attachment-title">
                      <div>
                        <Paperclip size={14} />
                        <span>Allegati</span>
                      </div>

                      <button
                        type="button"
                        className="tk-attachment-screenshot-button"
                        onClick={captureOtherScreen}
                        disabled={screenCaptureBusy}
                        title="Cattura uno schermo e aggiungilo direttamente agli allegati"
                      >
                        <Camera size={14} />
                        <span>
                          {screenCaptureBusy ? "Cattura..." : "Screenshot"}
                        </span>
                      </button>
                    </div>

                    <div
                      className={`tk-attachment ${ticketDragging ? "is-dragging" : ""}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => fileInputRef.current?.click()}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          fileInputRef.current?.click();
                        }
                      }}
                      onDragEnter={(event) => {
                        event.preventDefault();
                        setTicketDragging(true);
                      }}
                      onDragOver={(event) => {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "copy";
                        setTicketDragging(true);
                      }}
                      onDragLeave={(event) => {
                        if (
                          !event.currentTarget.contains(event.relatedTarget)
                        ) {
                          setTicketDragging(false);
                        }
                      }}
                      onDrop={handleTicketDrop}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={ACCEPT_ATTRIBUTE}
                        onChange={handleImageChange}
                      />

                      <Paperclip size={26} />

                      <strong>Allega file</strong>

                      <span>Trascina qui i file oppure clicca</span>

                      <small>
                        JPG, PNG, WEBP, SVG, PDF, Word, Excel · CTRL+V
                        screenshot
                      </small>

                      <b>
                        {form.attachments.length} file ·{" "}
                        {formatFileSize(
                          getAttachmentTotalSize(form.attachments),
                        )}{" "}
                        / 8 MB
                      </b>

                      {form.attachments.length > 0 && (
                        <div className="tk-selected-files">
                          {form.attachments.map((attachment, index) => (
                            <div
                              className="tk-selected-file"
                              key={`${attachment.name}-${attachment.size}-${index}`}
                            >
                              <span>{attachment.name}</span>

                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  removeTicketAttachment(index);
                                }}
                                aria-label={`Rimuovi ${attachment.name}`}
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>{" "}
                </div>

                <div className="tk-compose-options tk-compose-options-single mt-3">
                  <label className="tk-urgent-toggle">
                    <input
                      type="checkbox"
                      checked={form.urgent}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          urgent: event.target.checked,
                        }))
                      }
                    />

                    <span className="tk-switch" />

                    <div>
                      <b>
                        <AlertTriangle size={14} />
                        Ticket urgente
                      </b>
                      <small>
                        Verrà evidenziato e mostrato prima degli altri.
                      </small>
                    </div>
                  </label>
                </div>

                <div className="tk-form-actions">
                  <button
                    type="button"
                    className="tk-btn-secondary"
                    onClick={resetForm}
                  >
                    Pulisci
                  </button>

                  <button
                    type="submit"
                    className="tk-btn-primary"
                    disabled={imageBusy}
                  >
                    <Send size={16} />
                    Invia ticket
                  </button>
                </div>
              </form>

              <aside className="tk-compose-side">
                <span>Riepilogo</span>

                <div className="tk-summary-row">
                  <Building2 size={17} />
                  <div>
                    <small>Reparto</small>
                    <strong>{form.targetDepartment || "Da scegliere"}</strong>
                  </div>
                </div>

                <div className="tk-summary-row">
                  <User size={17} />
                  <div>
                    <small>Destinatario</small>
                    <strong>
                      {form.targetUser
                        ? displayUser(form.targetUser)
                        : form.targetDepartment
                          ? `Tutto il reparto ${form.targetDepartment}`
                          : "Da scegliere"}
                    </strong>
                  </div>
                </div>

                <div className="tk-summary-row">
                  <AlertTriangle size={17} />
                  <div>
                    <small>Priorità</small>
                    <strong>{form.urgent ? "Urgente" : "Normale"}</strong>
                  </div>
                </div>

                <div className="tk-summary-row">
                  <Paperclip size={17} />
                  <div>
                    <small>Allegati</small>
                    <strong>
                      {form.attachments.length
                        ? `${form.attachments.length} file`
                        : "Nessuno"}
                    </strong>
                  </div>
                </div>
              </aside>
            </section>
          </div>
        )}

        {activeTab === "mine" && (
          <div className="tk-tab-panel">
            <section className="tk-section">
              <div className="tk-section-heading tk-section-heading-with-sort">
                <div>
                  <span className="tk-section-kicker">Le mie richieste</span>
                  <h2>Stato dei miei ticket</h2>
                  <p>
                    Tutti i ticket che hai aperto tu, visualizzati in tabella con
                    stato, chat e ultimo aggiornamento. Clicca la riga per vedere il ticket oppure la colonna Chat per aprire la conversazione.
                  </p>
                </div>

                <div className="tk-heading-actions">
                  <div className="tk-stats-inline">
                    <span>
                      <CircleDot size={13} />
                      {mineStats.open}{" "}
                      aperti
                    </span>

                    <span>
                      <Users size={13} />
                      {mineStats.assigned}{" "}
                      assegnati
                    </span>

                    <span>
                      <LoaderCircle size={13} />
                      {mineStats.working}{" "}
                      in lavorazione
                    </span>

                    <span>
                      <CheckCircle2 size={13} />
                      {mineStats.closed}{" "}
                      chiusi
                    </span>

                  </div>

                  <label className="tk-sort-control">
                    <span>Ordina</span>
                    <select
                      value={mineSort}
                      onChange={(event) => {
                        setMineSort(event.target.value);
                        setMinePage(1);
                      }}
                    >
                      <option value="desc">Più recenti prima</option>
                      <option value="asc">Più vecchi prima</option>
                    </select>
                  </label>
                </div>
              </div>

              <div className="tk-mine-filterbar">
                <label className="tk-mine-search">
                  <Search size={15} />
                  <input
                    type="search"
                    value={mineSearch}
                    onChange={(event) => {
                      setMineSearch(event.target.value);
                      setMinePage(1);
                    }}
                    placeholder="Cerca numero ticket, titolo o testo…"
                    aria-label="Cerca nei miei ticket"
                  />
                  {mineSearch && (
                    <button
                      type="button"
                      className="tk-mine-search-clear"
                      onClick={() => {
                        setMineSearch("");
                        setMineSearchDebounced("");
                        setMinePage(1);
                      }}
                      title="Pulisci ricerca"
                      aria-label="Pulisci ricerca"
                    >
                      <X size={13} />
                    </button>
                  )}
                </label>

                <label className="tk-mine-filter-control">
                  <span>Stato</span>
                  <select
                    value={mineStatusFilter}
                    onChange={(event) => {
                      setMineStatusFilter(event.target.value);
                      setMinePage(1);
                    }}
                  >
                    <option value="all">Tutti gli stati</option>
                    <option value={STATUS.OPEN}>Aperti</option>
                    <option value={STATUS.ASSIGNED}>Assegnati</option>
                    <option value={STATUS.WORKING}>In lavorazione</option>
                    <option value={STATUS.CLOSED}>Chiusi</option>
                  </select>
                </label>

                <label className="tk-mine-filter-control">
                  <span>Urgenza</span>
                  <select
                    value={mineUrgentFilter}
                    onChange={(event) => {
                      setMineUrgentFilter(event.target.value);
                      setMinePage(1);
                    }}
                  >
                    <option value="all">Tutti</option>
                    <option value="urgent">Solo urgenti</option>
                    <option value="normal">Non urgenti</option>
                  </select>
                </label>

                {hasMineFilters && (
                  <button
                    type="button"
                    className="tk-mine-reset-filters"
                    onClick={() => {
                      setMineStatusFilter("all");
                      setMineUrgentFilter("all");
                      setMineSearch("");
                      setMineSearchDebounced("");
                      setMinePage(1);
                    }}
                  >
                    <X size={13} />
                    Azzera filtri
                  </button>
                )}
              </div>

              {mineLoading && myCreatedTickets.length === 0 ? (
                <div className="tk-mine-page-loading">
                  <LoaderCircle size={18} className="tk-spin" />
                  Caricamento dei miei ticket…
                </div>
              ) : myCreatedTickets.length > 0 ? (
                <div className="tk-mine-table-wrap tk-seven-row-scroll">
                  {/* Contenitore fisso: mostra circa 7 ticket e poi scorre solo la tabella. */}
                  <table className="tk-mine-table">
                    <thead>
                      <tr>
                        <th>Ticket</th>
                        <th>Data creazione</th>
                        <th>Titolo</th>
                        <th>Dest. utente</th>
                        <th>Assegnato da</th>
                        <th>Assegnato a</th>
                        <th>Stato</th>
                        <th>Chat</th>
                        <th>Allegati</th>
                      </tr>
                    </thead>

                    <tbody>
                      {myCreatedTickets.map((ticket, index) => {
                        const showClosedSeparator =
                          ticket.status === STATUS.CLOSED &&
                          mineStatusFilter !== STATUS.CLOSED &&
                          (
                            index === 0 ||
                            myCreatedTickets[index - 1]?.status !== STATUS.CLOSED
                          );

                        return (
                          <React.Fragment key={ticket.id}>
                            {showClosedSeparator && (
                              <tr
                                className="tk-mine-closed-separator"
                                aria-hidden="true"
                              >
                                <td colSpan={9}>
                                  <div className="tk-mine-closed-separator-content">
                                    <span className="tk-mine-closed-separator-line" />
                                    <span className="tk-mine-closed-separator-label">
                                      <CheckCircle2 size={13} />
                                      Ticket chiusi
                                    </span>
                                    <span className="tk-mine-closed-separator-line" />
                                  </div>
                                </td>
                              </tr>
                            )}

                            <tr
                              className="tk-mine-row"
                          role="button"
                          tabIndex={0}
                          onClick={() => openTicketDetail(ticket.id)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              openTicketDetail(ticket.id);
                            }
                          }}
                        >
                          <td data-label="Ticket">
                            <TicketCodeWithCopy
                              code={ticket.code || `TKT-${ticket.id}`}
                              className={`tk-table-code ${
                                ticket.urgent ? "tk-code-urgent" : "tk-code-normal"
                              }`}
                            />
                            {ticket.urgent && (
                              <span className="tk-table-urgent">
                                <AlertTriangle size={11} />
                                Urgente
                              </span>
                            )}
                          </td>

                          <td data-label="Data creazione" className="tk-date-cell">
                            {formatDateTime(ticket.createdAt)}
                          </td>

                          <td data-label="Titolo">
                            <strong className="tk-mine-title" title={ticket.title}>
                              {ticket.title}
                            </strong>
                          </td>

                          <td data-label="Dest. utente">
                            {ticket.targetUser
                              ? displayDepartmentUser(
                                  ticket.targetUser,
                                  departments,
                                  ticket.targetDepartment,
                                )
                              : `${ticket.targetDepartment} - Tutto il reparto`}
                          </td>

                          <td data-label="Assegnato da">
                            {ticket.assignedBy
                              ? displayDepartmentUser(
                                  ticket.assignedBy,
                                  departments,
                                  ticket.targetDepartment,
                                )
                              : "—"}
                          </td>

                          <td data-label="Assegnato a">
                            {ticket.assignedTo
                              ? displayDepartmentUser(
                                  ticket.assignedTo,
                                  departments,
                                  ticket.targetDepartment,
                                )
                              : "—"}
                          </td>

                          <td data-label="Stato">
                            <span
                              className={`tk-badge tk-status tk-status-${ticket.status}`}
                            >
                              <StatusIcon status={ticket.status} size={12} />
                              {STATUS_LABELS[ticket.status] || ticket.status}
                            </span>
                          </td>

                          <td data-label="Chat">
                            <div className="tk-chat-cell-state">
                              <button
                                type="button"
                                className={`tk-table-chat-button ${
                                  getChatUnreadCount(ticket.id) > 0 ? "has-unread" : ""
                                }`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openTicketChat(ticket.id);
                                }}
                                title={
                                  getChatUnreadCount(ticket.id) > 0
                                    ? `${getChatUnreadCount(ticket.id)} nuovi messaggi`
                                    : "Apri conversazione"
                                }
                              >
                                <MessageSquare size={13} />
                                <span>{getChatMessageCount(ticket.id)}</span>

                                {getChatUnreadCount(ticket.id) === 0 &&
                                  isWaitingForChatReply(ticket.id) && (
                                    <span
                                      className="tk-chat-waiting-reply"
                                      title="In attesa di input"
                                      aria-label="In attesa di input"
                                    >
                                      <WaitingInputIcon size={16} />
                                    </span>
                                  )}
                              </button>
                            </div>
                          </td>

                          <td data-label="Allegati">
                            <span className="tk-attachment-count">
                              <Paperclip size={13} />
                              {getTicketAttachmentCount(ticket)}
                            </span>
                          </td>

                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState
                  title={
                    hasMineFilters
                      ? "Nessun ticket corrisponde ai filtri"
                      : "Non hai ancora aperto ticket"
                  }
                  text={
                    hasMineFilters
                      ? "Modifica la ricerca o i filtri per visualizzare altri ticket."
                      : "Vai nella scheda “Nuovo ticket” per creare la tua prima richiesta."
                  }
                />
              )}

              <div className="tk-mine-pagination" aria-label="Paginazione dei miei ticket">
                <div className="tk-mine-pagination-info">
                  <strong>{minePagination.total}</strong>
                  <span>
                    {hasMineFilters ? "risultati" : "ticket"} ·{" "}
                    {minePagination.total > 0
                      ? `${(minePagination.page - 1) * minePagination.perPage + 1}–${Math.min(
                          minePagination.page * minePagination.perPage,
                          minePagination.total,
                        )} di ${minePagination.total}`
                      : "0 di 0"}{" "}
                    · Pagina {minePagination.page} di {minePagination.totalPages}
                  </span>
                  <small>50 per pagina</small>
                </div>

                <div className="tk-mine-pagination-controls">
                  <button
                    type="button"
                    className="tk-page-nav"
                    disabled={!minePagination.hasPrevious || mineLoading}
                    onClick={() =>
                      setMinePage((page) => Math.max(1, page - 1))
                    }
                  >
                    <ChevronLeft size={15} />
                    Precedente
                  </button>

                  <div className="tk-page-numbers" aria-label="Pagine">
                    {minePageItems.map((item) =>
                      item.type === "ellipsis" ? (
                        <span
                          key={item.value}
                          className="tk-page-ellipsis"
                          aria-hidden="true"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          type="button"
                          key={item.value}
                          className={`tk-page-number ${
                            item.value === minePagination.page ? "is-active" : ""
                          }`}
                          aria-current={
                            item.value === minePagination.page ? "page" : undefined
                          }
                          disabled={mineLoading}
                          onClick={() => setMinePage(item.value)}
                        >
                          {item.value}
                        </button>
                      ),
                    )}
                  </div>

                  <button
                    type="button"
                    className="tk-page-nav"
                    disabled={!minePagination.hasNext || mineLoading}
                    onClick={() =>
                      setMinePage((page) =>
                        Math.min(minePagination.totalPages, page + 1),
                      )
                    }
                  >
                    Successiva
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>

            </section>
          </div>
        )}

        {activeTab === "closed" && (
          <div className="tk-tab-panel">
            <section className="tk-section">
              <div className="tk-section-heading">
                <div>
                  <span className="tk-section-kicker">Archivio completo</span>
                  <h2>Ticket chiusi</h2>
                  <p>
                    Archivio paginato dal server: vengono caricati massimo 50 ticket alla volta.
                  </p>
                </div>

                {/* Totale dei risultati dopo scope, ricerca e intervallo date. */}
                <div className="tk-counter">{closedPagination.total}</div>
              </div>

              {/* Questi quattro pulsanti cambiano lo scope inviato al PHP. */}
              <div
                className="tk-closed-filters"
                role="group"
                aria-label="Filtra ticket chiusi"
              >
                <button
                  type="button"
                  className={`tk-closed-filter ${closedFilter === "mine" ? "is-active" : ""}`}
                  onClick={() => {
                    setClosedFilter("mine");
                    setClosedPage(1);
                  }}
                  aria-pressed={closedFilter === "mine"}
                >
                  <User size={14} />
                  <span>I miei</span>
                  <b>{closedStats.mine}</b>
                </button>

                <button
                  type="button"
                  className={`tk-closed-filter ${closedFilter === "closed-by-me" ? "is-active" : ""}`}
                  onClick={() => {
                    setClosedFilter("closed-by-me");
                    setClosedPage(1);
                  }}
                  aria-pressed={closedFilter === "closed-by-me"}
                >
                  <CheckCircle2 size={14} />
                  <span>Chiusi da me</span>
                  <b>{closedStats.closedByMe}</b>
                </button>

                <button
                  type="button"
                  className={`tk-closed-filter ${closedFilter === "department" ? "is-active" : ""}`}
                  onClick={() => {
                    setClosedFilter("department");
                    setClosedPage(1);
                  }}
                  aria-pressed={closedFilter === "department"}
                >
                  <Building2 size={14} />
                  <span>Reparto</span>
                  <b>{closedStats.department}</b>
                </button>

                <button
                  type="button"
                  className={`tk-closed-filter ${closedFilter === "all" ? "is-active" : ""}`}
                  onClick={() => {
                    setClosedFilter("all");
                    setClosedPage(1);
                  }}
                  aria-pressed={closedFilter === "all"}
                >
                  <Users size={14} />
                  <span>Tutti</span>
                  <b>{closedStats.all}</b>
                </button>
              </div>

              {/* Ricerca LIKE e intervallo date vengono inviati al PHP. */}
              <div className="tk-closed-searchbar">
                <label className="tk-closed-search">
                  <Search size={15} />
                  <input
                    type="search"
                    value={closedSearch}
                    onChange={(event) => setClosedSearch(event.target.value)}
                    placeholder="Numero ticket, titolo o descrizione..."
                    aria-label="Cerca nei ticket chiusi"
                  />

                  {closedSearch && (
                    <button
                      type="button"
                      className="tk-closed-search-clear"
                      onClick={() => {
                        setClosedSearch("");
                        setClosedSearchDebounced("");
                        setClosedPage(1);
                      }}
                      title="Cancella ricerca"
                      aria-label="Cancella ricerca"
                    >
                      <X size={13} />
                    </button>
                  )}
                </label>

                <label className="tk-closed-date-control">
                  <span>Chiuso dal</span>
                  <input
                    type="date"
                    value={closedDateFrom}
                    max={closedDateTo || undefined}
                    onChange={(event) => {
                      setClosedDateFrom(event.target.value);
                      setClosedPage(1);
                    }}
                  />
                </label>

                <label className="tk-closed-date-control">
                  <span>Chiuso al</span>
                  <input
                    type="date"
                    value={closedDateTo}
                    min={closedDateFrom || undefined}
                    onChange={(event) => {
                      setClosedDateTo(event.target.value);
                      setClosedPage(1);
                    }}
                  />
                </label>

                {hasClosedFilters && (
                  <button
                    type="button"
                    className="tk-closed-reset"
                    onClick={() => {
                      setClosedSearch("");
                      setClosedSearchDebounced("");
                      setClosedDateFrom("");
                      setClosedDateTo("");
                      setClosedPage(1);
                    }}
                  >
                    <X size={13} />
                    Azzera filtri
                  </button>
                )}
              </div>

              {/* Loader mostrato solo quando cambiamo pagina/filtro manualmente. */}
              {closedLoading && visibleClosedTickets.length === 0 ? (
                <div className="tk-mine-page-loading">
                  <LoaderCircle size={18} className="tk-spin" />
                  Caricamento ticket chiusi…
                </div>
              ) : visibleClosedTickets.length > 0 ? (
                <div className="tk-closed-table-wrap tk-seven-row-scroll">
                  {/* Contenitore fisso: mostra 7 ticket chiusi e poi scorre internamente. */}
                  <table className="tk-closed-table">
                    <thead>
                      <tr>
                        <th>Ticket</th>
                        <th>Data creazione</th>
                        <th>Titolo</th>
                        <th>Creato da</th>
                        <th>Chiuso da</th>
                        <th>Chat</th>
                        <th>Allegati</th>
                        <th>Chiuso il</th>
                      </tr>
                    </thead>

                    <tbody>
                      {visibleClosedTickets.map((ticket) => (
                        <tr
                          key={ticket.id}
                          className="tk-closed-row"
                          role="button"
                          tabIndex={0}
                          onClick={() => openTicketDetail(ticket.id)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              openTicketDetail(ticket.id);
                            }
                          }}
                        >
                          {/* Nei chiusi non mostriamo più il badge Urgente. */}
                          <td data-label="Ticket">
                            <TicketCodeWithCopy
                              code={ticket.code || `TKT-${ticket.id}`}
                              className="tk-table-code"
                            />
                          </td>

                          <td data-label="Data creazione" className="tk-date-cell">
                            {formatDateTime(ticket.createdAt)}
                          </td>

                          <td data-label="Titolo">
                            <strong>{ticket.title}</strong>
                          </td>

                          <td data-label="Creato da">
                            {displayDepartmentUser(
                              ticket.createdBy,
                              departments,
                              ticket.creatorDepartment,
                            )}
                          </td>

                          {/* Dest. utente / Assegnato da / Assegnato a sono volutamente rimossi. */}
                          <td data-label="Chiuso da">
                            {ticket.closedBy
                              ? displayDepartmentUser(
                                  ticket.closedBy,
                                  departments,
                                  ticket.targetDepartment,
                                )
                              : ticket.updatedBy
                                ? displayDepartmentUser(
                                    ticket.updatedBy,
                                    departments,
                                    ticket.targetDepartment,
                                  )
                                : "—"}
                          </td>

                          <td data-label="Chat">
                            <button
                              type="button"
                              className={`tk-table-chat-button ${
                                getChatUnreadCount(ticket.id) > 0 ? "has-unread" : ""
                              }`}
                              onClick={(event) => {
                                event.stopPropagation();
                                openTicketChat(ticket.id);
                              }}
                              title={
                                getChatUnreadCount(ticket.id) > 0
                                  ? `${getChatUnreadCount(ticket.id)} nuovi messaggi`
                                  : "Apri conversazione"
                              }
                            >
                              <MessageSquare size={13} />
                              <span>{getChatMessageCount(ticket.id)}</span>
                            </button>
                          </td>

                          <td data-label="Allegati">
                            <span className="tk-attachment-count">
                              <Paperclip size={13} />
                              {getTicketAttachmentCount(ticket)}
                            </span>
                          </td>

                          <td data-label="Chiuso il">
                            {formatDateTime(
                              ticket.resolvedAt || ticket.updatedAt,
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState
                  title="Nessun ticket chiuso"
                  text={
                    hasClosedFilters
                      ? "Nessun ticket chiuso corrisponde alla ricerca o all'intervallo di date selezionato."
                      : closedFilter === "mine"
                        ? "Non risultano ticket chiusi creati da te o assegnati direttamente a te."
                        : closedFilter === "closed-by-me"
                          ? "Non risultano ticket chiusi da te."
                          : closedFilter === "department"
                            ? `Non risultano ticket chiusi per il reparto ${currentDepartment}.`
                            : "Non risultano ticket chiusi."
                  }
                />
              )}

              {/* Paginazione vera: ogni click provoca una nuova GET closed_tickets. */}
              <div className="tk-mine-pagination" aria-label="Paginazione ticket chiusi">
                <div className="tk-mine-pagination-info">
                  <strong>{closedPagination.total}</strong>
                  <span>
                    {hasClosedFilters ? "risultati" : "ticket"} ·{" "}
                    {closedPagination.total > 0
                      ? `${(closedPagination.page - 1) * closedPagination.perPage + 1}–${Math.min(
                          closedPagination.page * closedPagination.perPage,
                          closedPagination.total,
                        )} di ${closedPagination.total}`
                      : "0 di 0"}{" "}
                    · Pagina {closedPagination.page} di {closedPagination.totalPages}
                  </span>
                  <small>50 per pagina</small>
                </div>

                <div className="tk-mine-pagination-controls">
                  <button
                    type="button"
                    className="tk-page-nav"
                    disabled={!closedPagination.hasPrevious || closedLoading}
                    onClick={() =>
                      setClosedPage((page) => Math.max(1, page - 1))
                    }
                  >
                    <ChevronLeft size={15} />
                    Precedente
                  </button>

                  <div className="tk-page-numbers" aria-label="Pagine ticket chiusi">
                    {closedPageItems.map((item) =>
                      item.type === "ellipsis" ? (
                        <span
                          key={item.value}
                          className="tk-page-ellipsis"
                          aria-hidden="true"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          type="button"
                          key={item.value}
                          className={`tk-page-number ${
                            item.value === closedPagination.page ? "is-active" : ""
                          }`}
                          aria-current={
                            item.value === closedPagination.page ? "page" : undefined
                          }
                          disabled={closedLoading}
                          onClick={() => setClosedPage(item.value)}
                        >
                          {item.value}
                        </button>
                      ),
                    )}
                  </div>

                  <button
                    type="button"
                    className="tk-page-nav"
                    disabled={!closedPagination.hasNext || closedLoading}
                    onClick={() =>
                      setClosedPage((page) =>
                        Math.min(closedPagination.totalPages, page + 1),
                      )
                    }
                  >
                    Successiva
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            </section>
          </div>
        )}

      </div>

      {quickCloseTicket &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="tk-chat-join-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Conferma chiusura per mancanza di informazioni"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !quickCloseBusy) {
                setQuickCloseTicketId(null);
              }
            }}
          >
            <div className="tk-chat-join-card">
              <div className="tk-chat-join-icon tk-quick-close-confirm-icon">
                <XCircle size={22} />
              </div>
              <h3>Chiudere il ticket?</h3>
              <p>
                Sei sicuro di voler chiudere <b>{quickCloseTicket.code || `TKT-${quickCloseTicket.id}`}</b>
                {" "}per mancanza di informazioni? La risposta di chiusura sarà impostata automaticamente su
                {" "}<b>“Ticket chiuso per mancanza di informazioni”</b>.
              </p>
              <div className="tk-chat-join-actions">
                <button
                  type="button"
                  className="tk-chat-join-cancel"
                  onClick={() => setQuickCloseTicketId(null)}
                  disabled={quickCloseBusy}
                >
                  Annulla
                </button>
                <button
                  type="button"
                  className="tk-chat-join-confirm tk-quick-close-confirm"
                  onClick={quickCloseMissingInfo}
                  disabled={quickCloseBusy}
                >
                  {quickCloseBusy ? (
                    <LoaderCircle size={15} className="tk-spin" />
                  ) : (
                    <XCircle size={15} />
                  )}
                  <span>{quickCloseBusy ? "Chiusura..." : "Sì, chiudi ticket"}</span>
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {selectedMyTicket &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="tk-ticket-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Dettaglio ${selectedMyTicket.code}`}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setSelectedMyTicketId(null);
              }
            }}
          >
            <div className="tk-ticket-detail-modal-card tk-ticket-info-modal-card">
              <button
                type="button"
                className="tk-ticket-detail-close"
                onClick={() => setSelectedMyTicketId(null)}
                aria-label="Chiudi dettaglio ticket"
              >
                <X size={19} />
              </button>

              <div className="tk-ticket-detail-heading">
                <div>
                  <span className="tk-section-kicker">Dettaglio ticket</span>
                  <h2>
                    <TicketCodeWithCopy
                      code={selectedMyTicket.code || `TKT-${selectedMyTicket.id}`}
                      className={`tk-modal-ticket-code ${
                        selectedMyTicket.urgent
                          ? "tk-code-urgent"
                          : "tk-code-normal"
                      }`}
                    />
                  </h2>
                </div>

                <span className={`tk-badge tk-status tk-status-${selectedMyTicket.status}`}>
                  <StatusIcon status={selectedMyTicket.status} size={12} />
                  {STATUS_LABELS[selectedMyTicket.status] || selectedMyTicket.status}
                </span>
              </div>

              <div className="tk-ticket-detail-scroll">
                <TicketCard
                  ticket={selectedMyTicket}
                  currentUser={currentUser}
                  currentDepartment={currentDepartment}
                  isDirector={isDirector}
                  showCreator
                  allowStatusChange={!isDirector}
                  onStatusChange={updateTicketStatus}
                  onEditAssignment={() => openTicketAssignment(selectedMyTicket)}
                  onCloseStatusRequest={() =>
                    openTicketChat(selectedMyTicket.id, { startClosing: true })
                  }
                  onQuickCloseRequest={() => setQuickCloseTicketId(selectedMyTicket.id)}
                  onOpen={() => openTicketChat(selectedMyTicket.id)}
                />

                <div className="tk-assignment-audit">
                  <div>
                    <span>Destinazione originale</span>
                    <strong>
                      {selectedMyTicket.targetDepartment} · {selectedMyTicket.targetUser
                        ? displayUser(selectedMyTicket.targetUser)
                        : "Tutto il reparto"}
                    </strong>
                  </div>
                  <div>
                    <span>Assegnato da</span>
                    <strong>
                      {selectedMyTicket.assignedBy
                        ? displayUser(selectedMyTicket.assignedBy)
                        : "Non ancora assegnato"}
                    </strong>
                  </div>
                  <div>
                    <span>Assegnato a</span>
                    <strong>
                      {selectedMyTicket.assignedTo
                        ? displayUser(selectedMyTicket.assignedTo)
                        : "Non ancora assegnato"}
                    </strong>
                  </div>
                </div>

                {canAssignSelectedTicket && (
                  <div className="tk-assignment-panel">
                    <div className="tk-assignment-heading">
                      <div>
                        <span className="tk-section-kicker">Riassegnazione</span>
                        <h3>Assegna ticket</h3>
                      </div>
                      <p>
                        La destinazione originale non verrà modificata.
                      </p>
                    </div>

                    <div className="tk-assignment-grid">
                      <label className="tk-field">
                        <span>
                          <Building2 size={14} />
                          Reparto <b>*</b>
                        </span>
                        <select
                          value={assignmentForm.department}
                          onChange={(event) =>
                            setAssignmentForm({
                              department: event.target.value,
                              user: "",
                            })
                          }
                        >
                          <option value="">Seleziona reparto</option>
                          {Object.keys(departments).map((department) => (
                            <option key={department} value={department}>
                              {department}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="tk-field">
                        <span>
                          <User size={14} />
                          Utente <b>*</b>
                        </span>
                        <select
                          value={assignmentForm.user}
                          onChange={(event) =>
                            setAssignmentForm((current) => ({
                              ...current,
                              user: event.target.value,
                            }))
                          }
                          disabled={!assignmentForm.department}
                        >
                          <option value="">
                            {assignmentForm.department
                              ? "Seleziona utente"
                              : "Prima scegli il reparto"}
                          </option>
                          {assignmentMembers.map((member) => (
                            <option key={member} value={member}>
                              {displayUser(member)}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <button
                      type="button"
                      className="tk-assign-submit"
                      onClick={assignTicket}
                      disabled={
                        assignmentBusy ||
                        !assignmentForm.department ||
                        !assignmentForm.user
                      }
                    >
                      <Users size={15} />
                      {assignmentBusy ? "Assegnazione..." : "Assegna ticket"}
                    </button>
                  </div>
                )}

                {canCloseSelectedTicket && (
                  <div className="tk-close-panel">
                    <div className="tk-assignment-heading">
                      <div>
                        <span className="tk-section-kicker">Chiusura ticket</span>
                        <h3>Risposta di chiusura</h3>
                      </div>
                      <p>La risposta è obbligatoria. L'allegato è opzionale.</p>
                    </div>

                    <label className="tk-field tk-close-response">
                      <span>Risposta <b>*</b></span>
                      <textarea
                        value={closureForm.response}
                        onChange={(event) =>
                          setClosureForm((current) => ({
                            ...current,
                            response: event.target.value,
                          }))
                        }
                        placeholder="Scrivi la risposta conclusiva del ticket..."
                        rows={5}
                      />
                    </label>

                    <div className="tk-close-attachment-row">
                      <label className="tk-close-file-button">
                        <Paperclip size={15} />
                        <span>Allegato opzionale</span>
                        <input
                          ref={closureFileInputRef}
                          type="file"
                          accept={ACCEPT_ATTRIBUTE}
                          onChange={handleClosureAttachmentChange}
                        />
                      </label>

                      {closureForm.attachment && (
                        <div className="tk-close-file-selected">
                          <span title={closureForm.attachment.name}>
                            {closureForm.attachment.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setClosureForm((current) => ({
                                ...current,
                                attachment: null,
                              }));
                              if (closureFileInputRef.current) {
                                closureFileInputRef.current.value = "";
                              }
                            }}
                            aria-label="Rimuovi allegato di chiusura"
                          >
                            <X size={13} />
                          </button>
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      className="tk-close-submit"
                      onClick={closeTicket}
                      disabled={closureBusy || !closureForm.response.trim()}
                    >
                      <CheckCircle2 size={16} />
                      {closureBusy ? "Chiusura..." : "Chiudi ticket"}
                    </button>
                  </div>
                )}

                {selectedMyTicket.status === STATUS.CLOSED && (
                  <div
                    className={`tk-closure-summary ${
                      isMissingInfoClosure(selectedMyTicket.replyText)
                        ? "is-missing-info"
                        : "is-normal-close"
                    }`}
                  >
                    <div className="tk-assignment-heading">
                      <div>
                        <span className="tk-section-kicker">Ticket chiuso</span>
                        <h3>Risposta di chiusura</h3>
                      </div>
                      <p>{formatDateTime(selectedMyTicket.resolvedAt)}</p>
                    </div>

                    <div className="tk-closure-summary-text">
                      {selectedMyTicket.replyText || "—"}
                    </div>

                    <div className="tk-closure-meta">
                      <span>Chiuso da <b>{selectedMyTicket.closedBy ? displayUser(selectedMyTicket.closedBy) : "—"}</b></span>
                      {selectedMyTicket.replyAttachments?.length > 0 && (
                        <div className="tk-closure-files">
                          {selectedMyTicket.replyAttachments.map((attachment, index) => (
                            <a
                              key={`${attachment.url || attachment.name}-${index}`}
                              href={attachment.url || attachment.dataUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Paperclip size={13} />
                              {attachment.name || `Allegato ${index + 1}`}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}

      {selectedChatTicket &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="tk-ticket-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Conversazione ${selectedChatTicket.code}`}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setSelectedChatTicketId(null);
                setSelectedChatStartClosing(false);
              }
            }}
          >
            <div className="tk-ticket-detail-modal-card tk-ticket-chat-modal-card">
              <button
                type="button"
                className="tk-ticket-detail-close"
                onClick={() => {
                  setSelectedChatTicketId(null);
                  setSelectedChatStartClosing(false);
                }}
                aria-label="Chiudi conversazione"
              >
                <X size={19} />
              </button>

              <div className="tk-ticket-chat-only tk-ticket-chat-only-clean">
                <TicketChat
                  ticket={selectedChatTicket}
                  currentUser={currentUser}
                  currentDepartment={currentDepartment}
                  isDirector={isDirector}
                  onSeen={markChatSeen}
                  onTicketUpdated={async () => {
                    await Promise.all([
                      loadTickets({ silent: true }),
                      loadMyTickets({
                        page: minePage,
                        sort: mineSort,
                        silent: true,
                      }),
                      loadClosedTickets({
                        page: closedPage,
                        scope: closedFilter,
                        search: closedSearchDebounced,
                        dateFrom: closedDateFrom,
                        dateTo: closedDateTo,
                        sort: closedSort,
                        silent: true,
                      }),
                    ]);
                  }}
                  openClosureOnMount={selectedChatStartClosing}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}

      {screenshotDraft &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="tk-screenshot-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Screenshot aggiunto agli allegati"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setScreenshotDraft(null);
                setScreenshotCopied(false);
              }
            }}
          >
            <div className="tk-screenshot-card">
              <div className="tk-screenshot-preview">
                <img
                  src={screenshotDraft.attachment?.dataUrl}
                  alt="Anteprima screenshot"
                />
              </div>

              <div className="tk-screenshot-copy">
                <span>Screenshot aggiunto agli allegati</span>
                <strong>{screenshotDraft.file?.name}</strong>
              </div>

              <div className="tk-screenshot-actions">
                <button
                  type="button"
                  className="tk-screenshot-cancel"
                  onClick={cancelCapturedScreenshot}
                >
                  Annulla
                </button>

                <button
                  type="button"
                  className="tk-screenshot-copy-button"
                  onClick={copyCapturedScreenshot}
                >
                  {screenshotCopied ? <Check size={15} /> : <Copy size={15} />}
                  {screenshotCopied ? "Copiato" : "Copia"}
                </button>

                <button
                  type="button"
                  className="tk-screenshot-share-button"
                  onClick={shareCapturedScreenshot}
                  disabled={screenshotShareBusy}
                >
                  <Share2 size={15} />
                  {screenshotShareBusy ? "Condivisione..." : "Condividi"}
                </button>

                <button
                  type="button"
                  className="tk-screenshot-done-button"
                  onClick={() => {
                    setScreenshotDraft(null);
                    setScreenshotCopied(false);
                  }}
                >
                  <Check size={15} />
                  Usa allegato
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <style>{`
        * {
          box-sizing: border-box;
        }

        .tk-shell {
          position: relative;
          min-height: 100vh;
          overflow: hidden;
          color: #f7f7f8;
          background:
            radial-gradient(
              circle at 8% 8%,
              rgba(235,181,27,0.13),
              transparent 26%
            ),
            radial-gradient(
              circle at 91% 18%,
              rgba(255,255,255,0.07),
              transparent 25%
            ),
            radial-gradient(
              circle at 52% 100%,
              rgba(84,100,130,0.12),
              transparent 36%
            ),
            #202227;
        }

        .tk-shell::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            linear-gradient(
              135deg,
              rgba(255,255,255,0.025),
              transparent 35%
            );
        }

        .tk-ambient {
          position: absolute;
          pointer-events: none;
          border-radius: 50%;
          filter: blur(75px);
          opacity: 0.32;
        }

        .tk-ambient-one {
          width: 300px;
          height: 300px;
          top: 22%;
          left: -130px;
          background: rgba(235,181,27,0.12);
        }

        .tk-ambient-two {
          width: 340px;
          height: 340px;
          right: -150px;
          top: 48%;
          background: rgba(110,130,170,0.10);
        }

        .tk-page {
          position: relative;
          z-index: 2;
          width: min(1420px, calc(100% - 40px));
          margin: 0 auto;
          padding: 42px 0 75px;
        }

        .tk-hero {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 28px;
          margin-bottom: 31px;
        }

        .tk-eyebrow {
          display: flex;
          align-items: center;
          gap: 9px;
          margin-bottom: 13px;
          color: rgba(247,247,248,0.48);
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1.9px;
          text-transform: uppercase;
        }

        .tk-eyebrow span {
          width: 7px;
          height: 7px;
          flex: 0 0 auto;
          border-radius: 50%;
          background: ${GOLD};
          box-shadow: 0 0 12px rgba(235,181,27,0.65);
        }

        .tk-hero h1 {
          margin: 0;
          color: #f7f7f8;
          font-size: clamp(42px, 5vw, 68px);
          line-height: 0.98;
          font-weight: 300;
          letter-spacing: -3px;
        }

        .tk-hero h1 strong {
          color: ${GOLD};
          font-weight: 650;
          text-shadow: 0 0 28px rgba(235,181,27,0.18);
        }

        .tk-hero > div:first-child > p {
          max-width: 650px;
          margin: 20px 0 0;
          color: rgba(247,247,248,0.62);
          font-size: 14px;
          line-height: 1.7;
        }

        .tk-hero-actions {
          display: flex;
          align-items: center;
          gap: 10px;
          flex: 0 0 auto;
        }

        .tk-screen-capture-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 42px;
          padding: 0 13px;
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 14px;
          color: rgba(255,255,255,0.72);
          background: rgba(255,255,255,0.055);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.08);
          font-size: 9px;
          font-weight: 800;
          cursor: pointer;
          transition: border-color .18s ease, color .18s ease, background .18s ease, transform .18s ease;
        }

        .tk-screen-capture-button span {
          color: inherit;
          margin: 0;
          font-size: inherit;
          font-weight: inherit;
          letter-spacing: .4px;
          text-transform: none;
        }

        .tk-screen-capture-button:hover:not(:disabled) {
          transform: translateY(-1px);
          color: ${GOLD};
          border-color: rgba(235,181,27,0.32);
          background: rgba(235,181,27,0.08);
        }

        .tk-screen-capture-button:disabled {
          opacity: .55;
          cursor: wait;
        }

        .tk-user-glass {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 250px;
          padding: 13px 15px;
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 21px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.105),
              rgba(255,255,255,0.042)
            );
          box-shadow:
            0 18px 45px rgba(0,0,0,0.20),
            inset 0 1px 0 rgba(255,255,255,0.15);
          backdrop-filter: blur(24px) saturate(135%);
          -webkit-backdrop-filter: blur(24px) saturate(135%);
        }

        .tk-user-avatar {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 43px;
          height: 43px;
          flex: 0 0 auto;
          border-radius: 14px;
          color: #202227;
          background: ${GOLD};
          font-size: 15px;
          font-weight: 850;
          box-shadow:
            0 8px 22px rgba(235,181,27,0.13),
            inset 0 1px 0 rgba(255,255,255,0.35);
        }

        .tk-user-glass > div:last-child {
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .tk-user-glass span {
          margin-bottom: 1px;
          color: rgba(255,255,255,0.34);
          font-size: 8px;
          font-weight: 800;
          letter-spacing: 1.2px;
          text-transform: uppercase;
        }

        .tk-user-glass strong {
          overflow: hidden;
          color: rgba(255,255,255,0.88);
          font-size: 12px;
          font-weight: 700;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .tk-user-glass small {
          margin-top: 2px;
          color: ${GOLD};
          font-size: 9px;
          font-weight: 650;
        }

        .tk-warning-banner,
        .tk-notice,
        .tk-danger-notice,
        .tk-error {
          display: flex;
          align-items: flex-start;
          gap: 9px;
          margin-bottom: 15px;
          padding: 12px 14px;
          border-radius: 15px;
          font-size: 11px;
          line-height: 1.5;
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
        }

        .tk-warning-banner,
        .tk-danger-notice,
        .tk-error {
          color: rgba(255,222,222,0.92);
          border: 1px solid rgba(255,110,110,0.22);
          background: rgba(140,35,35,0.16);
        }

        .tk-notice {
          color: rgba(226,255,231,0.92);
          border: 1px solid rgba(120,220,145,0.20);
          background: rgba(40,120,65,0.14);
        }

        .tk-tabs {
          display: flex;
          align-items: center;
          gap: 7px;
          width: fit-content;
          max-width: 100%;
          margin-bottom: 18px;
          padding: 6px;
          overflow-x: auto;
          border: 1px solid rgba(255,255,255,0.13);
          border-radius: 20px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.085),
              rgba(255,255,255,0.033)
            );
          box-shadow:
            0 16px 42px rgba(0,0,0,0.17),
            inset 0 1px 0 rgba(255,255,255,0.13);
          backdrop-filter: blur(24px) saturate(135%);
          -webkit-backdrop-filter: blur(24px) saturate(135%);
          scrollbar-width: none;
        }

        .tk-tabs::-webkit-scrollbar {
          display: none;
        }

        .tk-tab {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 42px;
          padding: 0 14px;
          border: 1px solid transparent;
          border-radius: 14px;
          color: rgba(255,255,255,0.49);
          background: transparent;
          font-size: 10px;
          font-weight: 700;
          white-space: nowrap;
          cursor: pointer;
          transition:
            background 0.2s ease,
            border-color 0.2s ease,
            color 0.2s ease;
        }

        .tk-tab:hover {
          color: rgba(255,255,255,0.82);
          background: rgba(255,255,255,0.05);
        }

        .tk-tab.is-active {
          color: ${GOLD};
          border-color: rgba(235,181,27,0.23);
          background:
            linear-gradient(
              145deg,
              rgba(235,181,27,0.12),
              rgba(235,181,27,0.045)
            );
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.10);
        }

        .tk-tab b {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
          height: 20px;
          padding: 0 6px;
          border-radius: 999px;
          color: #202227;
          background: ${GOLD};
          font-size: 9px;
          font-weight: 900;
        }

        .tk-tab-panel {
          animation: tk-panel-in 0.22s ease both;
        }

        @keyframes tk-panel-in {
          from {
            opacity: 0;
            transform: translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .tk-section {
          margin-top: 18px;
          padding: 22px;
          border: 1px solid rgba(255,255,255,0.13);
          border-radius: 27px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.083),
              rgba(255,255,255,0.031)
            );
          box-shadow:
            0 24px 65px rgba(0,0,0,0.19),
            inset 0 1px 0 rgba(255,255,255,0.14);
          backdrop-filter: blur(26px) saturate(135%);
          -webkit-backdrop-filter: blur(26px) saturate(135%);
        }

        .tk-section-heading {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 20px;
          margin-bottom: 17px;
        }

        .tk-section-kicker {
          display: block;
          margin-bottom: 3px;
          color: ${GOLD};
          font-size: 8px;
          font-weight: 850;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }

        .tk-section-heading h2 {
          margin: 0;
          color: #f7f7f8;
          font-size: 20px;
          font-weight: 650;
          letter-spacing: -0.5px;
        }

        .tk-section-heading p {
          margin: 4px 0 0;
          color: rgba(255,255,255,0.40);
          font-size: 10px;
          line-height: 1.5;
        }

        .tk-counter {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 39px;
          height: 39px;
          padding: 0 10px;
          border: 1px solid rgba(255,255,255,0.11);
          border-radius: 13px;
          color: rgba(255,255,255,0.64);
          background: rgba(255,255,255,0.055);
          font-size: 11px;
          font-weight: 850;
        }

        .tk-department-heading-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 9px;
          flex-wrap: wrap;
        }

        .tk-department-view-tabs {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 14px;
          background: rgba(255,255,255,0.035);
        }

        .tk-department-view-tab {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 34px;
          padding: 0 11px;
          border: 1px solid transparent;
          border-radius: 10px;
          color: rgba(255,255,255,0.48);
          background: transparent;
          font-size: 10px;
          font-weight: 850;
          cursor: pointer;
          transition:
            color 0.18s ease,
            border-color 0.18s ease,
            background 0.18s ease,
            transform 0.18s ease;
        }

        .tk-department-view-tab:hover {
          color: rgba(255,255,255,0.82);
          background: rgba(255,255,255,0.045);
        }

        .tk-department-view-tab.is-active {
          color: ${GOLD};
          border-color: rgba(235,181,27,0.28);
          background: rgba(235,181,27,0.085);
        }

        .tk-department-view-tab:active {
          transform: scale(0.985);
        }

        .tk-department-view-tab b {
          display: grid;
          place-items: center;
          min-width: 21px;
          height: 21px;
          padding: 0 5px;
          border-radius: 7px;
          color: rgba(255,255,255,0.64);
          background: rgba(255,255,255,0.07);
          font-size: 8px;
          font-weight: 950;
        }

        .tk-department-view-tab.is-active b {
          color: #1b1b1d;
          background: ${GOLD};
        }

        .tk-department-toggle {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-height: 39px;
          padding: 0 13px;
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 13px;
          color: rgba(255,255,255,0.72);
          background: rgba(255,255,255,0.05);
          font-size: 10px;
          font-weight: 800;
          cursor: pointer;
          transition:
            color 0.18s ease,
            border-color 0.18s ease,
            background 0.18s ease,
            transform 0.18s ease;
        }

        .tk-department-toggle:hover,
        .tk-department-toggle.is-open {
          color: #fff;
          border-color: rgba(235,181,27,0.34);
          background: rgba(235,181,27,0.085);
        }

        .tk-department-toggle:active {
          transform: scale(0.985);
        }

        .tk-department-toggle b {
          display: grid;
          place-items: center;
          width: 20px;
          height: 20px;
          margin-left: 2px;
          border-radius: 7px;
          color: #1b1b1d;
          background: ${GOLD};
          font-size: 14px;
          font-weight: 900;
          line-height: 1;
        }

        .tk-list-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          padding: 14px 16px;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 18px;
          background: rgba(255,255,255,0.035);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
        }

        .tk-list-toolbar > div {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .tk-list-toolbar > div strong {
          color: rgba(255,255,255,0.88);
          font-size: 12px;
        }

        .tk-sort-control {
          display: flex;
          align-items: center;
          gap: 9px;
        }

        .tk-sort-control > span {
          color: rgba(255,255,255,0.42);
          font-size: 9px;
          font-weight: 850;
          letter-spacing: .7px;
          text-transform: uppercase;
        }

        .tk-sort-control select {
          min-width: 220px;
          height: 38px;
          padding: 0 34px 0 12px;
          border: 1px solid rgba(255,255,255,0.11);
          border-radius: 12px;
          outline: none;
          color: rgba(255,255,255,0.86);
          background: rgba(255,255,255,0.055);
          font-size: 10px;
          font-weight: 750;
          cursor: pointer;
        }

        .tk-sort-control select:focus {
          border-color: rgba(235,181,27,0.55);
          box-shadow: 0 0 0 3px rgba(235,181,27,0.09);
        }

        .tk-sort-control select option {
          color: #f7f7f8;
          background: #25272c;
        }

        .tk-section-heading-with-sort {
          align-items: flex-end;
        }

        .tk-heading-actions {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 10px;
        }

        .tk-mine-filterbar {
          display: grid;
          grid-template-columns: minmax(260px, 1fr) auto auto auto;
          align-items: end;
          gap: 10px;
          margin: 0 0 14px;
          padding: 11px;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 17px;
          background: rgba(255,255,255,0.028);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
        }

        .tk-mine-search {
          position: relative;
          display: flex;
          align-items: center;
          min-width: 0;
          height: 40px;
          padding: 0 38px 0 12px;
          border: 1px solid rgba(255,255,255,0.11);
          border-radius: 12px;
          color: rgba(255,255,255,0.38);
          background: rgba(255,255,255,0.045);
          transition:
            border-color 0.18s ease,
            box-shadow 0.18s ease,
            background 0.18s ease;
        }

        .tk-mine-search:focus-within {
          border-color: rgba(235,181,27,0.55);
          background: rgba(255,255,255,0.06);
          box-shadow: 0 0 0 3px rgba(235,181,27,0.09);
        }

        .tk-mine-search input {
          width: 100%;
          min-width: 0;
          height: 100%;
          margin-left: 9px;
          border: 0;
          outline: 0;
          color: rgba(255,255,255,0.88);
          background: transparent;
          font-size: 11px;
          font-weight: 700;
        }

        .tk-mine-search input::placeholder {
          color: rgba(255,255,255,0.28);
        }

        .tk-mine-search-clear {
          position: absolute;
          right: 9px;
          top: 50%;
          display: grid;
          place-items: center;
          width: 24px;
          height: 24px;
          padding: 0;
          border: 0;
          border-radius: 8px;
          color: rgba(255,255,255,0.46);
          background: rgba(255,255,255,0.05);
          cursor: pointer;
          transform: translateY(-50%);
        }

        .tk-mine-filter-control {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .tk-mine-filter-control > span {
          color: rgba(255,255,255,0.38);
          font-size: 8px;
          font-weight: 900;
          letter-spacing: .65px;
          text-transform: uppercase;
        }

        .tk-mine-filter-control select {
          min-width: 150px;
          height: 40px;
          padding: 0 32px 0 11px;
          border: 1px solid rgba(255,255,255,0.11);
          border-radius: 12px;
          outline: none;
          color: rgba(255,255,255,0.86);
          background: rgba(255,255,255,0.045);
          font-size: 10px;
          font-weight: 800;
          cursor: pointer;
        }

        .tk-mine-filter-control select:focus {
          border-color: rgba(235,181,27,0.55);
          box-shadow: 0 0 0 3px rgba(235,181,27,0.09);
        }

        .tk-mine-filter-control select option {
          color: #f7f7f8;
          background: #25272c;
        }

        .tk-mine-reset-filters {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          height: 40px;
          padding: 0 12px;
          border: 1px solid rgba(235,181,27,0.20);
          border-radius: 12px;
          color: rgba(235,181,27,0.86);
          background: rgba(235,181,27,0.055);
          font-size: 9px;
          font-weight: 900;
          cursor: pointer;
          white-space: nowrap;
        }

        .tk-mine-reset-filters:hover {
          border-color: rgba(235,181,27,0.38);
          background: rgba(235,181,27,0.09);
        }

        .tk-mine-table-wrap {
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 20px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.065),
              rgba(255,255,255,0.025)
            );
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.10),
            0 14px 34px rgba(0,0,0,0.12);
          backdrop-filter: blur(22px) saturate(135%);
          -webkit-backdrop-filter: blur(22px) saturate(135%);
        }

        .tk-mine-table {
          width: max-content;
          min-width: 100%;
          table-layout: auto;
          border-collapse: collapse;
        }

        .tk-mine-table th {
          padding: 11px 8px;
          color: rgba(255,255,255,0.34);
          border-bottom: 1px solid rgba(255,255,255,0.09);
          font-size: 8px;
          font-weight: 850;
          letter-spacing: 1px;
          text-align: left;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .tk-mine-table td {
          padding: 12px 8px;
          color: rgba(255,255,255,0.62);
          border-bottom: 1px solid rgba(255,255,255,0.055);
          font-size: 10px;
          vertical-align: middle;
        }

        .tk-date-cell {
          white-space: nowrap;
        }

        .tk-mine-table th,
        .tk-mine-table td {
          white-space: nowrap;
          overflow: visible;
          text-overflow: clip;
        }

        .tk-mine-table td[data-label="Ticket"] {
          min-width: 108px;
        }

        .tk-mine-table td[data-label="Data creazione"] {
          min-width: 126px;
        }

        .tk-mine-table td[data-label="Titolo"] {
          min-width: 150px;
          max-width: 210px;
        }

        .tk-mine-table td[data-label="Titolo"] strong {
          display: block;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .tk-mine-table td[data-label="Creato da"],
        .tk-mine-table td[data-label="Dest. utente"],
        .tk-mine-table td[data-label="Assegnato da"],
        .tk-mine-table td[data-label="Assegnato a"] {
          min-width: 128px;
        }

        .tk-mine-table td[data-label="Stato"] {
          min-width: 118px;
        }

        .tk-mine-table td[data-label="Chat"] {
          min-width: 90px;
        }

        .tk-mine-table td[data-label="Allegati"] {
          min-width: 68px;
        }

        .tk-edit-column {
          width: auto !important;
          min-width: 72px;
        }

        .tk-mine-table tbody tr {
          transition: background 0.2s ease;
        }

        .tk-mine-table tbody tr:hover {
          background: rgba(255,255,255,0.045);
        }

        .tk-mine-closed-separator {
          pointer-events: none;
        }

        .tk-mine-closed-separator td {
          padding: 16px 10px 10px !important;
          border-bottom: 0 !important;
          background: transparent !important;
        }

        .tk-mine-closed-separator-content {
          display: flex;
          align-items: center;
          width: 100%;
          gap: 10px;
        }

        .tk-mine-closed-separator-line {
          flex: 1 1 auto;
          height: 1px;
          background: linear-gradient(
            90deg,
            rgba(235,181,27,0.03),
            rgba(235,181,27,0.34),
            rgba(235,181,27,0.03)
          );
        }

        .tk-mine-closed-separator-label {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          flex: 0 0 auto;
          color: rgba(235,181,27,0.86);
          font-size: 9px;
          font-weight: 900;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .tk-mine-row {
          cursor: pointer;
          outline: none;
        }

        .tk-mine-row:focus-visible {
          background: rgba(235,181,27,0.07);
          box-shadow: inset 3px 0 0 ${GOLD};
        }

        .tk-attachment-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          min-width: 38px;
          min-height: 28px;
          padding: 0 8px;
          border: 1px solid rgba(235,181,27,0.20);
          border-radius: 9px;
          color: ${GOLD};
          background: rgba(235,181,27,0.07);
          font-size: 10px;
          font-weight: 800;
        }

        .tk-mine-table tbody tr:last-child td {
          border-bottom: 0;
        }

        .tk-department-table {
          min-width: 100%;
        }

        .tk-department-row.is-urgent {
          background: rgba(212,163,25,0.028);
        }

        .tk-department-row.is-urgent:hover {
          background: rgba(212,163,25,0.07);
        }

        .tk-mine-table td strong {
          display: block;
          color: rgba(255,255,255,0.90);
          font-size: 11px;
        }

        .tk-mine-title {
          max-width: 140px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          cursor: help;
        }

        .tk-table-description {
          display: -webkit-box;
          max-width: 330px;
          margin-top: 4px;
          overflow: hidden;
          color: rgba(255,255,255,0.38);
          font-size: 9px;
          line-height: 1.45;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }

        .tk-table-updated-by {
          display: block;
          margin-top: 3px;
          color: rgba(255,255,255,0.34);
          font-size: 8px;
        }

        .tk-closed-filters {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          margin-bottom: 14px;
          padding: 6px;
          overflow-x: auto;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 16px;
          background: rgba(255,255,255,0.03);
          scrollbar-width: none;
        }

        .tk-closed-filters::-webkit-scrollbar {
          display: none;
        }

        .tk-closed-searchbar {
          display: grid;
          grid-template-columns: minmax(260px, 1fr) auto auto auto;
          align-items: end;
          gap: 10px;
          width: 100%;
          margin: -4px 0 14px;
        }

        .tk-closed-search {
          display: flex;
          align-items: center;
          gap: 8px;
          min-height: 42px;
          padding: 0 11px;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 13px;
          color: rgba(255,255,255,0.42);
          background: rgba(255,255,255,0.035);
          transition: border-color .18s ease, background .18s ease;
        }

        .tk-closed-search:focus-within {
          border-color: rgba(235,181,27,0.32);
          background: rgba(235,181,27,0.045);
        }

        .tk-closed-search input {
          flex: 1;
          min-width: 0;
          border: 0;
          outline: 0;
          color: rgba(255,255,255,0.88);
          background: transparent;
          font-size: 10px;
          font-weight: 700;
        }

        .tk-closed-search input::placeholder {
          color: rgba(255,255,255,0.30);
        }

        .tk-closed-search-clear {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 25px;
          height: 25px;
          padding: 0;
          border: 0;
          border-radius: 8px;
          color: rgba(255,255,255,0.52);
          background: rgba(255,255,255,0.06);
          cursor: pointer;
        }

        .tk-closed-date-control {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .tk-closed-date-control > span {
          padding-left: 3px;
          color: rgba(255,255,255,0.34);
          font-size: 8px;
          font-weight: 850;
          letter-spacing: .7px;
          text-transform: uppercase;
        }

        .tk-closed-date-control input {
          min-height: 42px;
          padding: 0 10px;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 13px;
          outline: 0;
          color: rgba(255,255,255,0.76);
          color-scheme: dark;
          background: rgba(255,255,255,0.035);
          font-size: 10px;
          font-weight: 750;
        }

        .tk-closed-date-control input:focus {
          border-color: rgba(235,181,27,0.32);
        }

        .tk-closed-reset {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          min-height: 42px;
          padding: 0 12px;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 13px;
          color: rgba(255,255,255,0.56);
          background: rgba(255,255,255,0.035);
          font-size: 9px;
          font-weight: 850;
          cursor: pointer;
          white-space: nowrap;
        }

        .tk-closed-reset:hover {
          color: rgba(255,255,255,0.84);
          background: rgba(255,255,255,0.06);
        }

        .tk-closed-filter {
          display: inline-flex;
          flex: 0 0 auto;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 38px;
          padding: 0 12px;
          border: 1px solid transparent;
          border-radius: 11px;
          color: rgba(255,255,255,0.48);
          background: transparent;
          font-size: 10px;
          font-weight: 800;
          cursor: pointer;
          transition:
            color .18s ease,
            background .18s ease,
            border-color .18s ease,
            box-shadow .18s ease;
        }

        .tk-closed-filter b {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 22px;
          height: 22px;
          padding: 0 6px;
          border-radius: 999px;
          color: rgba(255,255,255,0.62);
          background: rgba(255,255,255,0.07);
          font-size: 9px;
          font-weight: 900;
        }

        .tk-closed-filter:hover {
          color: rgba(255,255,255,0.80);
          background: rgba(255,255,255,0.045);
        }

        .tk-closed-filter.is-active {
          border-color: rgba(235,181,27,0.22);
          color: ${GOLD};
          background: rgba(235,181,27,0.09);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06);
        }

        .tk-closed-filter.is-active b {
          color: #17181b;
          background: ${GOLD};
        }

        .tk-closed-table-wrap {
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 20px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.065),
              rgba(255,255,255,0.025)
            );
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.10),
            0 14px 34px rgba(0,0,0,0.12);
          backdrop-filter: blur(22px) saturate(135%);
          -webkit-backdrop-filter: blur(22px) saturate(135%);
        }

        /* =========================================================
           TABELLE A 7 RIGHE VISIBILI

           Usata solamente in:
           - Stato dei miei ticket
           - Ticket chiusi

           Il contenitore resta alto uguale e, dopo le prime 7 righe,
           lo scroll avviene all'interno della tabella invece che sulla pagina.
        ========================================================= */
        .tk-seven-row-scroll {
height: 475px;
max-height: 475px;
          overflow-x: auto;
          overflow-y: auto;
          overscroll-behavior: contain;
          scrollbar-gutter: stable;
        }

        /* Scrollbar compatta per non rubare spazio alle 7 righe. */
        .tk-seven-row-scroll::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .tk-seven-row-scroll::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.025);
        }

        .tk-seven-row-scroll::-webkit-scrollbar-thumb {
          border: 2px solid transparent;
          border-radius: 999px;
          background: rgba(255,255,255,0.18);
          background-clip: padding-box;
        }

        .tk-seven-row-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(235,181,27,0.40);
          background-clip: padding-box;
        }

        /* L'intestazione resta visibile mentre scorri le righe. */
        .tk-seven-row-scroll .tk-mine-table thead th,
        .tk-seven-row-scroll .tk-closed-table thead th {
          position: sticky;
          top: 0;
          z-index: 8;
          height: 34px;
          box-sizing: border-box;
          background: rgba(27,28,32,0.98);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
        }

        /* Altezza uniforme delle righe: 7 ticket occupano sempre lo stesso spazio. */
        .tk-seven-row-scroll .tk-mine-table .tk-mine-row td,
        .tk-seven-row-scroll .tk-closed-table .tk-closed-row td {
          height: 44px;
          padding-top: 8px;
          padding-bottom: 8px;
          box-sizing: border-box;
        }

        .tk-closed-table {
          width: max-content;
          min-width: 100%;
          table-layout: auto;
          border-collapse: collapse;
        }

        .tk-closed-table th {
          padding: 11px 8px;
          color: rgba(255,255,255,0.34);
          border-bottom: 1px solid rgba(255,255,255,0.09);
          font-size: 8px;
          font-weight: 850;
          letter-spacing: 1px;
          text-align: left;
          text-transform: uppercase;
        }

        .tk-closed-table td {
          padding: 12px 8px;
          color: rgba(255,255,255,0.62);
          border-bottom: 1px solid rgba(255,255,255,0.055);
          font-size: 10px;
          vertical-align: middle;
        }

        .tk-closed-table tbody tr {
          transition: background 0.2s ease;
        }

        .tk-closed-table tbody tr:hover {
          background: rgba(255,255,255,0.045);
        }

        .tk-closed-row { cursor: pointer; }

        .tk-closed-row:focus-visible {
          outline: 1px solid rgba(235,181,27,0.45);
          outline-offset: -1px;
          background: rgba(235,181,27,0.055);
        }

        .tk-closed-table tbody tr:last-child td {
          border-bottom: 0;
        }

        .tk-closed-table td strong {
          color: rgba(255,255,255,0.88);
          font-size: 11px;
        }

        .tk-closed-table th,
        .tk-closed-table td {
          white-space: nowrap;
          overflow: visible;
          text-overflow: clip;
        }

        .tk-closed-table td[data-label="Ticket"] {
          min-width: 108px;
        }

        .tk-closed-table td[data-label="Data creazione"],
        .tk-closed-table td[data-label="Chiuso il"] {
          min-width: 126px;
        }

        .tk-closed-table td[data-label="Titolo"] {
          min-width: 150px;
          max-width: 210px;
        }

        .tk-closed-table td[data-label="Titolo"] strong {
          display: block;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .tk-closed-table td[data-label="Chat"] {
          min-width: 90px;
        }

        .tk-closed-table td[data-label="Allegati"] {
          min-width: 68px;
        }

        .tk-table-code {
          display: inline-flex;
          color: ${GOLD};
          font-size: 9px;
          font-weight: 800;
        }

        .tk-code-copy-wrap {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          min-width: 0;
        }

        .tk-code-copy-value {
          min-width: 0;
          white-space: nowrap;
        }

        .tk-code-urgent .tk-code-copy-value {
          color: #d7a928;
          font-weight: 900;
          text-shadow: 0 0 14px rgba(215,169,40,0.14);
        }

        .tk-code-normal .tk-code-copy-value {
          color: #ffe45c;
          font-weight: 850;
        }

        .tk-code-urgent > svg {
          color: #d7a928;
        }

        .tk-code-normal > svg {
          color: #ffe45c;
        }

        .tk-code-copy-button {
          flex: 0 0 auto;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;
          padding: 0;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 7px;
          color: rgba(255,255,255,0.48);
          background: rgba(255,255,255,0.035);
          cursor: pointer;
          transition:
            color 0.16s ease,
            border-color 0.16s ease,
            background 0.16s ease;
        }

        .tk-code-copy-button:hover {
          color: ${GOLD};
          border-color: rgba(235,181,27,0.34);
          background: rgba(235,181,27,0.075);
        }

        .tk-code-copy-button.is-copied {
          color: #202227;
          border-color: ${GOLD};
          background: ${GOLD};
        }

        .tk-ticket-id .tk-code-copy-button {
          width: 20px;
          height: 20px;
        }

        .tk-modal-ticket-code {
          color: ${GOLD};
        }

        .tk-table-urgent {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin-top: 4px;
          color: ${GOLD};
          font-size: 8px;
          font-weight: 800;
        }

        .tk-ticket-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }

        .tk-ticket-card {
          position: relative;
          min-width: 0;
          padding: 16px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 20px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.072),
              rgba(255,255,255,0.029)
            );
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.10),
            0 12px 30px rgba(0,0,0,0.10);
          transition:
            border-color 0.22s ease,
            background 0.22s ease;
        }

        .tk-ticket-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 8%;
          width: 45%;
          height: 1px;
          pointer-events: none;
          background:
            linear-gradient(
              90deg,
              rgba(255,255,255,0.30),
              transparent
            );
        }

        .tk-ticket-card:hover {
          border-color: rgba(255,255,255,0.17);
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.09),
              rgba(255,255,255,0.038)
            );
        }

        .tk-ticket-card.is-urgent {
          border-color: rgba(235,181,27,0.24);
          background:
            linear-gradient(
              145deg,
              rgba(235,181,27,0.075),
              rgba(255,255,255,0.03)
            );
        }

        .tk-ticket-topline {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 12px;
        }

        .tk-ticket-id {
          display: flex;
          align-items: center;
          gap: 6px;
          min-width: 0;
          color: rgba(255,255,255,0.32);
          font-size: 8px;
          font-weight: 750;
          letter-spacing: 0.5px;
        }

        .tk-badges {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex-wrap: wrap;
          gap: 5px;
        }

        .tk-edit-assignment-button {
          width: 30px;
          height: 30px;
          border-radius: 9px;
          border: 1px solid rgba(235, 181, 27, 0.34);
          background: rgba(235, 181, 27, 0.09);
          color: #8a6700;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          cursor: pointer;
          flex: 0 0 auto;
          transition:
            transform 0.16s ease,
            border-color 0.16s ease,
            background 0.16s ease;
        }

        .tk-edit-assignment-button:hover {
          transform: translateY(-1px);
          border-color: rgba(235, 181, 27, 0.72);
          background: rgba(235, 181, 27, 0.17);
        }

        .tk-edit-assignment-button:focus-visible {
          outline: 2px solid rgba(235, 181, 27, 0.45);
          outline-offset: 2px;
        }

        .tk-quick-close-button {
          width: 30px;
          height: 30px;
          border-radius: 9px;
          border: 1px solid rgba(239, 93, 93, 0.3);
          background: rgba(239, 93, 93, 0.08);
          color: #ef6b6b;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          cursor: pointer;
          flex: 0 0 auto;
          transition:
            transform 0.16s ease,
            border-color 0.16s ease,
            background 0.16s ease;
        }

        .tk-quick-close-button:hover {
          transform: translateY(-1px);
          border-color: rgba(239, 93, 93, 0.68);
          background: rgba(239, 93, 93, 0.15);
        }

        .tk-quick-close-button:disabled {
          opacity: 0.42;
          cursor: not-allowed;
          transform: none;
        }

        .tk-quick-close-button:focus-visible {
          outline: 2px solid rgba(239, 93, 93, 0.38);
          outline-offset: 2px;
        }

        .tk-ticket-row-actions {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          white-space: nowrap;
        }

        .tk-quick-close-confirm-icon {
          color: #ef6b6b;
          border-color: rgba(239, 93, 93, 0.3);
          background: rgba(239, 93, 93, 0.08);
        }

        .tk-edit-column {
          width: 78px;
          min-width: 78px;
          text-align: center;
        }

        .tk-edit-assignment-table {
          margin: 0 auto;
        }

        .tk-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          min-height: 23px;
          padding: 0 7px;
          border-radius: 999px;
          font-size: 8px;
          font-weight: 800;
          white-space: nowrap;
        }

        .tk-badge-urgent {
          color: ${GOLD};
          border: 1px solid rgba(235,181,27,0.23);
          background: rgba(235,181,27,0.08);
        }

        .tk-status {
          color: rgba(255,255,255,0.62);
          border: 1px solid rgba(255,255,255,0.10);
          background: rgba(255,255,255,0.045);
        }

        .tk-status-assegnato {
          color: rgba(170,205,255,0.86);
          border-color: rgba(120,170,255,0.18);
          background: rgba(90,140,230,0.07);
        }

        .tk-status-in_lavorazione {
          color: rgba(255,221,120,0.82);
          border-color: rgba(235,181,27,0.15);
          background: rgba(235,181,27,0.05);
        }

        .tk-status-chiuso {
          color: rgba(165,225,180,0.78);
          border-color: rgba(100,190,120,0.16);
          background: rgba(60,155,85,0.07);
        }

        .tk-ticket-card h3 {
          margin: 0 0 7px;
          color: rgba(255,255,255,0.90);
          font-size: 14px;
          font-weight: 700;
          line-height: 1.35;
          letter-spacing: -0.2px;
        }

        .tk-ticket-description {
          margin: 0;
          color: rgba(255,255,255,0.50);
          font-size: 10px;
          line-height: 1.65;
          white-space: pre-wrap;
          overflow-wrap: anywhere;
        }

        .tk-image-preview {
          position: relative;
          display: block;
          width: 100%;
          height: 130px;
          margin-top: 12px;
          padding: 0;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 15px;
          background: rgba(0,0,0,0.13);
          cursor: pointer;
        }

        .tk-image-preview img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0.82;
          transition: opacity 0.2s ease;
        }

        .tk-image-preview:hover img {
          opacity: 0.98;
        }

        .tk-image-preview.is-file {
          min-height: 112px;
          padding: 0;
        }

        .tk-file-preview-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          min-height: 112px;
          color: ${GOLD};
          background:
            radial-gradient(circle at 50% 45%, rgba(235,181,27,0.12), transparent 52%),
            rgba(255,255,255,0.025);
        }

        .tk-image-preview span {
          position: absolute;
          right: 8px;
          bottom: 8px;
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 5px 8px;
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 999px;
          color: rgba(255,255,255,0.84);
          background: rgba(20,21,24,0.65);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          font-size: 8px;
          font-weight: 700;
        }

        .tk-ticket-meta {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px 13px;
          margin-top: 14px;
          padding-top: 12px;
          border-top: 1px solid rgba(255,255,255,0.07);
        }

        .tk-ticket-meta > div {
          display: flex;
          align-items: center;
          gap: 5px;
          min-width: 0;
          color: rgba(255,255,255,0.32);
          font-size: 8px;
        }

        .tk-ticket-meta b {
          color: rgba(255,255,255,0.57);
          font-weight: 700;
        }

        .tk-ticket-meta-full > div {
          padding: 6px 8px;
          border: 1px solid rgba(255,255,255,0.055);
          border-radius: 10px;
          background: rgba(255,255,255,0.025);
        }

        .tk-assignment-audit {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 9px;
          margin-top: 14px;
        }

        .tk-assignment-audit > div {
          display: flex;
          min-width: 0;
          flex-direction: column;
          gap: 5px;
          padding: 11px 12px;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 13px;
          background: rgba(255,255,255,0.03);
        }

        .tk-assignment-audit span {
          color: rgba(255,255,255,0.32);
          font-size: 8px;
          font-weight: 800;
          letter-spacing: 0.7px;
          text-transform: uppercase;
        }

        .tk-assignment-audit strong {
          overflow: hidden;
          color: rgba(255,255,255,0.78);
          font-size: 10px;
          font-weight: 720;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .tk-assignment-panel {
          margin-top: 14px;
          padding: 15px;
          border: 1px solid rgba(235,181,27,0.18);
          border-radius: 17px;
          background:
            linear-gradient(145deg, rgba(235,181,27,0.075), rgba(255,255,255,0.025));
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.07);
        }

        .tk-assignment-heading {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 13px;
        }

        .tk-assignment-heading h3 {
          margin: 4px 0 0;
          color: rgba(255,255,255,0.9);
          font-size: 14px;
        }

        .tk-assignment-heading p {
          margin: 0;
          color: rgba(255,255,255,0.38);
          font-size: 9px;
        }

        .tk-assignment-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }

        .tk-assignment-grid .tk-field {
          margin-bottom: 0;
        }

        .tk-assign-submit {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 40px;
          margin-top: 12px;
          padding: 0 15px;
          border: 1px solid rgba(235,181,27,0.36);
          border-radius: 12px;
          color: #191a1d;
          background: ${GOLD};
          font-size: 10px;
          font-weight: 850;
          cursor: pointer;
          transition: transform 0.18s ease, opacity 0.18s ease;
        }

        .tk-assign-submit:hover:not(:disabled) {
          transform: translateY(-1px);
        }

        .tk-assign-submit:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .tk-status-editor {
          margin-top: 13px;
          padding-top: 12px;
          border-top: 1px solid rgba(255,255,255,0.07);
        }

        .tk-status-editor > span {
          display: block;
          margin-bottom: 7px;
          color: rgba(255,255,255,0.32);
          font-size: 8px;
          font-weight: 800;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .tk-status-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .tk-status-choice {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          min-height: 31px;
          padding: 0 9px;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 10px;
          color: rgba(255,255,255,0.43);
          background: rgba(255,255,255,0.035);
          font-size: 8px;
          font-weight: 700;
          cursor: pointer;
          transition:
            color 0.2s ease,
            border-color 0.2s ease,
            background 0.2s ease;
        }

        .tk-status-choice:hover {
          color: rgba(255,255,255,0.75);
          border-color: rgba(255,255,255,0.16);
          background: rgba(255,255,255,0.06);
        }

        .tk-status-choice:disabled {
          cursor: default;
          opacity: 0.58;
        }

        .tk-status-choice.is-active:disabled {
          opacity: 1;
        }

        .tk-status-choice.is-active {
          color: ${GOLD};
          border-color: rgba(235,181,27,0.25);
          background: rgba(235,181,27,0.075);
        }

        .tk-last-update {
          margin-top: 9px;
          color: rgba(255,255,255,0.27);
          font-size: 7.5px;
        }

        .tk-last-update b {
          color: rgba(255,255,255,0.45);
          font-weight: 650;
        }

        .tk-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 160px;
          padding: 25px;
          border: 1px dashed rgba(255,255,255,0.10);
          border-radius: 18px;
          color: rgba(255,255,255,0.22);
          text-align: center;
        }

        .tk-empty strong {
          margin-top: 10px;
          color: rgba(255,255,255,0.50);
          font-size: 11px;
        }

        .tk-empty span {
          max-width: 430px;
          margin-top: 4px;
          color: rgba(255,255,255,0.30);
          font-size: 9px;
          line-height: 1.5;
        }

        .tk-compose-layout {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 285px;
          gap: 16px;
          align-items: start;
        }

        .tk-compose-card,
        .tk-compose-side {
          border: 1px solid rgba(255,255,255,0.13);
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.09),
              rgba(255,255,255,0.034)
            );
          box-shadow:
            0 24px 65px rgba(0,0,0,0.19),
            inset 0 1px 0 rgba(255,255,255,0.14);
          backdrop-filter: blur(27px) saturate(135%);
          -webkit-backdrop-filter: blur(27px) saturate(135%);
        }

        .tk-compose-card {
          padding: 24px;
          border-radius: 27px;
        }

        .tk-compose-header {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 22px;
        }

        .tk-compose-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 54px;
          height: 54px;
          flex: 0 0 auto;
          border: 1px solid rgba(235,181,27,0.24);
          border-radius: 17px;
          color: ${GOLD};
          background:
            linear-gradient(
              145deg,
              rgba(235,181,27,0.15),
              rgba(235,181,27,0.045)
            );
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.12);
        }

        .tk-compose-header span {
          color: rgba(255,255,255,0.31);
          font-size: 8px;
          font-weight: 800;
          letter-spacing: 1.4px;
          text-transform: uppercase;
        }

        .tk-compose-header h2 {
          margin: 1px 0 0;
          color: #f7f7f8;
          font-size: 21px;
          font-weight: 650;
          letter-spacing: -0.5px;
        }

        .tk-compose-header p {
          margin: 3px 0 0;
          color: rgba(255,255,255,0.38);
          font-size: 9px;
        }

        .tk-form-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 11px;
        }

        .tk-field {
          position: relative;
          display: block;
          margin-bottom: 13px;
        }

        .tk-field > span {
          display: flex;
          align-items: center;
          gap: 6px;
          margin: 0 0 7px 2px;
          color: rgba(255,255,255,0.47);
          font-size: 9px;
          font-weight: 700;
        }

        .tk-field input,
        .tk-field select,
        .tk-field textarea {
          width: 100%;
          outline: none;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 14px;
          color: rgba(255,255,255,0.86);
          background: rgba(255,255,255,0.045);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06);
          font: inherit;
          font-size: 11px;
          transition:
            border-color 0.2s ease,
            background 0.2s ease,
            box-shadow 0.2s ease;
        }

        .tk-field input,
        .tk-field select {
          height: 45px;
          padding: 0 13px;
        }

        .tk-field textarea {
          min-height: 145px;
          resize: vertical;
          padding: 12px 13px 25px;
          line-height: 1.6;
        }

        .tk-field select option {
          color: #f7f7f8;
          background: #25272b;
        }

        .tk-field input::placeholder,
        .tk-field textarea::placeholder {
          color: rgba(255,255,255,0.24);
        }

        .tk-field input:focus,
        .tk-field select:focus,
        .tk-field textarea:focus {
          border-color: rgba(235,181,27,0.36);
          background: rgba(255,255,255,0.065);
          box-shadow:
            0 0 0 3px rgba(235,181,27,0.045),
            inset 0 1px 0 rgba(255,255,255,0.08);
        }

        .tk-field select:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .tk-field > small {
          position: absolute;
          right: 9px;
          bottom: 7px;
          color: rgba(255,255,255,0.21);
          font-size: 7px;
        }

        .tk-compose-options {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          margin-top: 2px;
        }

        .tk-urgent-toggle,
        .tk-attachment {
          display: flex;
          align-items: center;
          gap: 10px;
          min-height: 65px;
          padding: 11px;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 15px;
          background: rgba(255,255,255,0.034);
          cursor: pointer;
        }

        .tk-urgent-toggle > input,
        .tk-attachment > input {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }

        .tk-switch {
          position: relative;
          width: 36px;
          height: 21px;
          flex: 0 0 auto;
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 999px;
          background: rgba(255,255,255,0.07);
          transition:
            background 0.2s ease,
            border-color 0.2s ease;
        }

        .tk-switch::after {
          content: "";
          position: absolute;
          top: 3px;
          left: 3px;
          width: 13px;
          height: 13px;
          border-radius: 50%;
          background: rgba(255,255,255,0.62);
          transition:
            transform 0.2s ease,
            background 0.2s ease;
        }

        .tk-urgent-toggle > input:checked + .tk-switch {
          border-color: rgba(235,181,27,0.35);
          background: rgba(235,181,27,0.16);
        }

        .tk-urgent-toggle > input:checked + .tk-switch::after {
          transform: translateX(15px);
          background: ${GOLD};
        }

        .tk-urgent-toggle > div,
        .tk-attachment > div {
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .tk-urgent-toggle b,
        .tk-attachment b {
          display: flex;
          align-items: center;
          gap: 5px;
          overflow: hidden;
          color: rgba(255,255,255,0.68);
          font-size: 9px;
          font-weight: 700;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .tk-urgent-toggle small,
        .tk-attachment small {
          margin-top: 2px;
          color: rgba(255,255,255,0.27);
          font-size: 7.5px;
          line-height: 1.35;
        }

        .tk-attachment > svg {
          flex: 0 0 auto;
          color: ${GOLD};
        }

        .tk-compose-image {
          position: relative;
          width: 180px;
          height: 112px;
          margin-top: 12px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.11);
          border-radius: 15px;
          background: rgba(0,0,0,0.15);
        }

        .tk-compose-image.is-file {
          width: min(100%, 340px);
          height: auto;
          min-height: 72px;
        }

        .tk-local-file-info {
          display: flex;
          align-items: center;
          gap: 9px;
          min-width: 0;
          width: 100%;
          min-height: 70px;
          padding: 14px 48px 14px 14px;
          color: rgba(255,255,255,0.78);
        }

        .tk-local-file-info > svg {
          flex: 0 0 auto;
          color: ${GOLD};
        }

        .tk-local-file-info span {
          min-width: 0;
          overflow: hidden;
          font-size: 11px;
          font-weight: 700;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .tk-compose-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .tk-compose-image button {
          position: absolute;
          top: 7px;
          right: 7px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 29px;
          height: 29px;
          padding: 0;
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 10px;
          color: #fff;
          background: rgba(20,20,23,0.72);
          cursor: pointer;
        }

        .tk-form-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          margin-top: 21px;
          padding-top: 17px;
          border-top: 1px solid rgba(255,255,255,0.07);
        }

        .tk-btn-primary,
        .tk-btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 40px;
          padding: 0 15px;
          border-radius: 999px;
          font-size: 9px;
          font-weight: 750;
          cursor: pointer;
          transition:
            background 0.2s ease,
            border-color 0.2s ease,
            color 0.2s ease;
        }

        .tk-btn-secondary {
          border: 1px solid rgba(255,255,255,0.11);
          color: rgba(255,255,255,0.46);
          background: rgba(255,255,255,0.035);
        }

        .tk-btn-primary {
          border: 1px solid rgba(235,181,27,0.44);
          color: ${GOLD};
          background: rgba(235,181,27,0.08);
        }

        .tk-btn-secondary:hover {
          color: rgba(255,255,255,0.76);
          background: rgba(255,255,255,0.06);
        }

        .tk-btn-primary:hover {
          color: #fff;
          border-color: ${GOLD};
          background: rgba(235,181,27,0.14);
        }

        .tk-btn-primary:disabled {
          opacity: 0.45;
          cursor: wait;
        }

        .tk-compose-side {
          position: sticky;
          top: 20px;
          padding: 19px;
          border-radius: 23px;
        }

        .tk-compose-side > span {
          display: block;
          margin-bottom: 12px;
          color: rgba(255,255,255,0.34);
          font-size: 8px;
          font-weight: 800;
          letter-spacing: 1.3px;
          text-transform: uppercase;
        }

        .tk-summary-row {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 11px 0;
          border-bottom: 1px solid rgba(255,255,255,0.065);
        }

        .tk-summary-row:last-child {
          border-bottom: 0;
        }

        .tk-summary-row > svg {
          flex: 0 0 auto;
          color: ${GOLD};
        }

        .tk-summary-row > div {
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .tk-summary-row small {
          color: rgba(255,255,255,0.27);
          font-size: 7.5px;
        }

        .tk-summary-row strong {
          margin-top: 2px;
          overflow: hidden;
          color: rgba(255,255,255,0.65);
          font-size: 9px;
          font-weight: 700;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .tk-stats-inline {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex-wrap: wrap;
          gap: 6px;
        }

        .tk-stats-inline span {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          min-height: 31px;
          padding: 0 9px;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 10px;
          color: rgba(255,255,255,0.42);
          background: rgba(255,255,255,0.035);
          font-size: 8px;
          white-space: nowrap;
        }

        .tk-reply-box {
          margin-top: 16px;
          padding: 14px;
          border: 1px solid rgba(235,181,27,0.18);
          border-radius: 15px;
          background: rgba(235,181,27,0.055);
        }

        .tk-reply-heading {
          display: flex;
          align-items: center;
          gap: 7px;
          color: ${GOLD};
          font-size: 12px;
        }

        .tk-reply-box p {
          margin: 10px 0 0;
          color: rgba(255,255,255,0.84);
          font-size: 13px;
          line-height: 1.65;
          white-space: pre-wrap;
          overflow-wrap: anywhere;
        }

        .tk-reply-meta {
          margin-top: 8px;
          color: rgba(255,255,255,0.46);
          font-size: 11px;
        }

        .tk-reply-attachment-button,
        .tk-reply-toggle {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 38px;
          margin-top: 10px;
          padding: 0 12px;
          border: 1px solid rgba(255,255,255,0.13);
          border-radius: 11px;
          color: rgba(255,255,255,0.78);
          background: rgba(255,255,255,0.05);
          font-size: 11px;
          font-weight: 750;
          cursor: pointer;
        }

        .tk-reply-editor {
          margin-top: 14px;
          padding-top: 13px;
          border-top: 1px solid rgba(255,255,255,0.07);
        }

        .tk-reply-form {
          display: grid;
          gap: 10px;
          margin-top: 10px;
        }

        .tk-reply-form textarea {
          width: 100%;
          min-height: 110px;
          resize: vertical;
          padding: 12px 13px;
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 13px;
          outline: none;
          color: #fff;
          background: rgba(0,0,0,0.16);
          font: inherit;
          font-size: 13px;
          line-height: 1.55;
        }

        .tk-reply-actions-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          flex-wrap: wrap;
        }

        .tk-reply-file {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          min-height: 38px;
          padding: 0 12px;
          border: 1px solid rgba(255,255,255,0.11);
          border-radius: 11px;
          color: rgba(255,255,255,0.70);
          background: rgba(255,255,255,0.04);
          font-size: 11px;
          cursor: pointer;
          overflow: hidden;
        }

        .tk-reply-file input {
          position: absolute;
          inset: 0;
          opacity: 0;
          cursor: pointer;
        }

        .tk-reply-save {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 38px;
          padding: 0 13px;
          border: 1px solid rgba(235,181,27,0.28);
          border-radius: 11px;
          color: #202227;
          background: ${GOLD};
          font-size: 11px;
          font-weight: 850;
          cursor: pointer;
        }

        .tk-reply-save:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .tk-reply-local-preview {
          position: relative;
          width: 190px;
          height: 120px;
          overflow: hidden;
          border-radius: 13px;
          border: 1px solid rgba(255,255,255,0.11);
        }

        .tk-reply-local-preview.is-file {
          width: min(100%, 360px);
          height: auto;
          min-height: 64px;
        }

        .tk-reply-local-preview.is-file .tk-local-file-info {
          min-height: 62px;
          padding: 12px 45px 12px 12px;
        }

        .tk-reply-local-preview img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .tk-reply-local-preview button {
          position: absolute;
          top: 7px;
          right: 7px;
          display: grid;
          place-items: center;
          width: 28px;
          height: 28px;
          padding: 0;
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 9px;
          color: #fff;
          background: rgba(15,16,18,0.75);
          cursor: pointer;
        }

        /* Leggibilità: dimensioni minime più comode su desktop e mobile. */
        .tk-eyebrow { font-size: 11px; }
        .tk-user-glass span { font-size: 10px; }
        .tk-user-glass strong { font-size: 14px; }
        .tk-user-glass small { font-size: 11px; }
        .tk-warning-banner, .tk-notice, .tk-danger-notice, .tk-error { font-size: 12px; }
        .tk-tab { font-size: 12px; min-height: 46px; }
        .tk-tab b { font-size: 10px; }
        .tk-section-kicker { font-size: 10px; }
        .tk-section-heading p { font-size: 12px; }
        .tk-ticket-id { font-size: 11px; }
        .tk-badge { font-size: 10px; }
        .tk-ticket-card h3 { font-size: 17px; line-height: 1.3; }
        .tk-ticket-description { font-size: 13px; line-height: 1.7; }
        .tk-image-preview span { font-size: 10px; }
        .tk-ticket-meta > div { font-size: 11px; }
        .tk-status-editor > span { font-size: 10px; }
        .tk-status-choice { font-size: 11px; min-height: 36px; padding: 0 11px; }
        .tk-last-update { font-size: 11px; }
        .tk-compose-header span { font-size: 10px; }
        .tk-compose-header p { font-size: 12px; line-height: 1.5; }
        .tk-field > span { font-size: 11px; }
        .tk-field input, .tk-field select, .tk-field textarea { font-size: 13px; }
        .tk-btn-primary, .tk-btn-secondary { font-size: 11px; min-height: 44px; }
        .tk-compose-side > span { font-size: 10px; }
        .tk-urgent-toggle b, .tk-attachment b { font-size: 12px; }
        .tk-urgent-toggle small, .tk-attachment small { font-size: 10px; }
        .tk-summary-row small { font-size: 10px; }
        .tk-summary-row strong { font-size: 11px; }
        .tk-stats-inline span { font-size: 10px; }

        .tk-open-chat {
          position: relative;
          width: 100%;
          min-height: 42px;
          margin-top: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          border: 1px solid rgba(235,181,27,0.24);
          border-radius: 14px;
          color: #f8e6a3;
          background: rgba(235,181,27,0.075);
          font-size: 10px;
          font-weight: 800;
          cursor: pointer;
          transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s ease;
        }

        .tk-open-chat:hover {
          border-color: rgba(235,181,27,0.42);
          background: rgba(235,181,27,0.12);
        }

        .tk-open-chat:active {
          transform: scale(0.99);
        }

        .tk-open-chat-badge {
          position: absolute;
          top: -8px;
          right: -8px;
          min-width: 20px;
          height: 20px;
          display: grid;
          place-items: center;
          padding: 0 5px;
          border: 2px solid #202227;
          border-radius: 999px;
          color: #202227;
          background: ${GOLD};
          box-shadow: 0 7px 18px rgba(0,0,0,0.28), 0 0 16px rgba(235,181,27,0.16);
          font-size: 8px;
          font-weight: 950;
          line-height: 1;
        }

        .tk-table-chat-button {
          position: relative;
          display: inline-flex;
          flex: 0 0 auto;
          align-items: center;
          justify-content: center;
          gap: 6px;
          min-width: 58px;
          height: 32px;
          padding: 0 10px;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 11px;
          color: rgba(255,255,255,0.60);
          background: rgba(255,255,255,0.045);
          font-size: 10px;
          font-weight: 900;
          cursor: pointer;
          transition:
            transform 0.18s ease,
            color 0.18s ease,
            border-color 0.18s ease,
            background 0.18s ease;
        }

        .tk-table-chat-button:hover {
          transform: translateY(-1px);
          color: ${GOLD};
          border-color: rgba(235,181,27,0.30);
          background: rgba(235,181,27,0.08);
        }

        .tk-table-chat-button.has-unread {
          color: #202227;
          border-color: ${GOLD};
          background: ${GOLD};
          box-shadow: 0 7px 20px rgba(235,181,27,0.18);
        }

        .tk-table-chat-button.has-unread::after {
          content: "";
          position: absolute;
          top: -3px;
          right: -3px;
          width: 8px;
          height: 8px;
          border: 2px solid #202227;
          border-radius: 50%;
          background: #ff5d5d;
        }

        .tk-chat-cell-state {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-wrap: nowrap;
        }

        .tk-chat-waiting-reply {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          margin-left: 5px;
          color: ${GOLD};
          line-height: 1;
          vertical-align: middle;
          filter: drop-shadow(0 0 4px rgba(235,181,27,0.18));
        }

        .tk-table-unread {
          width: fit-content;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin-top: 5px;
          padding: 3px 6px;
          border: 1px solid rgba(235,181,27,0.24);
          border-radius: 999px;
          color: ${GOLD};
          background: rgba(235,181,27,0.075);
          font-size: 7px;
          font-weight: 900;
          white-space: nowrap;
        }

        .tk-chat-panel {
          margin-top: 16px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 22px;
          background: linear-gradient(145deg, rgba(255,255,255,0.07), rgba(255,255,255,0.025));
          box-shadow: 0 22px 60px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.10);
          backdrop-filter: blur(24px) saturate(135%);
          -webkit-backdrop-filter: blur(24px) saturate(135%);
        }

        .tk-chat-heading {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
          padding: 17px 18px 14px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }

        .tk-chat-heading h3 {
          margin: 0;
          color: rgba(255,255,255,0.92);
          font-size: 16px;
          font-weight: 720;
          letter-spacing: -0.25px;
        }

        .tk-chat-heading p {
          margin: 3px 0 0;
          color: rgba(255,255,255,0.36);
          font-size: 9px;
        }

        .tk-chat-heading-actions {
          display: flex;
          align-items: center;
          gap: 9px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .tk-chat-status-control {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 5px 7px 5px 9px;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 999px;
          background: rgba(255,255,255,0.04);
        }

        .tk-chat-status-control > span {
          color: rgba(255,255,255,0.42);
          font-size: 8px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .65px;
        }

        .tk-chat-status-control select {
          min-width: 116px;
          border: 0;
          outline: 0;
          color: rgba(255,255,255,0.90);
          background: rgba(0,0,0,0.18);
          border-radius: 999px;
          padding: 5px 23px 5px 9px;
          font-size: 9px;
          font-weight: 760;
          cursor: pointer;
        }

        .tk-chat-status-control select:disabled {
          opacity: .62;
          cursor: default;
        }

        .tk-chat-closure-summary,
        .tk-chat-close-panel {
          margin: 0 16px 14px;
          padding: 14px;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 17px;
          background: rgba(255,255,255,0.045);
        }

        .tk-chat-closure-summary {
          border-color: rgba(105,207,134,0.20);
          background: rgba(105,207,134,0.055);
        }

        .tk-chat-closure-summary.is-missing-info {
          border-color: rgba(239,93,93,0.34);
          background: rgba(239,93,93,0.09);
        }

        .tk-chat-closure-title,
        .tk-chat-close-heading,
        .tk-chat-close-actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
        }

        .tk-chat-closure-title {
          justify-content: flex-start;
          color: #79d995;
        }

        .tk-chat-closure-summary.is-missing-info .tk-chat-closure-title {
          color: #ef6b6b;
        }

        .tk-chat-closure-title > div {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .tk-chat-closure-title strong,
        .tk-chat-close-heading strong {
          color: rgba(255,255,255,0.92);
          font-size: 12px;
        }

        .tk-chat-closure-title span {
          color: rgba(255,255,255,0.38);
          font-size: 9px;
        }

        .tk-chat-closure-summary > p {
          margin: 12px 0 0;
          color: rgba(255,255,255,0.74);
          font-size: 11px;
          line-height: 1.55;
          white-space: pre-wrap;
        }

        .tk-chat-closure-files {
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
          margin-top: 11px;
        }

        .tk-chat-closure-files button,
        .tk-chat-close-file,
        .tk-chat-close-remove-file,
        .tk-chat-close-cancel,
        .tk-chat-close-submit {
          border: 1px solid rgba(255,255,255,0.10);
          color: rgba(255,255,255,0.72);
          background: rgba(255,255,255,0.05);
          border-radius: 11px;
          font: inherit;
        }

        .tk-chat-closure-files button {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 9px;
          font-size: 9px;
          cursor: pointer;
        }

        .tk-chat-close-heading {
          align-items: flex-start;
          margin-bottom: 10px;
        }

        .tk-chat-close-heading > div {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .tk-chat-close-cancel {
          padding: 6px 9px;
          font-size: 9px;
          cursor: pointer;
        }

        .tk-chat-close-panel > textarea {
          width: 100%;
          resize: vertical;
          min-height: 86px;
          border: 1px solid rgba(255,255,255,0.10);
          outline: none;
          border-radius: 13px;
          padding: 10px 11px;
          color: rgba(255,255,255,0.90);
          background: rgba(0,0,0,0.14);
          font: inherit;
          font-size: 11px;
          line-height: 1.5;
        }

        .tk-chat-close-panel > textarea:focus {
          border-color: rgba(235,181,27,0.38);
          box-shadow: 0 0 0 3px rgba(235,181,27,0.07);
        }

        .tk-chat-close-actions {
          justify-content: flex-start;
          margin-top: 9px;
        }

        .tk-chat-close-file {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          max-width: 240px;
          padding: 8px 10px;
          cursor: pointer;
          font-size: 9px;
        }

        .tk-chat-close-file span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .tk-chat-close-file input {
          display: none;
        }

        .tk-chat-close-remove-file {
          display: inline-grid;
          place-items: center;
          width: 29px;
          height: 29px;
          padding: 0;
          cursor: pointer;
        }

        .tk-chat-close-submit {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-left: auto;
          padding: 8px 11px;
          color: #171717;
          border-color: rgba(235,181,27,0.7);
          background: ${GOLD};
          font-size: 9px;
          font-weight: 850;
          cursor: pointer;
        }

        .tk-chat-close-submit:disabled {
          opacity: .45;
          cursor: not-allowed;
        }

        .tk-chat-live {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 7px 9px;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 999px;
          color: rgba(255,255,255,0.48);
          background: rgba(255,255,255,0.04);
          font-size: 8px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.7px;
        }

        .tk-chat-live span {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #69cf86;
          box-shadow: 0 0 10px rgba(105,207,134,0.48);
        }

        .tk-chat-live.is-loading span {
          background: ${GOLD};
          box-shadow: 0 0 10px rgba(235,181,27,0.48);
          animation: tk-chat-pulse 0.9s ease-in-out infinite alternate;
        }

        @keyframes tk-chat-pulse {
          from { opacity: 0.35; }
          to { opacity: 1; }
        }

        .tk-chat-messages {
          display: flex;
          flex-direction: column;
          gap: 12px;
          height: min(390px, 46vh);
          min-height: 250px;
          max-height: 420px;
          padding: 17px;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: rgba(255,255,255,0.13) transparent;
        }

        .tk-chat-empty {
          flex: 1;
          min-height: 220px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 7px;
          color: rgba(255,255,255,0.34);
          text-align: center;
          font-size: 10px;
        }

        .tk-chat-empty strong {
          color: rgba(255,255,255,0.68);
          font-size: 12px;
        }

        .tk-chat-message-row {
          width: min(78%, 650px);
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .tk-chat-message-row.is-mine {
          align-self: flex-end;
          align-items: flex-end;
        }

        .tk-chat-message-row.is-other {
          align-self: flex-start;
          align-items: flex-start;
        }

        .tk-chat-message-meta {
          display: flex;
          align-items: center;
          gap: 7px;
          padding: 0 4px;
          color: rgba(255,255,255,0.34);
          font-size: 8px;
        }

        .tk-chat-message-meta strong {
          color: rgba(255,255,255,0.66);
          font-weight: 800;
        }

        .tk-chat-bubble {
          min-width: 80px;
          padding: 11px 12px;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 16px 16px 16px 5px;
          background: rgba(255,255,255,0.065);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.07);
        }

        .tk-chat-message-row.is-mine .tk-chat-bubble {
          border-color: rgba(235,181,27,0.25);
          border-radius: 16px 16px 5px 16px;
          background: linear-gradient(145deg, rgba(235,181,27,0.18), rgba(235,181,27,0.085));
        }

        .tk-chat-bubble p {
          margin: 0;
          color: rgba(255,255,255,0.86);
          font-size: 11px;
          line-height: 1.55;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .tk-chat-me {
          padding: 0 4px;
          color: ${GOLD};
          font-size: 8px;
          font-weight: 850;
        }

        .tk-chat-edited {
          display: block;
          margin-top: 6px;
          color: rgba(255,255,255,0.30);
          font-size: 7px;
          text-align: right;
        }

        .tk-chat-message-row.is-pending .tk-chat-bubble { opacity: 1; }

        .tk-chat-pending {
          display: inline-flex;
          align-items: center;
          justify-content: flex-end;
          gap: 4px;
          width: 100%;
          margin-top: 6px;
          color: rgba(255,255,255,0.38);
          font-size: 7px;
        }

        .tk-chat-pending svg { animation: tk-chat-spin 0.8s linear infinite; }
        @keyframes tk-chat-spin { to { transform: rotate(360deg); } }

        .tk-chat-typing-row {
          width: fit-content;
          max-width: 80%;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 4px;
          align-self: flex-start;
        }

        .tk-chat-typing-row small {
          padding-left: 5px;
          color: rgba(255,255,255,0.34);
          font-size: 7px;
        }

        .tk-chat-typing-bubble {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          min-width: 54px;
          height: 34px;
          padding: 0 12px;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 16px 16px 16px 5px;
          background: rgba(255,255,255,0.06);
        }

        .tk-chat-typing-bubble span {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: rgba(255,255,255,0.58);
          animation: tk-typing-dot 1.05s ease-in-out infinite;
        }

        .tk-chat-typing-bubble span:nth-child(2) { animation-delay: 0.14s; }
        .tk-chat-typing-bubble span:nth-child(3) { animation-delay: 0.28s; }

        @keyframes tk-typing-dot {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.38; }
          30% { transform: translateY(-4px); opacity: 1; }
        }

        .tk-chat-attachments {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 7px;
          margin-top: 8px;
        }

        .tk-chat-attachment {
          min-width: 0;
          overflow: hidden;
          display: flex;
          align-items: center;
          gap: 7px;
          padding: 6px;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 11px;
          color: rgba(255,255,255,0.72);
          background: rgba(0,0,0,0.12);
          cursor: pointer;
        }

        .tk-chat-attachment img {
          width: 42px;
          height: 42px;
          flex: 0 0 auto;
          object-fit: cover;
          border-radius: 8px;
        }

        .tk-chat-attachment > span:last-child {
          min-width: 0;
          overflow: hidden;
          font-size: 8px;
          font-weight: 700;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .tk-chat-file-icon {
          display: grid;
          place-items: center;
          width: 42px;
          height: 42px;
          flex: 0 0 auto;
          border-radius: 8px;
          color: ${GOLD};
          background: rgba(235,181,27,0.08);
        }

        .tk-chat-error {
          display: flex;
          align-items: center;
          gap: 7px;
          margin: 0 16px 12px;
          padding: 9px 10px;
          border: 1px solid rgba(255,100,100,0.18);
          border-radius: 11px;
          color: rgba(255,205,205,0.88);
          background: rgba(140,35,35,0.12);
          font-size: 9px;
        }

        .tk-chat-composer {
          padding: 12px;
          border-top: 1px solid rgba(255,255,255,0.08);
          background: rgba(0,0,0,0.08);
        }

        .tk-chat-selected-files {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-bottom: 8px;
        }

        .tk-chat-selected-file {
          max-width: 240px;
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 7px;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 9px;
          color: rgba(255,255,255,0.65);
          background: rgba(255,255,255,0.045);
          font-size: 8px;
        }

        .tk-chat-selected-file > span {
          min-width: 0;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .tk-chat-selected-file button {
          display: grid;
          place-items: center;
          width: 18px;
          height: 18px;
          flex: 0 0 auto;
          padding: 0;
          border: 0;
          border-radius: 6px;
          color: rgba(255,255,255,0.55);
          background: rgba(255,255,255,0.06);
          cursor: pointer;
        }

        .tk-chat-compose-row {
          display: flex;
          align-items: flex-end;
          gap: 8px;
        }

        .tk-chat-file-input { display: none; }

        .tk-chat-attach-button {
          display: grid;
          place-items: center;
          width: 42px;
          height: 42px;
          flex: 0 0 auto;
          padding: 0;
          border: 1px solid rgba(255,255,255,0.11);
          border-radius: 13px;
          color: rgba(255,255,255,0.58);
          background: rgba(255,255,255,0.05);
          cursor: pointer;
        }

        .tk-chat-attach-button:hover,
        .tk-chat-attach-button.is-dragging {
          color: ${GOLD};
          border-color: rgba(235,181,27,0.32);
          background: rgba(235,181,27,0.075);
        }

        .tk-chat-compose-row textarea {
          min-height: 42px;
          max-height: 120px;
          flex: 1 1 auto;
          resize: vertical;
          padding: 11px 12px;
          border: 1px solid rgba(255,255,255,0.11);
          outline: none;
          border-radius: 13px;
          color: rgba(255,255,255,0.88);
          background: rgba(255,255,255,0.05);
          font: inherit;
          font-size: 10px;
          line-height: 1.45;
        }

        .tk-chat-compose-row textarea:focus {
          border-color: rgba(235,181,27,0.32);
          box-shadow: 0 0 0 3px rgba(235,181,27,0.055);
        }

        .tk-chat-compose-row textarea::placeholder { color: rgba(255,255,255,0.26); }

        .tk-chat-send {
          min-height: 42px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          flex: 0 0 auto;
          padding: 0 14px;
          border: 1px solid rgba(235,181,27,0.38);
          border-radius: 13px;
          color: #202227;
          background: ${GOLD};
          font-size: 9px;
          font-weight: 900;
          cursor: pointer;
        }

        .tk-chat-send:disabled { opacity: 0.42; cursor: not-allowed; }

        .tk-chat-compose-hint {
          margin-top: 7px;
          color: rgba(255,255,255,0.24);
          font-size: 7px;
          text-align: right;
        }

        .tk-chat-readonly {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          padding: 13px;
          border-top: 1px solid rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.38);
          background: rgba(0,0,0,0.07);
          font-size: 9px;
        }

        .tk-chat-messages {
          scroll-behavior: smooth;
          overscroll-behavior: contain;
        }

        .tk-chat-seen {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin-top: 5px;
          color: rgba(255,255,255,0.28);
          font-size: 7px;
          line-height: 1;
        }

        .tk-chat-seen.is-seen {
          position: relative;
          color: #53bdeb;
          cursor: help;
        }

        .tk-chat-seen.is-seen[data-seen-users]:hover::after {
          content: attr(data-seen-users);
          position: absolute;
          right: 0;
          bottom: calc(100% + 7px);
          z-index: 40;
          width: max-content;
          max-width: 260px;
          padding: 7px 9px;
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 9px;
          color: rgba(255,255,255,0.92);
          background: rgba(15,17,21,0.97);
          box-shadow: 0 8px 24px rgba(0,0,0,0.32);
          font-size: 8px;
          font-weight: 700;
          line-height: 1.35;
          white-space: normal;
          pointer-events: none;
        }

        .tk-chat-close-direct {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          min-height: 34px;
          padding: 0 11px;
          border: 1px solid rgba(235,181,27,0.3);
          border-radius: 10px;
          color: ${GOLD};
          background: rgba(235,181,27,0.08);
          font-size: 8px;
          font-weight: 850;
          cursor: pointer;
        }

        .tk-chat-close-direct:hover {
          background: rgba(235,181,27,0.14);
        }

        .tk-chat-close-direct:disabled {
          opacity: 0.5;
          cursor: wait;
        }

        .tk-mine-pagination {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          width: 100%;
          margin-top: 18px;
          padding: 12px 14px;
          border: 1px solid rgba(255,255,255,0.085);
          border-radius: 16px;
          background: rgba(255,255,255,0.028);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.035);
        }

        .tk-mine-pagination-info {
          display: flex;
          align-items: baseline;
          gap: 7px;
          min-width: 0;
          color: rgba(255,255,255,0.52);
          font-size: 10px;
          font-weight: 750;
        }

        .tk-mine-pagination-info strong {
          color: ${GOLD};
          font-size: 13px;
          font-weight: 950;
        }

        .tk-mine-pagination-info small {
          color: rgba(255,255,255,0.28);
          font-size: 8px;
          font-weight: 800;
          white-space: nowrap;
        }

        .tk-mine-pagination-controls,
        .tk-page-numbers {
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .tk-page-nav,
        .tk-page-number {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          height: 34px;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 10px;
          color: rgba(255,255,255,0.65);
          background: rgba(255,255,255,0.035);
          font-size: 9px;
          font-weight: 900;
          cursor: pointer;
          transition:
            border-color 0.18s ease,
            background 0.18s ease,
            color 0.18s ease,
            transform 0.18s ease;
        }

        .tk-page-nav {
          padding: 0 10px;
        }

        .tk-page-number {
          width: 34px;
          padding: 0;
        }

        .tk-page-nav:hover:not(:disabled),
        .tk-page-number:hover:not(:disabled) {
          color: #fff;
          border-color: rgba(235,181,27,0.25);
          background: rgba(235,181,27,0.07);
        }

        .tk-page-number.is-active {
          color: #1b1b1d;
          border-color: ${GOLD};
          background: ${GOLD};
          box-shadow: 0 7px 18px rgba(235,181,27,0.16);
        }

        .tk-page-nav:disabled,
        .tk-page-number:disabled {
          opacity: 0.32;
          cursor: default;
        }

        .tk-page-ellipsis {
          display: grid;
          place-items: center;
          width: 22px;
          color: rgba(255,255,255,0.30);
          font-size: 13px;
          font-weight: 900;
        }

        .tk-mine-page-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          min-height: 120px;
          color: rgba(255,255,255,0.50);
          font-size: 10px;
          font-weight: 850;
        }

        .tk-my-closed-section {
          margin-top: 26px;
          padding-top: 22px;
          border-top: 1px solid rgba(255,255,255,0.08);
        }

        .tk-my-closed-heading {
          margin-bottom: 14px;
        }

        .tk-chat-join-strip {
          justify-content: space-between;
          gap: 12px;
          padding: 10px 12px;
        }

        .tk-chat-join-strip > div {
          display: flex;
          align-items: center;
          gap: 7px;
        }

        .tk-chat-join-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 34px;
          padding: 0 12px;
          border: 1px solid rgba(235,181,27,0.32);
          border-radius: 11px;
          color: ${GOLD};
          background: rgba(235,181,27,0.08);
          font-size: 8px;
          font-weight: 800;
          cursor: pointer;
        }

        .tk-chat-join-button:hover {
          background: rgba(235,181,27,0.13);
        }

        .tk-chat-join-modal {
          position: fixed;
          inset: 0;
          z-index: 2147483645;
          display: grid;
          place-items: center;
          padding: 18px;
          background: rgba(7,8,11,0.72);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
        }

        .tk-chat-join-card {
          width: min(390px, calc(100vw - 30px));
          padding: 22px;
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 22px;
          background: #1d1f24;
          box-shadow: 0 34px 90px rgba(0,0,0,0.52);
          text-align: center;
        }

        .tk-chat-join-icon {
          width: 50px;
          height: 50px;
          display: grid;
          place-items: center;
          margin: 0 auto 12px;
          border: 1px solid rgba(235,181,27,0.28);
          border-radius: 16px;
          color: ${GOLD};
          background: rgba(235,181,27,0.08);
        }

        .tk-chat-join-card h3 {
          margin: 0 0 8px;
          color: #fff;
          font-size: 16px;
        }

        .tk-chat-join-card p {
          margin: 0;
          color: rgba(255,255,255,0.52);
          font-size: 10px;
          line-height: 1.55;
        }

        .tk-chat-join-actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 9px;
          margin-top: 18px;
        }

        .tk-chat-join-cancel,
        .tk-chat-join-confirm {
          min-height: 40px;
          border-radius: 12px;
          font-size: 9px;
          font-weight: 850;
          cursor: pointer;
        }

        .tk-chat-join-cancel {
          border: 1px solid rgba(255,255,255,0.11);
          color: rgba(255,255,255,0.72);
          background: rgba(255,255,255,0.05);
        }

        .tk-chat-join-confirm {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          border: 1px solid rgba(235,181,27,0.38);
          color: #202227;
          background: ${GOLD};
        }

        .tk-chat-join-cancel:disabled,
        .tk-chat-join-confirm:disabled {
          opacity: 0.5;
          cursor: wait;
        }

        .tk-ticket-detail-modal {
          position: fixed;
          inset: 0;
          z-index: 2147483600;
          display: grid;
          place-items: center;
          padding: clamp(12px, 2.5vw, 30px);
          overflow: hidden;
          background: rgba(8,9,12,0.80);
          backdrop-filter: blur(24px) saturate(125%);
          -webkit-backdrop-filter: blur(24px) saturate(125%);
        }

        .tk-ticket-detail-modal-card {
          position: relative;
          width: min(860px, calc(100vw - 28px));
          max-height: calc(100vh - 28px);
          display: flex;
          flex-direction: column;
          min-height: 0;
          padding: 18px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 26px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.11),
              rgba(255,255,255,0.045)
            ),
            #1d1f24;
          box-shadow:
            0 45px 120px rgba(0,0,0,0.54),
            inset 0 1px 0 rgba(255,255,255,0.15);
          backdrop-filter: blur(34px) saturate(145%);
          -webkit-backdrop-filter: blur(34px) saturate(145%);
        }

        .tk-ticket-detail-close {
          position: absolute;
          top: 16px;
          right: 16px;
          z-index: 4;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 12px;
          color: #fff;
          background: rgba(10,11,14,0.58);
          cursor: pointer;
          transition: 0.2s ease;
        }

        .tk-ticket-detail-close:hover {
          border-color: rgba(235,181,27,0.38);
          color: ${GOLD};
          background: rgba(235,181,27,0.08);
        }

        .tk-ticket-detail-heading {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 2px 52px 14px 4px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }

        .tk-ticket-info-modal-card {
          height: auto;
          max-height: min(860px, calc(100vh - 28px));
        }

        .tk-ticket-chat-modal-card {
          width: min(820px, calc(100vw - 28px));
          height: min(720px, calc(100vh - 28px));
        }

        .tk-ticket-detail-scroll {
          flex: 1 1 auto;
          min-height: 0;
          padding: 14px 4px 4px;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: rgba(235,181,27,0.38) transparent;
        }

        .tk-ticket-detail-scroll .tk-ticket-card {
          margin: 0;
          box-shadow: none;
        }

        .tk-current-destination {
          color: ${GOLD};
          font-weight: 750;
          white-space: nowrap;
        }

        .tk-ticket-chat-only-clean {
          padding-top: 10px;
        }

        .tk-chat-modal-title {
          min-width: 0;
        }

        .tk-chat-modal-title-row {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 4px;
        }

        .tk-ticket-detail-heading h2 {
          max-width: 500px;
          margin: 7px 0 0;
          overflow: hidden;
          color: rgba(255,255,255,0.82);
          font-size: 13px;
          font-weight: 720;
          line-height: 1.35;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .tk-chat-modal-status {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex-wrap: wrap;
          gap: 5px;
        }

        .tk-chat-modal-status .tk-status-choice {
          min-height: 29px;
          padding-inline: 8px;
        }

        .tk-ticket-chat-only {
          flex: 1 1 auto;
          min-height: 0;
          padding-top: 12px;
          overflow: hidden;
        }

        .tk-ticket-chat-only .tk-chat-panel {
          height: 100%;
          min-height: 0;
          margin-top: 0;
          display: flex;
          flex-direction: column;
        }

        .tk-ticket-chat-only .tk-chat-heading {
          flex: 0 0 auto;
        }

        .tk-ticket-chat-only .tk-chat-messages {
          flex: 1 1 auto;
          height: auto;
          min-height: 0;
          max-height: none;
          overflow-y: auto;
        }

        .tk-ticket-chat-only .tk-chat-composer,
        .tk-ticket-chat-only .tk-chat-readonly,
        .tk-ticket-chat-only .tk-chat-error {
          flex: 0 0 auto;
        }

        .tk-image-modal {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 100vh;
          max-width: 100vw;
          max-height: 100vh;
          z-index: 2147483647;
          display: grid;
          place-items: center;
          margin: 0;
          padding: clamp(10px, 2vw, 24px);
          overflow: hidden;
          overscroll-behavior: none;
          box-sizing: border-box;
          background: rgba(8,9,12,0.78);
          backdrop-filter: blur(26px) saturate(125%);
          -webkit-backdrop-filter: blur(26px) saturate(125%);
        }

        .tk-image-modal-card {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          justify-content: center;
          width: min(900px, calc(100vw - 32px));
          max-width: calc(100vw - 32px);
          max-height: calc(100vh - 32px);
          margin: 0;
          padding: 14px;
          overflow: hidden;
          box-sizing: border-box;
          border: 1px solid rgba(255,255,255,0.17);
          border-radius: 25px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.13),
              rgba(255,255,255,0.055)
            );
          box-shadow:
            0 45px 120px rgba(0,0,0,0.52),
            inset 0 1px 0 rgba(255,255,255,0.20);
          backdrop-filter: blur(32px) saturate(145%);
          -webkit-backdrop-filter: blur(32px) saturate(145%);
        }

        .tk-image-modal-card > img {
          display: block;
          width: auto;
          height: auto;
          max-width: 100%;
          max-height: calc(100vh - 142px);
          margin: 0 auto;
          flex: 0 1 auto;
          min-height: 0;
          object-fit: contain;
          border-radius: 17px;
          background: rgba(0,0,0,0.19);
        }

        .tk-image-close {
          position: absolute;
          top: 21px;
          right: 21px;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 12px;
          color: #fff;
          background: rgba(15,16,18,0.65);
          cursor: pointer;
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
        }

        .tk-image-modal-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          min-width: 0;
          padding: 10px 2px 0;
        }

        .tk-image-caption {
          min-width: 0;
          overflow: hidden;
          color: rgba(255,255,255,0.44);
          font-size: 9px;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .tk-image-download {
          flex: 0 0 auto;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 38px;
          padding: 0 13px;
          border: 1px solid rgba(235,181,27,0.28);
          border-radius: 12px;
          color: ${GOLD};
          background: rgba(235,181,27,0.09);
          font-size: 10px;
          font-weight: 800;
          cursor: pointer;
          white-space: nowrap;
        }

        .tk-image-download:hover:not(:disabled) {
          background: rgba(235,181,27,0.15);
        }

        .tk-image-download:disabled {
          opacity: 0.55;
          cursor: wait;
        }

        @media (max-width: 1000px) {
          .tk-ticket-grid {
            grid-template-columns: 1fr;
          }

          .tk-compose-layout {
            grid-template-columns: 1fr;
          }

          .tk-compose-side {
            position: static;
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 0 18px;
          }

          .tk-compose-side > span {
            grid-column: 1 / -1;
          }
        }

        @media (max-width: 760px) {
          .tk-chat-message-row { width: 90%; }

          .tk-chat-messages {
            min-height: 235px;
            max-height: 390px;
            padding: 13px;
          }

          .tk-chat-attachments { grid-template-columns: minmax(0, 1fr); }
          .tk-chat-send span { display: none; }

          .tk-chat-send {
            width: 42px;
            padding: 0;
          }

          .tk-chat-compose-hint {
            text-align: left;
            line-height: 1.45;
          }

          .tk-page {
            width: min(100% - 24px, 1420px);
            padding-top: 25px;
          }

          .tk-hero {
            align-items: flex-start;
            flex-direction: column;
          }

          .tk-hero-actions {
            width: 100%;
            align-items: stretch;
            flex-wrap: wrap;
          }

          .tk-screen-capture-button {
            flex: 0 0 auto;
          }

          .tk-user-glass {
            width: 100%;
            min-width: 0;
          }

          .tk-hero h1 {
            font-size: 43px;
            letter-spacing: -2px;
          }

          .tk-tabs {
            width: 100%;
          }

          .tk-tab {
            flex: 1 0 auto;
          }

          .tk-section,
          .tk-compose-card {
            padding: 17px;
            border-radius: 22px;
          }

          .tk-section-heading {
            flex-direction: column;
          }

          .tk-form-grid,
          .tk-compose-options {
            grid-template-columns: 1fr;
          }

          .tk-compose-side {
            grid-template-columns: 1fr;
          }

          .tk-compose-side > span {
            grid-column: auto;
          }

          .tk-stats-inline {
            justify-content: flex-start;
          }
        }

        @media (max-width: 470px) {
          .tk-ticket-detail-modal {
            padding: 8px;
          }

          .tk-ticket-detail-modal-card {
            width: calc(100vw - 16px);
            max-height: calc(100vh - 16px);
            padding: 12px;
            border-radius: 20px;
          }

          .tk-ticket-info-modal-card {
            height: auto;
            max-height: calc(100vh - 16px);
          }

          .tk-ticket-chat-modal-card {
            height: calc(100vh - 16px);
          }

          .tk-ticket-detail-heading {
            align-items: flex-start;
            flex-direction: column;
            padding: 2px 46px 10px 2px;
          }

          .tk-ticket-detail-heading h2 {
            max-width: calc(100vw - 92px);
          }

          .tk-chat-modal-status {
            width: 100%;
            justify-content: flex-start;
          }

          .tk-page {
            width: min(100% - 18px, 1420px);
          }

          .tk-ticket-topline {
            align-items: flex-start;
            flex-direction: column;
          }

          .tk-badges {
            justify-content: flex-start;
          }

          .tk-compose-header {
            align-items: flex-start;
          }

          .tk-form-actions {
            align-items: stretch;
            flex-direction: column-reverse;
          }

          .tk-btn-primary,
          .tk-btn-secondary {
            width: 100%;
          }

          .tk-image-modal {
            width: 100%;
            height: 100vh;
            padding: 10px;
          }

          .tk-image-modal-card {
            width: calc(100vw - 20px);
            max-width: calc(100vw - 20px);
            max-height: calc(100vh - 20px);
            padding: 10px;
            border-radius: 18px;
          }

          .tk-image-modal-card > img {
            max-height: calc(100vh - 124px);
            border-radius: 13px;
          }

          .tk-image-close {
            top: 16px;
            right: 16px;
          }

          .tk-image-modal-footer {
            gap: 8px;
          }

          .tk-image-download {
            min-height: 36px;
            padding: 0 10px;
            font-size: 9px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .tk-tab-panel {
            animation: none !important;
          }

          .tk-ticket-card,
          .tk-tab,
          .tk-status-choice,
          .tk-btn-primary,
          .tk-btn-secondary,
          .tk-switch,
          .tk-switch::after {
            transition: none !important;
          }
        }

        

        /* =========================================================
           MULTI ALLEGATI / DRAG & DROP
        ========================================================= */
        .tk-compose-message-row {
          display: grid;
          grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr);
          gap: 12px;
          align-items: stretch;
        }

        .tk-message-field {
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .tk-message-field textarea {
          flex: 1;
          min-height: 220px;
          height: 100%;
        }

        .tk-compose-options-single {
          grid-template-columns: minmax(0, 1fr);
          max-width: 420px;
        }

        .tk-attachment {
          position: relative;
          min-height: 220px;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 7px;
          padding: 18px;
          border: 1.5px dashed rgba(255,255,255,0.22);
          border-radius: 16px;
          background: rgba(255,255,255,0.028);
          text-align: center;
          cursor: pointer;
          transition:
            border-color .18s ease,
            background .18s ease,
            box-shadow .18s ease,
            transform .18s ease;
        }

        .tk-attachment.is-dragging,
        .tk-reply-file.is-dragging {
          border-color: ${GOLD};
          background: rgba(235,181,27,0.10);
          box-shadow: 0 0 0 3px rgba(235,181,27,0.08), 0 14px 38px rgba(0,0,0,0.18);
          transform: translateY(-1px);
        }

        .tk-attachment > input,
        .tk-reply-file > input {
          display: none;
        }

        .tk-attachment > svg,
        .tk-reply-file > svg {
          color: ${GOLD};
        }

        .tk-attachment > strong,
        .tk-reply-file > strong {
          color: rgba(255,255,255,0.92);
          font-size: 13px;
          font-weight: 850;
        }

        .tk-attachment > span,
        .tk-reply-file > span {
          color: rgba(255,255,255,0.52);
          font-size: 11px;
        }

        .tk-attachment > small,
        .tk-reply-file > small {
          color: rgba(255,255,255,0.32);
          font-size: 9px;
          line-height: 1.45;
        }

        .tk-attachment > b,
        .tk-reply-file > b {
          margin-top: 3px;
          color: ${GOLD};
          font-size: 10px;
          font-weight: 850;
        }

        .tk-selected-files {
          width: 100%;
          max-height: 104px;
          margin-top: 5px;
          overflow-y: auto;
          display: grid;
          gap: 5px;
        }

        .tk-selected-file {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          min-width: 0;
          padding: 6px 7px 6px 9px;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 9px;
          background: rgba(0,0,0,0.16);
          text-align: left;
        }

        .tk-selected-file > span {
          min-width: 0;
          overflow: hidden;
          color: rgba(255,255,255,0.72);
          font-size: 9px;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .tk-selected-file > button {
          flex: 0 0 auto;
          width: 24px;
          height: 24px;
          display: grid;
          place-items: center;
          padding: 0;
          border: 0;
          border-radius: 7px;
          color: rgba(255,255,255,0.65);
          background: rgba(255,255,255,0.06);
          cursor: pointer;
        }

        .tk-stored-files {
          margin-top: 12px;
        }

        .tk-stored-files-title {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 7px;
          color: rgba(255,255,255,0.46);
          font-size: 10px;
          font-weight: 750;
        }

        .tk-stored-files-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
        }

        .tk-stored-file {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          max-width: 240px;
          min-height: 38px;
          padding: 5px 9px 5px 5px;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 10px;
          color: rgba(255,255,255,0.72);
          background: rgba(255,255,255,0.035);
          cursor: pointer;
        }

        .tk-stored-file img,
        .tk-stored-file-icon {
          width: 28px;
          height: 28px;
          flex: 0 0 auto;
          border-radius: 7px;
        }

        .tk-stored-file img {
          object-fit: cover;
        }

        .tk-stored-file-icon {
          display: grid;
          place-items: center;
          color: ${GOLD};
          background: rgba(235,181,27,0.08);
        }

        .tk-stored-file > span:last-child {
          min-width: 0;
          overflow: hidden;
          font-size: 10px;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .tk-reply-main-row {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(250px, .72fr);
          gap: 10px;
          align-items: stretch;
        }

        .tk-reply-main-row textarea {
          min-height: 150px;
          height: 100%;
        }

        .tk-reply-file {
          position: relative;
          min-height: 150px;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 5px;
          padding: 13px;
          border: 1.5px dashed rgba(255,255,255,0.20);
          border-radius: 13px;
          color: rgba(255,255,255,0.70);
          background: rgba(255,255,255,0.025);
          text-align: center;
          cursor: pointer;
          overflow: hidden;
          transition:
            border-color .18s ease,
            background .18s ease,
            box-shadow .18s ease,
            transform .18s ease;
        }

        .tk-reply-actions-row {
          justify-content: flex-end;
        }

        .tk-inline-file-error {
          padding: 8px 10px;
          border: 1px solid rgba(255,95,95,0.18);
          border-radius: 9px;
          color: #ffb1b1;
          background: rgba(255,80,80,0.055);
          font-size: 10px;
        }

        @media (max-width: 900px) {
          .tk-list-toolbar,
          .tk-section-heading-with-sort {
            align-items: stretch;
          }

          .tk-list-toolbar {
            flex-direction: column;
          }

          .tk-heading-actions {
            width: 100%;
            align-items: stretch;
          }

          .tk-department-heading-actions {
            width: 100%;
            justify-content: space-between;
          }

          .tk-department-view-tabs {
            width: 100%;
          }

          .tk-department-view-tab {
            flex: 1 1 0;
            min-width: 0;
          }

          .tk-mine-pagination {
            align-items: stretch;
            flex-direction: column;
          }

          .tk-mine-pagination-info {
            justify-content: center;
            flex-wrap: wrap;
          }

          .tk-mine-pagination-controls {
            justify-content: space-between;
            width: 100%;
          }

          .tk-page-numbers {
            flex: 1 1 auto;
            justify-content: center;
            min-width: 0;
          }

          .tk-page-nav {
            padding: 0 8px;
          }

          .tk-department-toggle {
            flex: 1 1 auto;
          }

          .tk-closed-searchbar {
            grid-template-columns: 1fr 1fr;
          }

          .tk-closed-search {
            grid-column: 1 / -1;
          }

          .tk-closed-reset {
            grid-column: 1 / -1;
          }

          .tk-mine-filterbar {
            grid-template-columns: 1fr 1fr;
          }

          .tk-mine-search {
            grid-column: 1 / -1;
          }

          .tk-mine-reset-filters {
            width: 100%;
          }

          .tk-sort-control {
            width: 100%;
            justify-content: space-between;
          }

          .tk-sort-control select {
            width: 100%;
            min-width: 0;
          }

          .tk-mine-filter-control select {
            width: 100%;
            min-width: 0;
          }
          .tk-compose-message-row,
          .tk-reply-main-row {
            grid-template-columns: 1fr;
          }

          .tk-attachment,
          .tk-message-field textarea {
            min-height: 180px;
          }

          .tk-reply-file,
          .tk-reply-main-row textarea {
            min-height: 135px;
          }
        }

        .tk-attachment-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
}

.tk-attachment-title {
  display: flex;
  align-items: center;
  gap: 7px;
  min-height: 22px;
  color: rgba(255, 255, 255, 0.78);
  font-size: 13px;
  font-weight: 700;
}

.tk-attachment-title svg {
  color: rgba(255, 255, 255, 0.78);
}

        .tk-close-panel,
        .tk-closure-summary {
          margin-top: 16px;
          padding: 18px;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 18px;
          background: rgba(255,255,255,0.035);
        }

        .tk-closure-summary.is-normal-close {
          border-color: rgba(105,207,134,0.24);
          background: rgba(105,207,134,0.065);
        }

        .tk-closure-summary.is-missing-info {
          border-color: rgba(239,93,93,0.34);
          background: rgba(239,93,93,0.09);
        }

        .tk-closure-summary.is-normal-close .tk-section-kicker {
          color: #79d995;
        }

        .tk-closure-summary.is-missing-info .tk-section-kicker {
          color: #ef6b6b;
        }

        .tk-close-response textarea {
          width: 100%;
          resize: vertical;
          min-height: 120px;
        }

        .tk-close-attachment-row {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 12px;
        }

        .tk-close-file-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 9px 12px;
          border-radius: 11px;
          border: 1px solid rgba(255,255,255,0.12);
          background: rgba(255,255,255,0.05);
          cursor: pointer;
          font-size: 12px;
          font-weight: 800;
        }

        .tk-close-file-button input { display: none; }

        .tk-close-file-selected {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
          max-width: 360px;
          padding: 8px 10px;
          border-radius: 10px;
          background: rgba(255,255,255,0.04);
          font-size: 12px;
        }

        .tk-close-file-selected span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .tk-close-file-selected button {
          border: 0;
          background: transparent;
          color: inherit;
          cursor: pointer;
          display: inline-flex;
        }

        .tk-close-submit {
          margin-top: 14px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          border: 0;
          border-radius: 12px;
          padding: 11px 16px;
          font-weight: 900;
          cursor: pointer;
          background: #ebb51b;
          color: #111;
        }

        .tk-close-submit:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .tk-closure-summary-text {
          margin-top: 12px;
          white-space: pre-wrap;
          line-height: 1.55;
          color: rgba(255,255,255,0.86);
        }

        .tk-closure-meta {
          margin-top: 14px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          font-size: 12px;
          color: rgba(255,255,255,0.68);
        }

        .tk-closure-files {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .tk-closure-files a {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 9px;
          border-radius: 9px;
          background: rgba(255,255,255,0.05);
          color: inherit;
          text-decoration: none;
        }

        .tk-no-edit {
          opacity: 0.35;
        }


        /* ======================================================
           TICKET RICEVUTI · VISTA / AZIONE CHAT
        ====================================================== */

        .tk-received-heading-actions {
          display: flex;
          align-items: center;
          gap: 10px;
          flex: 0 0 auto;
        }

        .tk-received-scroll-area {
          width: 100%;
          max-width: 100%;
          overflow-y: auto;
          overflow-x: hidden;
          padding-right: 3px;
          scrollbar-width: thin;
          scrollbar-color: rgba(235,181,27,0.34) transparent;
          overscroll-behavior: contain;
        }

        .tk-received-scroll-area::-webkit-scrollbar {
          width: 6px;
        }

        .tk-received-scroll-area::-webkit-scrollbar-track {
          background: transparent;
        }

        .tk-received-scroll-area::-webkit-scrollbar-thumb {
          border-radius: 999px;
          background: rgba(235,181,27,0.30);
        }

        /*
         * Vista lista: intestazione + due righe complete visibili.
         * Le righe successive scorrono soltanto dentro questa sezione.
         */
        .tk-received-scroll-area-list {
          max-height: 390px;
        }

        .tk-received-scroll-area-list .tk-received-list-wrap {
          overflow-x: auto;
          overflow-y: visible;
          border-radius: 20px;
        }

        .tk-received-list-table thead {
          position: sticky;
          top: 0;
          z-index: 8;
        }

        .tk-received-list-table thead th {
          background: rgba(50,52,58,0.98);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
        }

        /*
         * Vista griglia: il blocco ricevuti non allunga tutta la pagina.
         * Rimane visibile una porzione equivalente a circa due righe di card.
         */
        .tk-received-scroll-area-grid {
          max-height: min(68vh, 680px);
          padding-right: 7px;
        }

        .tk-view-switch {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          padding: 3px;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 12px;
          background: rgba(255,255,255,0.035);
        }

        .tk-view-switch button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 31px;
          height: 31px;
          padding: 0;
          border: 0;
          border-radius: 9px;
          color: rgba(255,255,255,0.38);
          background: transparent;
          cursor: pointer;
          transition: color .18s ease, background .18s ease, transform .18s ease;
        }

        .tk-view-switch button:hover {
          color: rgba(255,255,255,0.78);
          background: rgba(255,255,255,0.055);
        }

        .tk-view-switch button.is-active {
          color: ${GOLD};
          background: rgba(235,181,27,0.11);
          box-shadow: inset 0 0 0 1px rgba(235,181,27,0.14);
        }

        .tk-open-chat-action-required {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          margin-left: 2px;
          color: ${GOLD};
          line-height: 1;
          filter: drop-shadow(0 0 4px rgba(235,181,27,0.18));
        }

        .tk-received-list-table td[data-label="Titolo"] {
          min-width: 190px;
        }

        .tk-received-list-table td[data-label="Chat"] {
          min-width: 96px;
        }

        /* ======================================================
           SCREENSHOT NEGLI ALLEGATI
        ====================================================== */

        .tk-attachment-title {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .tk-attachment-title > div {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          min-width: 0;
        }

        .tk-attachment-screenshot-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          min-height: 30px;
          padding: 0 10px;
          border: 1px solid rgba(235,181,27,0.24);
          border-radius: 10px;
          color: ${GOLD};
          background: rgba(235,181,27,0.065);
          font-size: 8px;
          font-weight: 800;
          cursor: pointer;
          transition: transform .18s ease, background .18s ease, border-color .18s ease;
        }

        .tk-attachment-screenshot-button:hover:not(:disabled) {
          transform: translateY(-1px);
          border-color: rgba(235,181,27,0.42);
          background: rgba(235,181,27,0.11);
        }

        .tk-attachment-screenshot-button:disabled {
          opacity: .5;
          cursor: wait;
        }

        .tk-screenshot-modal {
          position: fixed;
          inset: 0;
          z-index: 12000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          background: rgba(8,9,12,0.72);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
        }

        .tk-screenshot-card {
          width: min(760px, calc(100vw - 32px));
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 23px;
          background: #292c31;
          box-shadow: 0 30px 90px rgba(0,0,0,.48);
        }

        .tk-screenshot-preview {
          width: 100%;
          max-height: 52vh;
          overflow: hidden;
          background: #15171a;
        }

        .tk-screenshot-preview img {
          display: block;
          width: 100%;
          max-height: 52vh;
          object-fit: contain;
        }

        .tk-screenshot-copy {
          display: flex;
          flex-direction: column;
          gap: 3px;
          padding: 16px 18px 8px;
        }

        .tk-screenshot-copy span {
          color: rgba(255,255,255,.45);
          font-size: 9px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .9px;
        }

        .tk-screenshot-copy strong {
          overflow: hidden;
          color: rgba(255,255,255,.84);
          font-size: 11px;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .tk-screenshot-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          padding: 12px 18px 18px;
          flex-wrap: wrap;
        }

        .tk-screenshot-actions button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          min-height: 36px;
          padding: 0 13px;
          border-radius: 11px;
          font-size: 9px;
          font-weight: 800;
          cursor: pointer;
        }

        .tk-screenshot-cancel {
          border: 1px solid rgba(255,255,255,.10);
          color: rgba(255,255,255,.58);
          background: rgba(255,255,255,.04);
        }

        .tk-screenshot-copy-button,
        .tk-screenshot-share-button {
          border: 1px solid rgba(255,255,255,.14);
          color: rgba(255,255,255,.78);
          background: rgba(255,255,255,.065);
        }

        .tk-screenshot-done-button {
          border: 1px solid rgba(235,181,27,.34);
          color: #171717;
          background: ${GOLD};
        }

        @media (max-width: 720px) {
          .tk-received-heading-actions {
            width: 100%;
            justify-content: space-between;
          }

          .tk-attachment-title {
            align-items: flex-start;
          }

          .tk-attachment-screenshot-button span {
            display: none;
          }

          .tk-attachment-screenshot-button {
            width: 32px;
            padding: 0;
          }

          .tk-screenshot-actions {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .tk-screenshot-actions button {
            width: 100%;
          }
        }

      `}</style>
    </div>
  );
}

export default TicketPage;
